/* aviaf -- live data layer.
 *
 * Pure WS-driven after a single REST bootstrap. No polling, no mock
 * fallback once the dashboard mounts. Mock fixtures from data.jsx
 * are immediately wiped at hook mount; the dashboard shows ONLY what
 * the hub actually has.
 *
 * Lifecycle:
 *   mount -> resetState() -> bootstrap() -> openBridge()
 *   on WS reconnect after a drop -> bootstrap() again to recover any
 *     events missed during the disconnect window
 *   unmount -> close bridge
 *
 * State location: window.{INSTANCES, ACCOUNTS, TRADES, FEED,
 *   EQUITY_TOTAL/MGC/NQ, OPEN_POSITIONS}.
 *   Pages already read these on every `tick`-driven re-render, so
 *   in-place mutation propagates to the UI without React.Context.
 *
 * Account classification mirrors aviaf/broker/topstepx/account_rules.py:
 *   PRAC...,  PRACTST...  -> 'practice' (paper badge)
 *   XFA...,   EXPRESS...  -> 'xfa'      (xfa/warn badge)
 *   ...KTC                -> 'combine'  (live badge)
 *   LFA..., FT..., PA...  -> 'funded'   (live badge)
 *
 * Pages backed by no API (Collectors, Strategies, Configs, Research,
 * Regime, SPC) show empty arrays -- never fake fillers.
 */

(function () {
  /* -- observable rev counter; pages read window.* on every tick -- */
  window.__LIVE = {
    rev: 0,
    authed: false,
    instancesLive: 0,
    wsStatus: "idle",
    lastBootstrap: null,
    bootstrapping: false,
    hasData: false,           // true once we have ANYTHING to show (cache or bootstrap)
    err: null,
    wsDownSince: null,        // ms wall-clock the live WS feed dropped (null = live)
    pingClient: null,         // browser-measured WS round-trip to the hub (ms), raw latest
    pingServer: null,         // hub-measured WS round-trip to this browser (ms), raw latest
    // Smoothed latency stats (what the meter displays).  Raw per-sample RTT
    // jitters ±100ms, so monitoring tools never show the instantaneous sample.
    // srtt = RFC 6298 smoothed RTT (α=1/8); jit = RFC 6298 RTTVAR (β=1/4, the
    // mean deviation = jitter); p95 = 95th percentile over a rolling 40-sample
    // window.  Populated by _recordPing().
    pingClientSrtt: null, pingClientJit: null, pingClientP95: null,
    pingServerSrtt: null, pingServerJit: null, pingServerP95: null,
    // Per-data-source "confirmed loaded" flags.  true means the /api/*
    // returned (with data or empty — either is authoritative); a
    // missing/false flag means the fetch hasn't resolved yet and the
    // component should shimmer.  Bootstrap sets each flag right after
    // the corresponding step succeeds.  Cache hydration sets the flag
    // when it restores data for that source.
    loaded: {
      // accounts:       step 1   /api/accounts + /api/instances
      // equitySnapshot: step 2   /api/equity?limit=2 per account
      // fills:          step 3   /api/fills
      // positions:      step 4   /api/positions
      // equitySeries:   step 5   /api/equity?bucket_seconds=3600
      // feed:           step 6   /api/events
      // spc:            step 7   /api/spc
      // alerts:         step 7   /api/alerts
      // inventory:      step 7   /api/collectors|configs|research
    },
  };
  /* -- external store: bump() notifies React subscribers (useSyncExternalStore) --
   * Previously bump() only incremented rev and relied on every page
   * POLLING window.__LIVE.rev — ui.jsx useDataLoad at 250ms, mobile's
   * useMLoaded at 250ms, plus a 1Hz App tick — to repaint.  That sampled
   * live data on a 250ms–1s clock (status felt laggy) and re-rendered the
   * whole tree on every interval even when nothing changed (idle CPU drain
   * + jank).  Now bump() drives a real useSyncExternalStore subscription:
   * a WS frame repaints subscribed components within one animation frame
   * (~instant), and NOTHING polls — idle = zero renders.  Multiple bumps
   * inside one frame coalesce into a single notify, so a bootstrap burst
   * (dozens of bump() calls in a row) collapses to one render pass. */
  const _liveListeners = new Set();
  let _notifyScheduled = false;
  function _flushNotify() {
    _notifyScheduled = false;
    _liveListeners.forEach((fn) => { try { fn(); } catch (_) { /* a bad subscriber must not stall the rest */ } });
  }
  function _scheduleNotify() {
    if (_notifyScheduled) return;
    _notifyScheduled = true;
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(_flushNotify);
    else setTimeout(_flushNotify, 16);
  }
  function bump() { window.__LIVE.rev += 1; _scheduleNotify(); }
  function markLoaded(key) { window.__LIVE.loaded[key] = true; bump(); }

  // ── Latency estimator (RFC 6298 SRTT/RTTVAR + rolling p95) ─────────────
  // The instantaneous WS RTT is noisy (event-loop scheduling, GC, tab
  // throttling swing it ±100ms), so — like every real network monitor — we
  // display a smoothed estimate, not the last raw sample.  SRTT/RTTVAR are the
  // exact TCP retransmission-timer estimators (RFC 6298 §2): SRTT tracks the
  // trend, RTTVAR is the mean deviation (industry "jitter").  A 40-sample ring
  // gives an honest p95 tail alongside the smooth headline.
  const _pingRing = { client: [], server: [] };
  function _recordPing(which, sample) {
    if (!(sample >= 0)) return;
    const L = window.__LIVE;
    const sK = "ping" + which + "Srtt", vK = "ping" + which + "Jit", pK = "ping" + which + "P95";
    if (L[sK] == null) {            // first sample seeds the estimator (RFC 6298)
      L[sK] = sample; L[vK] = sample / 2;
    } else {
      const err = sample - L[sK];
      L[vK] = Math.round((0.75 * L[vK] + 0.25 * Math.abs(err)));   // β = 1/4
      L[sK] = Math.round((0.875 * L[sK] + 0.125 * sample));         // α = 1/8
    }
    const ring = _pingRing[which.toLowerCase()];
    ring.push(sample);
    if (ring.length > 40) ring.shift();
    const sorted = ring.slice().sort((a, b) => a - b);
    L[pK] = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))];
  }
  window.__LIVE._recordPing = _recordPing;

  // useSyncExternalStore plumbing.  subscribe/getSnapshot are stable
  // (defined once) as the hook requires.  getSnapshot returns rev — a
  // number that only changes on bump() — so React re-renders a subscriber
  // exactly when live data changed, with no tearing and no polling.
  window.__liveSubscribe = function (fn) { _liveListeners.add(fn); return () => { _liveListeners.delete(fn); }; };
  window.__liveSnapshot = function () { return window.__LIVE.rev; };
  window.useLive = function useLive() {
    return React.useSyncExternalStore(window.__liveSubscribe, window.__liveSnapshot);
  };

  /* -- localStorage cache (instant first paint on reload) --
   * Persist the slices that drive the visible cards so a reload shows
   * last-known values within ~10ms instead of waiting ~500ms for
   * /api/* round trips. The next bootstrap revalidates and updates.
   * Cache is namespaced per-origin (default localStorage scope).
   */
  // v3 — bumped after the time-based x-axis refactor and the aggregate
  // forward-fill rewrite changed how EQUITY_TS / EQUITY_TOTAL are built
  // (longest-account ts donor + binary-search interpolation across
  // shorter accounts).  Stale v2 caches would feed the new renderer
  // arrays that don't align with the new windowStartUs anchor, leaving
  // the plot blank until the 5-minute TTL expired.  Bumping the
  // version means every browser does one full bootstrap on next load.
  const CACHE_KEY = "aviaf:live-cache:v3";
  const CACHE_TTL_MS = 5 * 60 * 1000;   // 5 min; older = stale, ignore
  const CACHE_KEYS = [
    "INSTANCES","ACCOUNTS","TRADES","FEED","EQUITY_TOTAL","EQUITY_BY_STRATEGY","PNL_TOTAL","PNL_BY_STRATEGY","EQUITY_TS","AUM_30D_AGO","AUM_30D_HAS_HIST"
  ];
  // applyFrame() previously called persistCache() on every WS frame,
  // which JSON.stringify'd the entire window state (TRADES alone is
  // up to 5000 rows × ~200 B ≈ 1 MB) on every equity tick.  Under the
  // pre-throttle 6.6/s emit rate that was ~6.6 MB/s of synchronous
  // main-thread work + localStorage.setItem against the 5 MB browser
  // quota, which is what crashed the tab after a few hours.  Throttle
  // the persist to at most once per PERSIST_MIN_DT_MS, scheduling a
  // single trailing flush so the cache still catches up after a burst.
  const PERSIST_MIN_DT_MS = 5000;
  let _lastPersistMs = 0;
  let _persistTimer = null;
  function _doPersist() {
    _lastPersistMs = Date.now();
    _persistTimer = null;
    try {
      const snap = { _ts: _lastPersistMs };
      for (const k of CACHE_KEYS) snap[k] = window[k];
      localStorage.setItem(CACHE_KEY, JSON.stringify(snap));
    } catch (_) { /* storage full or disabled, ignore */ }
  }
  function persistCache() {
    const now = Date.now();
    const since = now - _lastPersistMs;
    if (since >= PERSIST_MIN_DT_MS) {
      _doPersist();
      return;
    }
    // Already-scheduled trailing flush covers this caller too.
    if (_persistTimer) return;
    _persistTimer = setTimeout(_doPersist, PERSIST_MIN_DT_MS - since);
  }
  function hydrateCache() {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return false;
      const snap = JSON.parse(raw);
      if (!snap || !snap._ts) return false;
      if (Date.now() - snap._ts > CACHE_TTL_MS) return false;
      for (const k of CACHE_KEYS) {
        if (snap[k] !== undefined) window[k] = snap[k];
      }
      // Loaded flags are NOT flipped from cache.  The cache populates
      // window.* so the bootstrap can incrementally replace fields
      // without flicker, but every KPI/section shimmers until its own
      // fresh fetch lands.  Why: 30D P&L = aum - AUM_30D_AGO.  After
      // refresh, bootstrap step 1 replaces ACCOUNTS (fresh aum) while
      // AUM_30D_AGO is still cached; the render between those steps
      // produced obviously wrong numbers (e.g. -$134K) for a brief
      // window — which the operator sees as the page "loading wrong
      // values, then correcting".  Holding the shimmer until step N
      // means the first non-shimmer paint is internally consistent.
      return true;
    } catch (_) { return false; }
  }
  function clearCache() {
    try { localStorage.removeItem(CACHE_KEY); } catch (_) {}
  }

  /* -- account classification --
   * Mirror of aviaf/broker/topstepx/account_rules.py prefix matching.
   * The classification IS the badge class; CSS in index.html styles
   * each one. Order matters: PRACTST is matched before PRAC since
   * the latter is a substring of the former (both behave the same
   * here -- both classify as 'practice'). XFA prefix runs before any
   * future XFA-prefixed combine. Update ACCOUNT_CLASSIFICATIONS in
   * pages.jsx + index.html CSS if you add a new kind.
   */
  // Accounts removed from agent config but not yet soft-deleted in the
  // hub DB (agent hasn't reconnected since removal).  Force these into
  // ACCOUNTS_INACTIVE so the Overview doesn't show stale cards.  Safe to
  // remove an entry once the agent's next HELLO auto-deactivates the row.
  const DECOMMISSIONED_LABELS = new Set(["combine4"]);

  // Instances that report accounts the aviaf engine does NOT manage —
  // read-only mirrors (ops/tools/topstep_mirror_agent.py feeds the 369979
  // TopStep login as "topstep-mirror" after the 2026-07-26 audit found 21 of
  // its accounts trading unseen).  Their trades are real and belong in
  // per-strategy P&L, but they are not deployments: the strategies page counts
  // them separately from live/stopped so the roster numbers stay honest.
  window.EXTERNAL_INSTANCES = new Set(["topstep-mirror"]);

  const COMBINE_PREFIXES = ["150KTC","100KTC","50KTC","25KTC","200KTC","250KTC"];
  function classifyAccount(name) {
    const n = (name || "").toUpperCase();
    if (n.startsWith("PRACTST") || n.startsWith("PRAC")) return "practice";
    if (n.startsWith("XFA") || n.startsWith("EXPRESS"))  return "xfa";
    if (COMBINE_PREFIXES.some(p => n.startsWith(p)))     return "combine";
    if (n.startsWith("LFA") || n.startsWith("FT") || n.startsWith("PA")) return "funded";
    return "unknown";
  }
  // For UI consumers that want a longer human label.
  window.ACCOUNT_KIND_LABELS = {
    practice: "Practice",
    combine:  "Combine",
    xfa:      "XFA",
    funded:   "Funded",
    unknown:  "Unknown",
  };

  /* XFA reality: by design these accounts start at $0 broker balance.
   * The "150K" / "100K" / "50K" tier sets the MLL headroom only — it
   * is NOT a starting cash deposit. So a balance of $192 on a $150K-
   * tier XFA means $192 of accrued profit, not "implausibly low".
   * (See aviaf/broker/topstepx/account_rules.py:141-181 + the
   * size_variable=True branch in get_account_rules.)
   *
   * For the dashboard:
   *   - XFA balance can sit anywhere in [-mll_headroom, +∞)
   *   - "Initial" for an XFA = $0 (starting balance), NOT the tier
   *   - Overall P&L for an XFA = current balance (you started at zero)
   *   - MLL floor = -headroom while balance < headroom (trailing),
   *                 then ratchets up; locks at $0 once balance ≥ headroom
   *
   * Combines + LFA + practice keep their existing semantics.
   */
  window.xfaStartingBalance = function() { return 0; };
  // Returns the size-tier headroom for an XFA given its tier capital.
  // tierCapital = initial_capital from the agent (which is the toml-
  // configured tier size, e.g. 150000 for a 150K-tier XFA).
  window.xfaHeadroom = function(tierCapital) {
    const c = Number(tierCapital) || 0;
    if (c >= 150000) return 4500;
    if (c >= 100000) return 3000;
    if (c >= 50000)  return 2000;
    return 2000;  // smallest active tier — clamp
  };

  // Corrected MLL floor — fixes XFA where the agent reports the combine-
  // style floor (tier - headroom, e.g. $145,500) instead of the XFA-style
  // floor (0 - headroom, e.g. -$4,500).  Detect: if agent floor exceeds
  // equity by more than 3× the tier headroom, it's the wrong formula.
  window.resolvedMllFloor = function(a) {
    let f = Number(a?.mllFloor) || 0;
    if (a?.kind === "xfa") {
      const hr = window.xfaHeadroom(a.initial_capital);
      const eq = Number(a.equity) || 0;
      if (f > eq + hr * 3) f = -hr;
    }
    return f;
  };

  /* Anchor balance for P&L computation. Combine + practice start at
   * initial_capital ($150K etc); XFA + LFA start at $0 (the "tier"
   * size is just for headroom, not a deposited cash amount). Used
   * everywhere we compute totalPnl / overallPnl / allocated capital. */
  window.accountAnchor = function(a) {
    if (!a) return 0;
    if (a.kind === "xfa" || a.kind === "funded") return 0;
    return Number(a.initial_capital) || 0;
  };

  // "Pre-staged" account: registered on the hub (via the dashboard) but never
  // activated on the AGENT. The hub deliberately defers ACCOUNT_ADD until the
  // first strategy_add (register_account in http.py), so an account with no
  // strategies is never attached, never emits equity, and — without this —
  // shimmers FOREVER waiting for data that will never arrive. Detect it so the
  // UI can show an actionable "add a strategy to activate" state instead.
  // Guarded on the roster being loaded so a real account mid-bootstrap (which
  // WILL verify shortly) isn't misflagged.
  window.acctPreStaged = function(a) {
    if (!a || a.equityVerified) return false;
    if (!(window.__LIVE && window.__LIVE.loaded && window.__LIVE.loaded.accounts)) return false;
    const hasStrats =
      (Array.isArray(a.strategies) && a.strategies.length > 0) ||
      (a.strategyStatus && Object.keys(a.strategyStatus).length > 0) ||
      (window.STRATS || []).some((s) => s.acct === a.id);
    return !hasStrats;
  };

  /* Practice accounts have no real broker balance — the agent reports
   * balance=0 because there's no funded account behind them. Synthesize
   * the displayed equity from the configured tier + realized P&L derived
   * from this account's exit fills (the snapshot's realized_pnl field is
   * unreliable for practice — the agent often leaves it at 0).
   *
   * Returns the synthesized equity, or null if `snap` provides a real
   * non-zero balance (a paper broker that *does* expose balance wins).
   */
  window.practiceEquity = function(a, snap) {
    if (!a || a.kind !== "practice") return null;
    const real = Number(snap?.balance) || 0;
    // Trust agent-reported balance when it's non-zero — newer agents
    // emit the realized P&L sum INTO `balance` for practice accounts so
    // the dashboard doesn't need to re-derive it.
    if (real > 0) return null;
    // Fall-back path: agent emits balance=0 (legacy "zero broker
    // balance" behaviour).  Only synth from fills when we actually
    // have fills attributed to this account — otherwise an empty
    // fills table would clobber the (correct) anchor down further.
    const trades = window.TRADES || [];
    const acctFills = trades.filter(t => t.account === a.id);
    if (acctFills.length === 0) return null;       // no signal → don't override
    const anchor = Number(a.initial_capital) || 0;
    let realized = 0;
    for (const t of acctFills) {
      if (t.isExit) realized += Number(t.pnl) || 0;
    }
    const unreal = Number(snap?.unrealized_pnl) || 0;
    return anchor + realized + unreal;
  };

  /* CME trading-day key (YYYY-MM-DD) for any timestamp — matches how
   * TopStep groups daily P&L.  The futures session rolls at 17:00
   * America/Chicago, so a fill at/after 17:00 CT belongs to the NEXT
   * calendar CT day (the CME "trade date").  Using this everywhere the
   * dashboard buckets a "day" makes the Trade Calendar + day-P&L line up
   * with TopStep exactly, independent of the browser's timezone.  Accepts
   * an ISO string, epoch-ms number, or Date; returns "" for bad input. */
  window.tradingDayKey = function(t) {
    const d = (t instanceof Date) ? t : new Date(t);
    if (isNaN(d.getTime())) return "";
    const parts = {};
    for (const p of new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Chicago", year: "numeric", month: "2-digit",
      day: "2-digit", hour: "2-digit", hour12: false,
    }).formatToParts(d)) parts[p.type] = p.value;
    let y = +parts.year, m = +parts.month, day = +parts.day;
    const hh = (+parts.hour) % 24;   // some engines emit "24" at midnight
    if (hh >= 17) {                  // ≥17:00 CT → next CT calendar day
      const nd = new Date(Date.UTC(y, m - 1, day + 1));
      y = nd.getUTCFullYear(); m = nd.getUTCMonth() + 1; day = nd.getUTCDate();
    }
    return `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  };
  window.tradingDayToday = function() { return window.tradingDayKey(new Date()); };

  /* Practice day_pnl from the snapshot is unreliable when the agent
   * computes day_pnl off the (zero) broker balance and emits absurd
   * values like -$148K.  Derive from THIS CME trading day's exit fills
   * (17:00-CT rollover, matching TopStep) — but ONLY when we have at
   * least one attributed exit fill for this account today.  Returning 0
   * from "no fills" would clobber the agent's real day_pnl when the agent
   * already reports it correctly.
   */
  window.practiceDayPnl = function(a) {
    if (!a || a.kind !== "practice") return null;
    const trades = window.TRADES || [];
    const todayKey = window.tradingDayToday();
    let day = 0;
    let touched = 0;
    for (const t of trades) {
      if (t.account !== a.id || !t.isExit || !t.exitTime) continue;
      if (window.tradingDayKey(t.exitTime) === todayKey) {
        day += Number(t.pnl) || 0;
        touched += 1;
      }
    }
    return touched > 0 ? day : null;
  };

  /* XFA payout calculator. Per Topstep:
   *   - Either path: max payout = 50% of balance, capped at the path's $ cap
   *   - Standard:    5 winning days (≥ $150 each), no consistency rule, $5K cap
   *   - Consistency: 3 trading days, biggest day ≤ 40% of cumulative, $6K cap
   *   - 90/10 trader/Topstep split, weekly cadence per account
   *
   * What we can compute today from REST/WS data (balance only):
   *   - withdrawable = 50% of balance (path-capped)
   * What requires agent-side goal_state emission (checklist §2.11):
   *   - winning days completed (need aviaf to emit per-day net pnl)
   *   - days until next payout (need last_payout_date)
   *   - biggest_day / cumulative ratio (consistency-path eligibility)
   *
   * Returns null for non-XFA. For XFA: { withdrawable, standard_max,
   * consistency_max, balance_pct, paths: [{name, winning_days,
   * consistency_pct, max_payout}] }
   */
  window.xfaPayoutMath = function(a) {
    if (!a || a.kind !== "xfa") return null;
    const balance = Math.max(0, Number(a.equity) || 0);
    const half = balance * 0.5;
    const STANDARD_CAP = 5000;
    const CONSISTENCY_CAP = 6000;
    return {
      balance,
      withdrawable: Math.min(half, CONSISTENCY_CAP),
      standard_max: Math.min(half, STANDARD_CAP),
      consistency_max: Math.min(half, CONSISTENCY_CAP),
      // Dollars left until you cap out each path (informational).
      to_standard_cap: balance > 0 ? Math.max(0, STANDARD_CAP*2 - balance) : STANDARD_CAP*2,
      to_consistency_cap: balance > 0 ? Math.max(0, CONSISTENCY_CAP*2 - balance) : CONSISTENCY_CAP*2,
      paths: [
        { name: "standard",    winning_days: 5, consistency_pct: 0,    max_payout: STANDARD_CAP },
        { name: "consistency", winning_days: 3, consistency_pct: 0.40, max_payout: CONSISTENCY_CAP },
      ],
      split: "90/10",
      cadence_days: 7,   // at most once per week per account
    };
  };

  /* Account-kind rules — mirror of aviaf/broker/topstepx/account_rules.py.
   * Returns the static rule summary for a kind (and tier where size-
   * variable). Populates the "what does this account do" copy in the
   * dashboard so XFA isn't shown as 'no target — survival mode' (which
   * was the catch-all for any non-combine).
   *
   * Sources (verified 2026):
   *   help.topstep.com/en/articles/8284197 (combine)
   *   help.topstep.com/en/articles/8284215 (XFA payout paths)
   *   help.topstep.com/en/articles/10657969 (LFA tiers)
   */
  window.ACCOUNT_KIND_RULES = function(kind, capital) {
    capital = Number(capital) || 0;
    if (kind === "combine") {
      // Profit target / MLL headroom by tier.
      let target = 0, headroom = 0, contracts = 0;
      if (capital >= 150000)      { target = 9000;  headroom = 4500; contracts = 15; }
      else if (capital >= 100000) { target = 6000;  headroom = 3000; contracts = 10; }
      else                        { target = 3000;  headroom = 2000; contracts = 5; }
      return {
        kind: "combine",
        label: "Combine",
        target_dollars: target,
        mll_headroom: headroom,
        max_contracts: contracts,
        drawdown_type: "trailing_eod",
        consistency_pct: 0.50,         // best winning day ≤ 50% of profit_target
        notes: [
          "Trailing MLL — floor rises with profit, locks at starting balance.",
          `Best winning day ≤ ${(0.5*target).toLocaleString("en-US",{style:"currency",currency:"USD",maximumFractionDigits:0})} (50% of profit target).`,
          "DLL removed Aug 2024 — only MLL applies.",
        ],
      };
    }
    if (kind === "xfa") {
      // Express Funded — size_variable. Both initial_capital AND
      // mll_headroom resolve from CURRENT balance (not a fixed tier
      // like combine). Mirrors get_account_rules() with size_variable=True
      // in account_rules.py:233-247: rules.update({initial_capital: balance,
      // mll_headroom: headroom_for_capital(balance)}).
      let headroom = 2000, contracts = 5;
      if (capital >= 150000)      { headroom = 4500; contracts = 15; }
      else if (capital >= 100000) { headroom = 3000; contracts = 10; }
      else if (capital >= 50000)  { headroom = 2000; contracts = 5; }
      // <$50K clamps to smallest tier ($2k headroom)
      return {
        kind: "xfa",
        label: "Express Funded",
        target_dollars: 0,             // no profit target — already funded
        mll_headroom: headroom,
        // Resolved-at-runtime initial: for XFA the "anchor" is the
        // current balance (the trailing peak which the MLL floor follows).
        // Caller should pass the latest balance, not a fixed config value.
        initial_capital_resolved: Math.round(capital),
        max_contracts: contracts,
        drawdown_type: "trailing_eod",
        consistency_pct: 0,            // NOT a general rule — path-specific only
        winning_day_min: 150.0,
        payout_paths: {
          standard:    { winning_days: 5, consistency_pct: 0,    max_payout: 5000 },
          consistency: { winning_days: 3, consistency_pct: 0.40, max_payout: 6000 },
        },
        payout_split: "90/10",
        notes: [
          "Size resolved from current balance — headroom and 'initial' update as balance grows.",
          "Trailing MLL — floor rises with profit, locks at starting balance once balance ≥ initial+headroom.",
          "Standard path: 5 winning days (≥ $150 each), max payout $5,000.",
          "Consistency path: 3 trading days, biggest day ≤ 40% of cumulative net, max payout $6,000.",
          "90/10 trader/Topstep split. Once-per-week payout cadence.",
        ],
      };
    }
    if (kind === "funded") {
      // Live Funded Account — DLL tiered by tradable balance (20% of total).
      let dll = 2250;
      if (capital >= 100000) dll = 15000;
      else if (capital >= 50000) dll = 9000;
      else if (capital >= 20000) dll = 6000;
      else if (capital >= 10000) dll = 4500;
      else if (capital >= 5000)  dll = 3000;
      return {
        kind: "funded",
        label: "Live Funded",
        target_dollars: 0,
        mll_headroom: dll,
        max_contracts: 15,
        drawdown_type: "eod",          // EOD-based, not trailing intraday
        notes: [
          `Daily Loss Limit ${dll.toLocaleString("en-US",{style:"currency",currency:"USD",maximumFractionDigits:0})} (tier-based).`,
          "EOD drawdown — not trailing intraday.",
          "20% of balance is the tradable portion; 80% reserved.",
        ],
      };
    }
    if (kind === "practice") {
      return {
        kind: "practice",
        label: "Practice",
        target_dollars: 0,
        mll_headroom: 0,
        max_contracts: 50,
        drawdown_type: "none",
        notes: [
          "Simulator — no profit target, no MLL, no payout.",
          "Used for soak testing strategies and configs.",
        ],
      };
    }
    return {
      kind: "unknown",
      label: "Unknown",
      target_dollars: 0,
      mll_headroom: 0,
      drawdown_type: "none",
      notes: ["Unrecognised account prefix — rules not encoded."],
    };
  };

  /* -- adapters -- */

  function instanceFromRow(row, latestHealth, hbSeries) {
    const host = latestHealth?.payload?.host || {};
    const proc = latestHealth?.payload?.process || {};
    const memTotal = host.mem_total_bytes || 0;
    const memUsed  = host.mem_used_bytes  || 0;
    return {
      id: row.instance_id,
      host: row.hostname || row.instance_id,
      pid: proc.pid || 0,
      account: null,                    // joined from /api/accounts below
      config: row.version || "--",
      started: row.first_seen_us
        ? new Date(row.first_seen_us / 1000).toISOString().replace("T"," ").slice(0,16) + " UTC"
        : "--",
      uptime: row.last_seen_us && row.first_seen_us
        ? humanDuration((row.last_seen_us - row.first_seen_us) / 1_000_000)
        : "--",
      state: row.connected ? "running" : "stopped",
      cpu:  Math.round((host.cpu_pct ?? 0) * 10) / 10,
      mem:  Math.round((proc.rss_bytes || 0) / 1_000_000),
      memPct: memTotal ? memUsed / memTotal : 0,
      load1m: host.load_1m ?? 0,
      py: "--",
      branch: row.version || "--",
      ticksPerSec: null,                      // no source on hub yet; render as '--'
      lagMs: row.last_clock_offset_us != null
        ? Math.abs(Math.round(row.last_clock_offset_us / 1000)) : null,
      // Hub↔agent WebSocket round-trip latency (ms), measured by the ingest
      // server's RFC 6455 protocol ping/pong.  null until the first ping
      // completes or when disconnected.  Refreshes each bootstrap / 40s poll.
      rttMs: row.rtt_ms != null ? row.rtt_ms : null,
      // Wall-clock ms of the last hub-side ingest from this instance.  Used
      // by the Overview Health panel to derive an "agent heartbeat Ns ago"
      // freshness signal.
      lastSeenMs: row.last_seen_us ? Math.round(row.last_seen_us / 1000) : null,
      strategies: [],
      heartbeat: hbSeries,
    };
  }

  function accountFromRow(row) {
    const cls = classifyAccount(row.account_name);
    // Strategy field is a comma-separated list when an account runs
    // multiple strategies (e.g. prac with ['orb', 'pair_divergence']).
    // The agent's _account_summary should emit "orb,pair_divergence" --
    // we split here so every consumer (cards, strategies tab, detail
    // pages) sees a list. Single-strategy stays as a 1-element list.
    const stratStr = (row.strategy || "").trim();
    // Dedupe the comma-list — agents occasionally emit duplicates
    // (e.g. "orb,mr,orb") and we don't want them rendered three
    // times on the AccountCard or counted twice in PNL_BY_STRATEGY.
    const strategies = stratStr
      ? Array.from(new Set(stratStr.split(",").map(s => s.trim()).filter(Boolean)))
      : [];
    return {
      id: row.label,
      // Composite key (instance/label) for React keys + dedup maps.
      // `id` stays the bare label because it doubles as account_label in
      // /api/equity calls and #/accounts/<id> routing, but labels repeat
      // across instances (e.g. `prac` on both aviaf-paper and aviaf-live),
      // so anything that KEYS on the account must use uid to avoid merging
      // two instances' cards/strategy-lines into one.
      uid: row.instance_id + "/" + row.label,
      broker: "TopstepX",
      kind: cls,
      kindLabel: window.ACCOUNT_KIND_LABELS[cls] || cls,
      code: row.account_name || row.label,   // long broker account number
      label: row.label,                       // short human-friendly: combine1, prac, etc
      account_id: row.account_id,
      strategy: strategies[0] || "--",   // primary (first listed)
      strategies,                         // full list — len > 1 = multi-strategy
      // Practice has no real broker balance; default to the tier so the
      // card never shows $0 before the first equity event lands.
      equity: cls === "practice" ? (Number(row.initial_capital) || 0) : 0,
      dayStart: row.initial_capital || 0,
      sessionStart: row.initial_capital || 0,
      dayPnl: 0,
      totalPnl: 0,
      target: 0,
      mllFloor: 0,
      // False until we've actually seen an agent-emitted equity event (or
      // the post-add /api/equity fetch returned a row). Practice rows can
      // skip the wait because their equity is synthesised from initial_
      // capital + fills; the card always has *something* truthful to
      // render.  Card components shimmer the equity/day-P&L slots when
      // this flag is false so a freshly-added combine doesn't read "$0"
      // for the few seconds between register_account and the first
      // agent equity push.
      equityVerified: cls === "practice",
      openedAt: "--",
      instance: row.instance_id,
      initial_capital: row.initial_capital || 0,
      active: !!row.active,
    };
  }

  // Duration HH:MM:SS — float-cast the seconds-mod-60 explicitly through
  // Math.floor before pad2.  The naive `holdS % 60` returns
  // 3.190054000006 on the fractional hold values the agent emits, and
  // pad2 happily stringifies that to "3.190054000006", which is what
  // produced "121:40:3.1900540000060573" on trade #62.
  function fmtDuration(holdS) {
    return holdS
      ? `${pad2(Math.floor(holdS/3600))}:${pad2(Math.floor((holdS%3600)/60))}:${pad2(Math.floor(holdS%60))}`
      : "--";
  }

  // The agent writes one fills row per fill EVENT, not per round-trip:
  // an open + close emits two rows.  The exit fill's row carries the
  // EXIT order's side (= opposite of the trade direction) and an
  // intended_price for the EXIT order — neither of which describe the
  // trade's entry leg.  These two helpers split the rendering:
  //   entryStubFromFill  — open leg, isExit=false, pnl=0
  //   pairedTradeFromFills — closed round-trip, isExit=true, sided per
  //                          entry leg, entry-price from the matched
  //                          entry fill (NOT exit.intended_price).
  function entryStubFromFill(f) {
    const ts = f.ts_us || Date.now() * 1000;
    const side = (f.side === "buy" || f.side === "long") ? "long" : "short";
    return {
      id: f.fill_pk ?? ts,
      account: f.account_label,
      instance: f.instance_id,
      symbol: f.symbol,
      size: f.qty,
      side,
      entryTime: new Date(ts / 1000).toISOString(),
      exitTime:  new Date(ts / 1000).toISOString(),
      duration: "--",
      entryPrice: f.price,
      exitPrice: f.price,
      pnl: 0,
      commissions: -(f.commission || 0),
      fees: 0,
      // Two distinct fields on the fill row:
      //   strategy — canonical slug ("breakout_v1") used for per-strategy
      //              attribution.  Set by the agent on every fill.
      //   reason   — free-text rationale ("liquidity_test") shown in the
      //              trades table.  Useful for tracebacks but not for stats.
      // Older fills may lack `strategy`; we fall back to reason so the
      // dashboard still renders something rather than "--".
      strategy: f.strategy || null,
      reason: f.reason || "--",
      isExit: false,
    };
  }

  function pairedTradeFromFills(entryStub, exitFill) {
    const exitTs = exitFill.ts_us || Date.now() * 1000;
    const exitSideStr = String(exitFill.side || "").toLowerCase();
    const exitIsLong = exitSideStr === "buy" || exitSideStr === "long";
    let tradeSide, entryPrice, entryTs;
    if (entryStub) {
      tradeSide = entryStub.side;
      entryPrice = entryStub.entryPrice;
      entryTs = Date.parse(entryStub.entryTime);
    } else {
      // Orphan exit (matching entry paged out of the 5K window, or
      // agent-restart artifact).  Flip the exit side to derive the
      // trade direction; intended_price is the closest we have to an
      // entry price.
      tradeSide = exitIsLong ? "short" : "long";
      entryPrice = exitFill.intended_price ?? exitFill.price;
      entryTs = exitTs - (exitFill.hold_seconds || 0) * 1_000_000;
    }
    const holdS = exitFill.hold_seconds != null
      ? exitFill.hold_seconds
      : Math.max(0, (exitTs - entryTs) / 1_000_000);
    return {
      id: exitFill.fill_pk ?? exitTs,
      account: exitFill.account_label,
      instance: exitFill.instance_id || (entryStub && entryStub.instance),
      symbol: exitFill.symbol,
      size: exitFill.qty,
      side: tradeSide,
      entryTime: new Date(entryTs / 1000).toISOString(),
      exitTime:  new Date(exitTs / 1000).toISOString(),
      duration: fmtDuration(holdS),
      entryPrice,
      exitPrice: exitFill.price,
      pnl: exitFill.pnl_dollars ?? 0,
      commissions: (entryStub ? (entryStub.commissions || 0) : 0) - (exitFill.commission || 0),
      fees: 0,
      strategy: exitFill.strategy || (entryStub && entryStub.strategy) || null,
      reason: exitFill.reason || (entryStub && entryStub.reason) || "--",
      // is_exit=true means this row is a completed round-trip; consumers
      // (WR, PF, dayClosed, etc.) filter on it to exclude open entries.
      isExit: true,
    };
  }

  // FIFO lot pairing — same algorithm the hub-side backfill runs (see
  // DAL.backfill_fill_exit_attribution) but on the client so the rendered
  // trade list shows one row per round-trip instead of one per fill.
  // Each (instance, account, symbol, strategy) book holds open entry
  // stubs; an is_exit=1 fill pops the oldest opposite-side stub and
  // emits a paired closed trade.  Unmatched entries stay as open rows.
  function pairFillsToTrades(fills) {
    const sorted = [...fills].sort((a, b) => (a.ts_us || 0) - (b.ts_us || 0));
    const books = new Map();
    const trades = [];
    const keyOf = (f) =>
      `${f.instance_id || ""}|${f.account_label || ""}|${f.symbol || ""}|${f.strategy || ""}`;
    for (const f of sorted) {
      const k = keyOf(f);
      const q = books.get(k) || [];
      if (!f.is_exit) {
        q.push(entryStubFromFill(f));
        books.set(k, q);
        continue;
      }
      const exitSide = String(f.side || "").toLowerCase();
      const exitSign = (exitSide === "buy" || exitSide === "long") ? +1 : -1;
      let entry = null;
      for (let i = 0; i < q.length; i++) {
        const eSign = q[i].side === "long" ? +1 : -1;
        if (eSign === -exitSign) { entry = q[i]; q.splice(i, 1); break; }
      }
      books.set(k, q);
      trades.push(pairedTradeFromFills(entry, f));
    }
    for (const [, q] of books) for (const stub of q) trades.push(stub);
    // Newest first so the WS prepend convention (and existing slice(0,5000)
    // cap that drops the oldest) keeps the most relevant rows.
    trades.sort((a, b) => Date.parse(b.exitTime) - Date.parse(a.exitTime));
    return trades.slice(0, 5000);
  }

  // Incremental version of pairFillsToTrades for a single live WS fill.
  // Mutates window.TRADES in place: entry fills prepend an open stub,
  // exit fills find the oldest matching open stub and replace it with a
  // paired closed trade.  Orphan exits (no stub in the window) fall
  // back to a single-leg closed row.
  function applyLiveFillToTrades(fill) {
    const cur = window.TRADES || [];
    if (!fill.is_exit) {
      window.TRADES = [entryStubFromFill(fill), ...cur].slice(0, 5000);
      return;
    }
    const exitSide = String(fill.side || "").toLowerCase();
    const exitSign = (exitSide === "buy" || exitSide === "long") ? +1 : -1;
    let matchIdx = -1, matchTs = Infinity;
    for (let i = 0; i < cur.length; i++) {
      const t = cur[i];
      if (t.isExit) continue;
      if (t.account !== fill.account_label) continue;
      if (t.symbol !== fill.symbol) continue;
      if ((t.strategy || null) !== (fill.strategy || null)) continue;
      if (fill.instance_id && t.instance && t.instance !== fill.instance_id) continue;
      const tSign = t.side === "long" ? +1 : -1;
      if (tSign === exitSign) continue;
      const ts = Date.parse(t.entryTime);
      if (Number.isFinite(ts) && ts < matchTs) { matchTs = ts; matchIdx = i; }
    }
    if (matchIdx >= 0) {
      const stub = cur[matchIdx];
      const paired = pairedTradeFromFills(stub, fill);
      const next = cur.slice();
      next.splice(matchIdx, 1);
      next.unshift(paired);
      window.TRADES = next.slice(0, 5000);
    } else {
      window.TRADES = [pairedTradeFromFills(null, fill), ...cur].slice(0, 5000);
    }
  }

  // Format a single agent event as a terminal-style log line and
  // prepend to window.INSTANCE_LOGS[instance_id].  Caps array at 500
  // so a long-running session can't grow it unbounded.  Mirror the
  // shape that data-live's bootstrap pass produces so InstanceLog
  // doesn't need to know the source.
  function pushInstanceLog(e) {
    const id = e.instance_id;
    if (!id) return;
    const p = e.payload || {};
    let lvl = "INFO", src = e.kind || "evt", txt = "";
    if (e.kind === "health") {
      const cpu = p.host?.cpu_pct;
      const mem = p.host?.mem_used_bytes && p.host?.mem_total_bytes
        ? Math.round(p.host.mem_used_bytes / p.host.mem_total_bytes * 100)
        : null;
      const load = p.host?.load_1m;
      txt = "health · cpu " +
        (cpu != null ? cpu.toFixed(1) : "?") + "% · mem " +
        (mem != null ? mem + "%" : "?") + " · load " +
        (load != null ? load.toFixed(2) : "?");
      lvl = cpu != null && cpu > 90 ? "WARN" : "INFO";
      src = "health";
    } else if (e.kind === "alert") {
      lvl = (p.severity || "warn").toUpperCase();
      src = "alert";
      txt = (p.code ? "[" + p.code + "] " : "") + (p.message || p.code || "(unknown)");
    } else {
      txt = e.kind + " · " + (e.account_label || "");
    }
    const ts = e.ts_us || Date.now() * 1000;
    const tIso = new Date(ts / 1000).toISOString();
    const line = { t: tIso.slice(11, 19), ts, lvl, src, txt };
    const arr = (window.INSTANCE_LOGS[id] = window.INSTANCE_LOGS[id] || []);
    arr.unshift(line);  // newest-first to match bootstrap order
    if (arr.length > 500) arr.length = 500;
  }

  function eventToFeed(e) {
    return {
      t: new Date((e.ts_us || Date.now()*1000) / 1000).toISOString().slice(11,19),
      tag: e.kind === "fill" ? "fill"
         : e.kind === "alert" ? "risk"
         : (e.kind === "status" || e.kind === "health") ? "sys"
         : "signal",
      txt: formatEventText(e),
    };
  }

  function formatEventText(e) {
    const p = e.payload || {};
    const acct = e.account_label || "--";
    if (e.kind === "fill")  return `${acct} · ${p.symbol||"?"} ${p.side||"?"} ${p.qty??""} @ ${p.price??"?"}`;
    if (e.kind === "alert") return `${acct} · ${(p.severity||"?").toUpperCase()} · ${p.message||p.code||""}`;
    if (e.kind === "equity") return `${acct} · equity $${(p.balance||0).toLocaleString()} (day ${(p.day_pnl||0)>=0?"+":""}$${Math.abs(p.day_pnl||0).toLocaleString()})`;
    if (e.kind === "health") {
      const cpu = p.host?.cpu_pct;
      const mem = p.host?.mem_used_bytes && p.host?.mem_total_bytes
        ? `${Math.round(p.host.mem_used_bytes/p.host.mem_total_bytes*100)}%`
        : "--";
      return `health · cpu ${cpu?.toFixed?.(1)||"?"}% · mem ${mem} · load ${(p.host?.load_1m||0).toFixed(2)}`;
    }
    return `${e.kind} · ${e.instance_id}${e.account_label ? "/"+e.account_label : ""}`;
  }

  function humanDuration(secs) {
    secs = Math.max(0, Math.round(secs));
    const h = Math.floor(secs/3600), m = Math.floor((secs%3600)/60);
    return h > 0 ? `${h}h ${pad2(m)}m` : `${m}m`;
  }
  function pad2(n) { return String(n).padStart(2,"0"); }

  /* -- state reset (wipe seeded mock fixtures) -- */

  // Replace data.jsx's mock helper with one that reads positions from
  // the live equity payload stashed on each account.
  window.positionsForAccount = function(acctId) {
    const a = (window.ACCOUNTS || []).find(x => x.id === acctId);
    return a && Array.isArray(a.positions) ? a.positions : [];
  };

  // Bring agent-emitted position rows into the shape the AccountDetail
  // and per-strategy panels render against ({sym, entry, mark, unreal}).
  // Mirrors both name conventions so code keyed on either side keeps
  // working.  Without this, a position straight from the WS or the
  // /api/events bootstrap missed p.entry / p.unreal / p.sym and crashed
  // the detail page at `p.entry.toFixed(2)`.
  window.normalizePosition = function(pp) {
    if (!pp || typeof pp !== "object") return pp;
    const entry = pp.entry_price ?? pp.entry ?? 0;
    const mark = pp.mark ?? pp.mark_price ?? entry;
    const unreal = pp.unrealized ?? pp.unreal ?? 0;
    return {
      ...pp,
      sym: pp.sym ?? pp.symbol ?? "—",
      symbol: pp.symbol ?? pp.sym ?? "—",
      qty: Number(pp.qty ?? 0),
      side: pp.side ?? "long",
      entry,
      entry_price: entry,
      mark,
      unreal,
      unrealized: unreal,
    };
  };

  // Strip the agent-embedded "P&L=±$N" token from a strategy status line.
  // mr emits e.g. "long entry=… SL=… TP=… P&L=+$363", but that number is
  // BAR-cadence — recomputed once per closed bar and frozen between bars —
  // while the live tick-by-tick floating P&L is shown separately (the
  // pnl-mini / position rows derive it from positions[].unrealized, which
  // ticks every price update).  Rendering the frozen token next to the live
  // one reads as "P&L stuck / as if already closed", so we strip it at every
  // status-line render site.  Single source of truth for the strip rule.
  window.cleanSig = function (s) {
    if (!s || typeof s !== "string") return s;
    return s.replace(/\s*\bP&L=[+\-−]?\$?-?\d[\d.,]*/g, "").trim();
  };

  function resetState() {
    window.INSTANCES   = [];
    window.ACCOUNTS    = [];
    window.ACCOUNTS_INACTIVE = [];   // cemetery: soft-deleted / replaced-by-HELLO rows
    window.TRADES      = [];
    window.FEED        = [];
    window.STRATS      = [];
    // STRATEGY_DEFS: wipe unconditionally so the data.jsx mock catalog
    // (sd-mgc-alpha, sd-nq-orb, sd-mgc-grind) doesn't bleed into the
    // "+ Add strategy" picker on account detail.  seedKnownStrategyDefs
    // is invoked right after to repopulate with the agent's real
    // valid_kinds (orb, mr, orb_test, pair_divergence, ma_cross).
    window.STRATEGY_DEFS = [];
    try { seedKnownStrategyDefs(); } catch (_) {}
    window.CONFIGS     = [];
    window.COLLECTORS  = [];
    window.RESEARCH    = [];           // legacy: agent-emitted research_catalog (file scan)
    window.PROPOSALS   = [];
    // Continuous Research catalog — sourced from /api/research/{runs,
    // artifacts, candidates}.  These survive a fresh boot because the
    // hub persists them in sqlite (schema.sql §research_runs / model_artifacts /
    // strategy_candidates) regardless of agent connectivity.
    window.RESEARCH_RUNS = [];
    window.RESEARCH_ARTIFACTS = [];
    window.RESEARCH_CANDIDATES = [];
    window.REGIME      = { asOf: "--", symbols: [] };
    window.SPC_ROWS    = [];
    window.SPC_BASELINES = {};  // {`${acct}|${strat}`: {expected_wr, expected_pnl, ...}}
    window.ALERTS      = [];           // raw alert rows from /api/alerts + WS kind:"alert"
    window.OPEN_POSITIONS = {};
    window.EQUITY_TOTAL = [];
    window.EQUITY_BY_STRATEGY = {};   // { "<slug>": [equity, ...] } — fleet equity per strategy
    window.PNL_TOTAL    = [];         // overall P&L = EQUITY_TOTAL - allocated_anchor_total
    window.PNL_BY_STRATEGY = {};      // { "<slug>": [pnl, ...] } — per-strategy P&L (equity minus per-strategy allocated)
    window.EQUITY_TS    = [];         // parallel ts_us array — point i across EQUITY_TOTAL /
                                      // PNL_TOTAL / EQUITY_BY_STRATEGY / PNL_BY_STRATEGY occurs at EQUITY_TS[i].
                                      // Bootstrap seeds with the hourly /api/equity ts; WS ticks append wall-clock.
    window.AUM_30D_AGO  = null;       // fleet AUM at ~30d ago — used for 30D P&L KPI
    window.AUM_30D_HAS_HIST = false;  // true iff ≥1 account actually has a 30d-old equity row
    window.INSTANCE_LOGS = {};
    window.COL_EVENTS  = [];
    window.STORAGE_ROLLUP = { used:0, cap:0, ticksToday:0, tps:0 };
    bump();
  }

  /* -- collector payload normalization --
   *
   * The agent emits collector_stats with whatever fields it currently
   * supports (snake_case, often partial). PageCollectors expects a
   * richer camelCase shape (rate[], symbols[], storage{}, quality{},
   * etc.). Without defaults, a partial payload crashes the page on
   * `Math.max(...c.rate)` and friends. Keep the agent's keys as-is so
   * any future fields ride through; only fill in the gaps the renderer
   * relies on.
   */
  function normalizeCollector(p) {
    const c = p || {};
    const stor = c.storage || {};
    const qual = c.quality || {};
    return {
      ...c,
      host:           c.host || c.hostname || c._instance_id || "—",
      id:             c.id || c.collector_id || c.host || "—",
      state:          c.state || "running",
      venue:          c.venue || c.feed || "—",
      feed:           c.feed || c.venue || "—",
      pid:            Number(c.pid || 0),
      primarySymbol:  c.primarySymbol || c.primary_symbol
                      || (Array.isArray(c.symbols) && c.symbols[0]) || "—",
      symbols:        Array.isArray(c.symbols) ? c.symbols : [],
      rate:           Array.isArray(c.rate) ? c.rate : [],
      peakTps:        Number(c.peakTps ?? c.peak_tps ?? 0),
      ticksPerSec:    Number(c.ticksPerSec ?? c.ticks_per_sec ?? 0),
      ticksToday:     Number(c.ticksToday ?? c.ticks_today ?? 0),
      gapsToday:      Number(c.gapsToday ?? c.gaps_today ?? 0),
      lagMs:          Number(c.lagMs ?? c.lag_ms ?? 0),
      cpu:            Number(c.cpu ?? 0),
      mem:            Number(c.mem ?? 0),
      uptime:         c.uptime || "—",
      storage: {
        // Accept both naming conventions: the agent's collector_stats emit
        // uses `used_gb` / `capacity_gb` (lifecycle.py:_emit_collector_snapshot),
        // older mock data used bare `used` / `capacity`.  Reading only the
        // bare keys made the Collectors page show "0.00 TB of 0.0 TB · 0.0%"
        // because the live payload's _gb keys went unread.
        used:      Number(stor.used ?? stor.used_gb ?? c.storage_used ?? 0),
        capacity:  Number(stor.capacity ?? stor.capacity_gb ?? c.storage_capacity ?? 0),
        format:    stor.format || c.storage_format || "—",
        shards:    Number(stor.shards ?? c.shards ?? 0),
        retention: stor.retention || c.retention || "—",
      },
      quality: {
        coverage: Number(qual.coverage ?? c.coverage ?? 1),
        dupRate:  Number(qual.dupRate ?? c.dup_rate ?? 0),
        lastBar:  qual.lastBar || c.last_bar || "—",
      },
      backfill: c.backfill || null,
      // Staleness: agents emit collector_stats every 10s.  Anything older
      // than 30s in wall-clock should be flagged so a frozen-last-emit
      // doesn't masquerade as a live reading.  `_ts_us` is microseconds
      // since epoch, stamped by the hub on every event.
      stale: c._ts_us ? ((Date.now() * 1000) - c._ts_us) > 30_000_000 : true,
    };
  }

  /* -- regime derivation from collector telemetry --
   *
   * The Research page's "Market regime · live" cells expect
   *   {sym, venue, price, trend, regime, atrPct, atrZ, adx, note}
   * per symbol.  We don't (yet) compute true regime metrics agent-side;
   * for now derive a coarse "active/thin/quiet" indicator from the
   * collector's tps reading, plus the last_price / venue from the same
   * payload.  When the agent grows real regime fields, this helper
   * upgrades transparently — it just spreads everything the collector
   * supplies and falls back to the placeholder fields.
   */
  function rebuildRegimeFromCollectors() {
    const cols = window.COLLECTORS || [];
    const cells = [];
    for (const c of cols) {
      const sym = c.primary_symbol || (c.symbols && c.symbols[0]) || c.collector_id;
      if (!sym) continue;
      const tps = Number(c.ticks_per_sec || 0);
      const lastPrice = Number(c.last_price ?? c.px ?? 0);
      const reg = c.regime || (tps > 30 ? "active" : tps > 5 ? "normal" : "thin");
      cells.push({
        sym,
        venue: c.venue || c.feed || "—",
        price: lastPrice,
        trend: c.trend || "flat",
        regime: reg,
        atrPct: Number(c.atr_pct ?? 0),
        atrZ:   Number(c.atr_z ?? 0),
        adx:    Number(c.adx ?? 0),
        note:   c.note || `tps ${tps.toFixed(1)} · ${c.state || "—"}`,
      });
    }
    const stamp = cells.length
      ? new Date().toLocaleTimeString("en-US", {hour: "2-digit", minute: "2-digit"})
      : "--";
    window.REGIME = { asOf: stamp, symbols: cells };
  }

  /* -- config normalizer: map agent payload to PageConfigs fields --
   *
   * Agent emits per-config items with snake_case names + a `strategies`
   * list of slugs.  PageConfigs reads camelCase + `strategyDefs[]`.  We
   * normalize here so the agent's payload schema stays stable while the
   * dashboard renders without each call-site doing the field flip.
   */
  function normalizeConfig(c) {
    const lastMs = Number(c.last_modified_us ?? 0) / 1000;
    const lastEdit = lastMs
      ? new Date(lastMs).toISOString().replace("T", " ").slice(0, 16) + " UTC"
      : "—";
    // `body` is forwarded as-is when the agent ships it: the config
    // editor uses it as the file's TOML content.  Most agents don't
    // yet include `body` in config_inventory events (they emit
    // metadata only) — in that case the editor falls back to the
    // "TOML body not cached" placeholder.  When the agent starts
    // shipping it (one-line change in its config_inventory build:
    // `body=open(path).read()`), the editor lights up automatically.
    const body = typeof c.body === "string" ? c.body
               : typeof c.toml === "string" ? c.toml
               : null;
    return {
      id:            c.id || c.file || "config",
      label:         c.label || c.id || "active",
      file:          c.file || "—",
      active:        !!c.active,
      strategyDefs:  Array.isArray(c.strategies) ? c.strategies : (c.strategyDefs || []),
      dailyLossCap:  Number(c.daily_loss_cap ?? c.dailyLossCap ?? 0),
      maxConcurrent: Number(c.max_concurrent ?? c.maxConcurrent ?? 0),
      lastEdit,
      note:          c.summary || c.note || "",
      body,
      _instance_id:  c._instance_id,
    };
  }

  /* -- debounced SPC refresh on new exit fills --
   *
   * SPC is computed hub-side from fills, so we re-fetch /api/spc after
   * an exit fill rather than recomputing client-side.  Debounce to ~3s
   * so a chain of exits during a flatten doesn't fire one request per
   * fill.  Returns immediately if there's already a pending timer.
   */
  let _spcTimer = null;
  function scheduleSpcRefresh() {
    if (_spcTimer) return;
    _spcTimer = setTimeout(async () => {
      _spcTimer = null;
      try {
        const r = await fetch("/api/spc?limit=200", { credentials: "same-origin" });
        if (r.ok) {
          window.SPC_ROWS = await r.json();
          bump();
        }
      } catch (_) { /* tolerated; next refresh will retry */ }
    }, 3000);
  }

  /* -- debounced /api/alerts refresh after a WS-pushed alert event --
   *
   * The WS frame doesn't carry the DB alert_pk (it's assigned at INSERT,
   * after the ingest handler forwards the event).  We optimistically
   * prepend a synthetic row so the Alerts inbox shows it instantly, then
   * debounce-refresh from /api/alerts so the row picks up its real
   * alert_pk (needed for the POST /api/alerts/{pk}/ack endpoint).
   */
  let _alertsTimer = null;
  function scheduleAlertsRefresh() {
    if (_alertsTimer) return;
    _alertsTimer = setTimeout(async () => {
      _alertsTimer = null;
      try {
        const r = await fetch("/api/alerts?limit=200", { credentials: "same-origin" });
        if (r.ok) {
          window.ALERTS = await r.json();
          bump();
        }
      } catch (_) { /* tolerated; next refresh will retry */ }
    }, 1500);
  }

  /* -- browser notification for crit alerts --
   *
   * Fires when a WS alert frame arrives with normalised severity = crit
   * AND the user has previously granted Notification permission (we never
   * auto-prompt outside a user gesture — PageAlerts surfaces an "Enable
   * notifications" button for the opt-in flow).  Title carries the source
   * + account so the OS notification is actionable without expanding.
   */
  function normalizeSeverityForNotify(s) {
    const v = String(s || "").toLowerCase();
    if (v === "crit" || v === "critical" || v === "alarm" || v === "error") return "crit";
    if (v === "warn" || v === "warning") return "warn";
    return "info";
  }
  function maybeNotifyCrit(msg) {
    try {
      if (typeof window.Notification === "undefined") return;
      if (Notification.permission !== "granted") return;
      const p = msg.payload || {};
      if (normalizeSeverityForNotify(p.severity) !== "crit") return;
      const acct = msg.account_label ? ` · ${msg.account_label}` : "";
      const src  = p.source ? ` [${p.source}]` : "";
      const body = (p.message || p.code || "(no message)").slice(0, 200);
      const n = new Notification(`aviaf · CRIT${acct}${src}`, {
        body,
        tag: "aviaf-alert-" + (msg.ts_us || Date.now()),
        requireInteraction: false,
      });
      n.onclick = () => { try { window.focus(); location.hash = "#/alerts"; n.close(); } catch (_) {} };
    } catch (_) { /* never let notify path crash applyFrame */ }
  }

  /* -- runtime-strategy roster derivation --
   *
   * The per-account "Performance · research vs live" section + the
   * instance-detail Strategies-attached table both read window.STRATS,
   * a flat list of {acct, defId, name, pnl30, riskPerTrade, ...} rows
   * — one per (account, strategy) deployment.  We derive these from the
   * already-fetched accounts + per-account totals so the section renders
   * even before the agent emits a proper strategy_def_inventory.
   *
   * Best-effort: numbers come from the account totals (pnl30 = account
   * totalPnl) and split evenly when an account runs multiple strategies.
   * Once the agent stamps per-fill strategy attribution AND emits real
   * bench numbers, this helper graduates to using PNL_BY_STRATEGY.
   */
  function rebuildStratsFromAccounts() {
    const out = [];
    const trades = window.TRADES || [];
    // Index exit fills by (acct, strategy) so each STRATS entry carries
    // its own wr/pf/eR/trades — the Divergence row needs these to
    // weight live-vs-bench averages.  Falls back to single-strategy
    // account attribution when a fill lacks `strategy`.
    const exitsBy = {};
    for (const t of trades) {
      if (!t.isExit) continue;
      let sId = t.strategy;
      if (!sId) {
        // Unattributed fill — resolve in priority order:
        //   1. operator-declared primary (ACCOUNT_PRIMARY_STRATEGY)
        //      — explicit intent, used regardless of how many
        //      strategies the account has.
        //   2. single-strategy account — unambiguous, use it.
        //   3. multi-strategy account, no primary — stash in an
        //      explicit `_unattributed` bucket so it's visible on
        //      the map without inflating any specific strategy.
        //
        // The prior `a.strategies[0]` fallback over-attributed every
        // untagged fill to whatever the agent listed first in HELLO.
        // For prac (orb_v5/orb_rule/orb_unfiltered/mr/ma_cross/fomc/
        // ma_cross_ng) that silently rolled untagged ORB fills into
        // mr_v3 and inflated mr_v3's reported P&L (~38.9k observed
        // 2026-05-25 — operator knew that couldn't be right).
        const primary = window.accountPrimaryStrategy
          ? window.accountPrimaryStrategy(t.account)
          : null;
        if (primary) {
          sId = primary;
        } else {
          // Search inactive accounts too — a retired combine's untagged
          // fills still belong to whatever it was running (see the
          // RETIRED-ACCOUNT INCLUSION note below).
          const a = (window.ACCOUNTS || []).find(x => x.id === t.account)
            || (window.ACCOUNTS_INACTIVE || []).find(x => x.id === t.account);
          const ss = a?.strategies || [];
          if (ss.length === 0) continue;
          if (ss.length === 1) sId = ss[0];
          else sId = "_unattributed";
        }
      }
      // Operator-curated rename: fold fills tagged with a legacy slug
      // ("orb" on combine4) into the canonical strategy bucket the
      // operator considers their actual home.  See FILL_REMAP above.
      if (window.fillSlugRemap) sId = window.fillSlugRemap(t.account, sId);
      const k = `${t.account}:${sId}`;
      (exitsBy[k] = exitsBy[k] || []).push(t);
    }

    // Bare-class collapse pass: agent emits fill.strategy="mr" (the
    // bare class slug) but operator's rostered variant is "mr_v2".
    // Fold the bare class slug into the rostered variant so the bubble
    // lights up with real P&L instead of splitting the chart into a
    // "mr" bubble with fills and a "mr_v2" bubble at $0.
    //
    // This used to be a much broader "any slug in the same inferred
    // class" collapse, but that over-attributed: anything that prefix-
    // matched mr (mr_v2, mr_v3, mean_reversion, mr_anything…) on a
    // multi-variant account would all funnel into the single rostered
    // variant — operator reported mr_v3 inflated to ~38.9k on prac
    // (2026-05-25) because that's where the collapse dumped everything.
    //
    // Now only the BARE class slug collapses.  A fill tagged "mr_v2"
    // on prac (roster only has "mr") will surface as its own bubble
    // rather than getting silently pumped into mr_v3.
    if (window.inferStrategyClass) {
      const classIds = new Set((window.STRATEGY_CLASSES || []).map((c) => c.id));
      for (const k of Object.keys(exitsBy)) {
        const sep = k.indexOf(":");
        const acct = k.slice(0, sep);
        const sId  = k.slice(sep + 1);
        if (!classIds.has(sId)) continue;  // not a bare class slug → leave alone
        const a = (window.ACCOUNTS || []).find(x => x.id === acct)
          || (window.ACCOUNTS_INACTIVE || []).find(x => x.id === acct);
        if (!a) continue;
        const roster = a.strategies || [];
        if (roster.includes(sId)) continue;  // bare slug IS rostered → no collapse needed
        const klass = window.inferStrategyClass(sId);
        if (klass === "other") continue;
        const matches = roster.filter(rId => window.inferStrategyClass(rId) === klass);
        if (matches.length !== 1) continue;
        const newK = `${acct}:${matches[0]}`;
        exitsBy[newK] = (exitsBy[newK] || []).concat(exitsBy[k]);
        delete exitsBy[k];
      }
    }

    // Rollout cutoff pass: when STRATEGY_CONFIG_SEED for an (acct, sId)
    // declares a `since` date, split that bucket into:
    //   - current variant (post-`since`)  → keeps the original key
    //   - legacy variant  (pre-`since`)  → moves to `<sId>_legacy`
    // Critical for accuracy: a slug like prac:mr is the SAME agent
    // name across mr_v1 → mr_v2 → mr_v3 rollouts.  Without this split
    // the dashboard labels the bucket "mr_v3" but counts every fill
    // mr ever made on that account, inflating mr_v3's reported P&L
    // by the lifetime of every prior ML model.  Operator-confirmed
    // on prac:mr (2026-05-25) — actual mr_v3 era ~$5.6k legit, but
    // the bare bucket was showing $38.9k.
    //
    // Override per-bucket via localStorage:
    //   localStorage.setItem("aviaf.strategy.since",
    //     JSON.stringify({"prac:mr_v3": "2026-05-22T00:00:00Z"}))
    if (window.strategyConfigFor) {
      let userSince = {};
      try {
        const raw = localStorage.getItem("aviaf.strategy.since");
        if (raw) userSince = JSON.parse(raw) || {};
      } catch (_) {}
      for (const k of Object.keys(exitsBy)) {
        const sep = k.indexOf(":");
        const acct = k.slice(0, sep);
        const sId  = k.slice(sep + 1);
        if (sId.endsWith("_legacy")) continue;
        const cfg = window.strategyConfigFor(acct, sId);
        const sinceStr = userSince[k] || (cfg && cfg.since);
        if (!sinceStr) continue;
        const cutoff = Date.parse(sinceStr);
        if (!Number.isFinite(cutoff)) continue;
        const pre = [], post = [];
        for (const t of exitsBy[k]) {
          const ms = t.exitTime ? Date.parse(t.exitTime) : 0;
          if (ms < cutoff) pre.push(t); else post.push(t);
        }
        if (pre.length === 0) continue;
        const legacyK = `${acct}:${sId}_legacy`;
        exitsBy[legacyK] = (exitsBy[legacyK] || []).concat(pre);
        exitsBy[k] = post;
      }
    }

    // Multi-era bucket split — generalisation of the single-cutoff
    // legacy pass above.  When STRATEGY_ERAS[acct][sId] declares an
    // ordered list of model rollouts, partition the bucket's fills
    // by exitTime so each model era shows in its own row.
    //
    // Required for accounts where the agent ships the same bare slug
    // ("mr") across multiple model rollouts (mr_v1 → v2 → v3 → v4).
    // FILL_REMAP folds the bare slug into the *latest* canonical
    // bucket (prac:mr → prac:mr_v3 on the current FILL_REMAP), but
    // that traps every old model's fills in the latest bucket and
    // inflates its reported P&L by the entire mr lineage.  Operator
    // confirmed (2026-05-26): prac:mr_v3 was showing combined v2+v3
    // + pre-v2 P&L, prac:mr_v4 was showing $0/0 trades despite real
    // unprofitable fills today.
    //
    // Each era entry: { since: ISO_date_or_omit, into: bucket_slug }.
    // Processed newest-first; a fill lands in the first era whose
    // `since` it satisfies.  Last era without `since` is the catch-
    // all for fills older than every cutoff.
    if (window.strategyErasFor) {
      for (const k of Object.keys(exitsBy)) {
        const sep = k.indexOf(":");
        const acct = k.slice(0, sep);
        const sId  = k.slice(sep + 1);
        if (sId.endsWith("_legacy")) continue;
        const eras = window.strategyErasFor(acct, sId);
        if (!eras || eras.length === 0) continue;
        const erasParsed = eras
          .map((e) => ({
            since: e.since ? Date.parse(e.since) : -Infinity,
            into:  e.into,
          }))
          .filter((e) => e.into)
          .sort((a, b) => b.since - a.since);  // newest-first
        if (erasParsed.length === 0) continue;
        const stays = [];
        for (const t of exitsBy[k]) {
          const ms = t.exitTime ? Date.parse(t.exitTime) : 0;
          const era = erasParsed.find((e) => ms >= e.since);
          if (!era) { stays.push(t); continue; }
          const targetK = `${acct}:${era.into}`;
          if (targetK === k) { stays.push(t); continue; }
          (exitsBy[targetK] = exitsBy[targetK] || []).push(t);
        }
        if (stays.length === 0) {
          delete exitsBy[k];
        } else {
          exitsBy[k] = stays;
        }
      }
    }

    // CME trading-day key for "today" — matches the calendar's bucketing
    // (window.tradingDayKey, 17:00-CT rollover) so per-strategy "today"
    // agrees with the highlighted calendar cell AND with TopStep.
    const _now = new Date();
    const _nowMs = _now.getTime();
    const _7dCutoff  = _nowMs - 7  * 86400000;
    const _30dCutoff = _nowMs - 30 * 86400000;
    const _todayKey = window.tradingDayToday();
    const _localKey = (iso) => window.tradingDayKey(iso);
    const _exitMs = (t) => t.exitTime ? Date.parse(t.exitTime) : 0;
    // Per-(account,strategy) 1R, captured in the loop so the cross-account
    // aggregation below can convert each trade to R using its own account's
    // risk unit (R is unitless → mergeable across accounts).
    const riskByKey = {};
    // Peak-to-trough drawdown on the cumulative P&L curve of a slice
    // of exits.  Called once per (strategy, window) — 7d / 30d / all —
    // so the risk-return map's y-axis tracks the same time window the
    // x-axis is showing.  Sorts by exitTime; safe on empty input.
    const _ddOfSlice = (slice) => {
      if (!slice.length) return 0;
      const sorted = [...slice].sort((x, y) => _exitMs(x) - _exitMs(y));
      let cum = 0, peak = 0, mdd = 0;
      for (const t of sorted) {
        cum += Number(t.pnl) || 0;
        if (cum > peak) peak = cum;
        if (peak - cum > mdd) mdd = peak - cum;
      }
      return mdd;
    };
    // RETIRED-ACCOUNT INCLUSION (2026-07-26).
    //
    // STRATS used to be derived from window.ACCOUNTS only — which excludes
    // active=0 / decommissioned accounts.  A TopStep combine that failed or
    // got decommissioned MID-WINDOW therefore vanished from every
    // per-strategy number the instant it was deactivated, even though its
    // trades sit inside the 7d/30d reporting window.  Verified against
    // TopStep /Trade/search 2026-07-26: combine8 (38 trades / −$1,889) and
    // combine9 (18 / −$4,304) both traded within the past 30 days and BOTH
    // were missing — so `ma_cross_gcv` (−$6.1k across those two accounts)
    // appeared NOWHERE on the strategies page, and ma_cross_hg / ma_cross_ng
    // / mr_v2 aggregates understated their losses.  A strategy's P&L must
    // not improve just because the account it lost money on was retired.
    //
    // Included: inactive accounts with at least one attributed exit inside
    // the 30-day window (the reporting horizon).  Long-dead accounts (prac,
    // combine4/5, express1 — last traded 2026-05/06) stay out so the map
    // doesn't fill with historical clutter.  Rows carry retired=true so the
    // UI can annotate them; their state resolves to "stopped" naturally
    // (no strategyStatus on a dead agent account).
    const _retiredWithRecentFills = (window.ACCOUNTS_INACTIVE || []).filter((a) => {
      if (!a || !a.id) return false;
      // Same-broker-account duplicate labels (dev-prac vs dev-prac1 point at
      // PRAC-…-63543377) must not double-count: skip an inactive label whose
      // broker account_id is also held by an ACTIVE account.
      const dupActive = (window.ACCOUNTS || []).some(
        (x) => x && x.account_id && a.account_id && x.account_id === a.account_id,
      );
      if (dupActive) return false;
      return Object.keys(exitsBy).some(
        (k) => k.startsWith(`${a.id}:`)
          && (exitsBy[k] || []).some((t) => _exitMs(t) >= _30dCutoff),
      );
    });
    const _retiredSet = new Set(_retiredWithRecentFills);
    for (const a of [...(window.ACCOUNTS || []), ..._retiredWithRecentFills]) {
      const acctRetired = _retiredSet.has(a);
      // Build the per-account strategy roster from the UNION of every
      // source that's seen a variant:
      //   1. a.strategies        — from /api/accounts (HELLO roster)
      //   2. a.strategyStatus    — from equity emits (live + bootstrap)
      //   3. exitsBy keys        — fills already attributed
      // This means a variant never disappears just because one source
      // missed it.  Critical for the risk-return map: /api/accounts
      // captures slugs at agent HELLO and goes stale after a hot-attach
      // of orb_rule/orb_v5/orb_unfiltered; without unioning, the map
      // only ever shows the boot-time slug list.
      const fromRoster = Array.isArray(a.strategies) ? a.strategies : [];
      const fromStatus = a.strategyStatus ? Object.keys(a.strategyStatus) : [];
      const fromExits = Object.keys(exitsBy)
        .filter((k) => k.startsWith(`${a.id}:`))
        .map((k) => k.slice(a.id.length + 1));
      // Canonicalize each raw slug via FILL_REMAP BEFORE deduping.
      // Without this, the roster ships bare "mr" while exitsBy has
      // canonical "mr_v2" (because FILL_REMAP folds orb fills into
      // mr_v2 on combine4) — both end up in the list and the
      // dashboard renders two rows for what's actually one strategy.
      // Post-canonicalization the seen-Set catches them as the same
      // sid and only one row is emitted.  The render-time skip below
      // (continue when remap differs) becomes a no-op safety net.
      const remap = (sid) =>
        (window.fillSlugRemap ? window.fillSlugRemap(a.id, sid) : sid) || sid;
      // When a slug has a multi-era split (STRATEGY_ERAS), expand
      // it to its era targets so every era gets its own roster row
      // even if one era has zero fills yet.  Without this, prac.mr_v4
      // wouldn't appear on the map until its first fill arrived —
      // operator can't see a deployed-but-quiet variant.
      const eraTargets = (sid) => {
        const eras = window.strategyErasFor ? window.strategyErasFor(a.id, sid) : null;
        if (!eras || eras.length === 0) return null;
        return eras.map((e) => e.into).filter(Boolean);
      };
      const seen = new Set();
      const list = [];
      const addOne = (sid) => {
        if (!seen.has(sid)) { seen.add(sid); list.push(sid); }
      };
      for (const sidRaw of [...fromRoster, ...fromStatus, ...fromExits]) {
        if (!sidRaw) continue;
        const sid = remap(sidRaw);
        const targets = eraTargets(sid);
        if (targets) {
          targets.forEach(addOne);
        } else {
          addOne(sid);
        }
      }
      if (list.length === 0) continue;
      window.__LIVE.strats_ready = true;   // signals the risk-return map
                                           // to swap shimmer → real bubbles
      const splitRisk = (a.initial_capital || 0) * 0.005;   // 0.5% of capital placeholder
      // True iff at least one strategy on the account has any attributed
      // exits.  When true, each strategy row uses its own attributed
      // exits — strategies with no fills correctly show $0/"—".  When
      // false (orb traded but its fills lack a strategy slug, etc.),
      // every strategy row also reads $0 — the previous "even split"
      // fallback manufactured P&L on strategies that never fired.
      const acctHasAttribution = list.some(
        (sId) => (exitsBy[`${a.id}:${sId}`] || []).length > 0,
      );
      for (const sId of list) {
        // Skip slugs whose fills are folded into another bucket via
        // FILL_REMAP — the canonical strat row consumes them, so emitting
        // this one too would create a phantom $0 bubble alongside the
        // populated bubble.
        if (window.fillSlugRemap && window.fillSlugRemap(a.id, sId) !== sId) continue;
        const exits = exitsBy[`${a.id}:${sId}`] || [];
        const tradeCount = exits.length;
        const wins = exits.filter(t => (t.pnl||0) > 0);
        const losses = exits.filter(t => (t.pnl||0) < 0);
        const wr = tradeCount ? wins.length / tradeCount : null;
        const stratGrossWin = wins.reduce((s,t)=>s+(t.pnl||0), 0);
        const stratGrossLoss = Math.abs(losses.reduce((s,t)=>s+(t.pnl||0), 0));
        // Same "all winners → ∞" convention as PageAccountDetail; the
        // strategy-mini-table and Overview Profit Factor KPI consume
        // pf as a number, so we leave Infinity here and let renderers
        // gate Number.isFinite to decide the display string.  Overview's
        // fleet-PF KPI re-aggregates from grossWin / grossLoss to avoid
        // Infinity poisoning the weighted-average.
        const pf = stratGrossLoss > 0
          ? stratGrossWin/stratGrossLoss
          : (stratGrossWin > 0 ? Infinity : null);
        const realizedAll = exits.reduce((s,t)=>s+(t.pnl||0), 0);
        // Time-windowed realized P&L.  Until 2026-05-24 the dashboard
        // emitted a single `pnl30` field that was actually all-time
        // realized — the risk-return map's "Weekly" mode divided it
        // by 4.3, which lied about strategies whose recent week
        // diverged sharply from their lifetime curve.  Now pnl30
        // actually means last-30-day, plus pnl7 (real last-7-day)
        // and pnlAll for the explicit all-time read.
        const exits7  = exits.filter((t) => _exitMs(t) >= _7dCutoff);
        const exits30 = exits.filter((t) => _exitMs(t) >= _30dCutoff);
        const realized7  = exits7.reduce((s,t)=>s+(t.pnl||0), 0);
        const realized30 = exits30.reduce((s,t)=>s+(t.pnl||0), 0);
        // Today's P&L = sum of THIS strategy's exits stamped today
        // (local).  Critical: without this filter, today was just
        // a.dayPnl / N which lied — e.g. prac with 4 strategies
        // attached showed each as having today P&L even if only one
        // (or zero) actually traded today.
        const todayPnl = exits
          .filter((t) => t.exitTime && _localKey(t.exitTime) === _todayKey)
          .reduce((s, t) => s + (t.pnl || 0), 0);
        // E[R] = mean P&L per trade ÷ risk per trade.  Falls back to
        // null when either is unknown so the dashboard renders a dash
        // instead of NaN.  Uses all-time realized so a sparse-trading
        // strategy still has a stable E[R] even if it had no fills in
        // the last 30 days.
        // 1R = the strategy's risk per trade. Priority:
        //   1) agent-emitted intended risk (vol_target_dollars /
        //      target_risk_dollars / max_dollar_risk_per_trade) — cleanest.
        //   2) realized 1R = the strategy's average losing trade (a standard
        //      R proxy when there's no configured stop-risk; instrument/size
        //      agnostic because it's the strategy's own realized loss scale).
        //   3) the 0.5%-of-capital placeholder — last resort, no-trade case.
        const _agentRisk = (a.strategyRisk && a.strategyRisk[sId] > 0) ? a.strategyRisk[sId] : 0;
        const _avgLoss = (() => {
          const ls = exits.filter((t) => (Number(t.pnl) || 0) < 0).map((t) => Math.abs(Number(t.pnl)));
          return ls.length ? ls.reduce((s, v) => s + v, 0) / ls.length : 0;
        })();
        const riskUnit = _agentRisk > 0 ? _agentRisk : (_avgLoss > 0 ? _avgLoss : splitRisk);
        const riskUnitReal = _agentRisk > 0 || _avgLoss > 0;
        riskByKey[`${a.id}:${sId}`] = riskUnit;
        const eR = (tradeCount && riskUnit)
          ? (realizedAll / tradeCount) / riskUnit
          : null;
        // Drawdown (in $): worst peak-to-trough on the realized
        // cumulative P&L curve sorted by exitTime.  Three windows so
        // the risk-return map's y-axis can match the x-axis window.
        const stratDd7   = _ddOfSlice(exits7);
        const stratDd30  = _ddOfSlice(exits30);
        const stratDdAll = _ddOfSlice(exits);
        let stratLastSig = null;
        if (tradeCount) {
          const sortedExits = [...exits].sort(
            (x, y) => _exitMs(x) - _exitMs(y),
          );
          const lastExit = sortedExits[sortedExits.length - 1];
          stratLastSig = lastExit && lastExit.exitTime
            ? new Date(lastExit.exitTime).toISOString().slice(11, 16) + " UTC"
            : null;
        }
        // Sharpe / Sortino on per-trade $ P&L.  Falls back to 0 when
        // we don't have at least 2 trades (std undefined otherwise).
        let stratSharpe = 0, stratSortino = 0;
        if (tradeCount >= 2) {
          const mean = realizedAll / tradeCount;
          const variance = exits.reduce(
            (s, t) => s + Math.pow((Number(t.pnl) || 0) - mean, 2), 0,
          ) / tradeCount;
          const std = Math.sqrt(variance);
          stratSharpe = std > 0 ? mean / std : 0;
          const downside = exits.reduce((s, t) => {
            const p = Number(t.pnl) || 0;
            return p < 0 ? s + p * p : s;
          }, 0);
          const dStd = Math.sqrt(downside / tradeCount);
          stratSortino = dStd > 0 ? mean / dStd : 0;
        }
        // Resolve per-strategy today/pnl30:
        //   - attribution available → use this strategy's own exits
        //   - no attribution → $0 (the strategy genuinely hasn't traded
        //     under its own name; splitting account-level P&L across
        //     attached strategies manufactures trades on strategies that
        //     didn't fire.  The orb-traded / pair_div-flat case landed
        //     "$1,504.60 · $1,504.60" on the card even though pair_div
        //     never opened a position — falsely attributing orb's P&L
        //     to pair_div.)
        // Account-level total still renders on the card's header, so
        // the operator never loses sight of true account P&L; we just
        // stop lying about which *strategy* earned it.
        const resolvedToday   = acctHasAttribution ? todayPnl    : 0;
        const resolvedPnl7    = acctHasAttribution ? realized7   : 0;
        const resolvedPnl30   = acctHasAttribution ? realized30  : 0;
        const resolvedPnlAll  = acctHasAttribution ? realizedAll : 0;
        // Strategy state derivation.  STRATS used to hardcode
        // state="running" for every row, which meant the grey-disabled
        // LED never lit even when the agent had stopped a strategy.
        // Source of truth: a.strategyStatus is populated on every
        // equity emit with one entry per ACTIVELY-RUNNING strategy.
        // If a configured strategy slug is missing from strategyStatus,
        // the agent isn't running it → "stopped".  account_blocked
        // (kill-switch / MLL breach) overrides everything to "breached".
        const stratStatusMap = a.strategyStatus || {};
        const stratPausedMap = a.strategyPaused || {};
        const agentHasStrat = Object.prototype.hasOwnProperty.call(stratStatusMap, sId);
        let stratState;
        if (a.account_blocked) stratState = "breached";
        else if (!agentHasStrat) stratState = "stopped";
        else if (stratPausedMap[sId]) stratState = "paused";   // remote-paused: attached but no new entries
        else stratState = "running";
        // Per-strategy "has open position" + unrealized P&L.
        //
        // Single source of truth: agent's lifecycle.py emits each
        // positions[] row with the owning strategy_id (added 2026-05-20).
        // Dashboard attribution is direct — no text-regex heuristics.
        // For accounts with positions whose strategy_id matches `sId`,
        // the LED blinks and unrealized sums those rows.  Anything else
        // (untagged orphan positions, agent on a build that predates
        // the strategy_id field, etc.) → no blink, no unrealized.
        //
        // Single-strategy fallback survives only for the brief rollout
        // window between the dashboard going live and every agent
        // having restarted with the new lifecycle.py.  Once all agents
        // emit strategy_id, the `list.length === 1` branch is dead and
        // can be removed.
        const acctPositions = Array.isArray(a.positions) ? a.positions : [];
        const tagged = acctPositions.filter(p => p && p.strategy_id === sId);
        // Contract count = MAX across tagged positions, not sum.  A
        // single strategy holds at most one net position per symbol;
        // if the broker reports multiple rows (per-leg partial fills,
        // long+short hedge legs, or duplicated entries), summing
        // double-counts.  Operator saw "25c" on an mr trade that was
        // really 1 contract — the symptom of a SUM across phantom
        // rows.  MAX is correct for a strategy whose net exposure is
        // a single position.
        const maxAbsQty = (rows) => rows.reduce(
          (m, p) => Math.max(m, Math.abs(Number(p.qty) || 0)), 0,
        );
        const firstSymbol = (rows) => {
          for (const p of rows) {
            const sym = p.sym || p.symbol;
            if (sym) return String(sym);
          }
          return null;
        };
        let stratHasOpenPos;
        let stratUnrealized;
        let stratOpenContracts;
        let stratPosSymbol = null;
        if (tagged.length > 0) {
          stratHasOpenPos = true;
          stratUnrealized = tagged.reduce((s, p) => s + (Number(p.unrealized) || 0), 0);
          stratOpenContracts = maxAbsQty(tagged);
          stratPosSymbol = firstSymbol(tagged);
        } else if (
          list.length === 1
          && acctPositions.length > 0
          && acctPositions.every(p => p && p.strategy_id == null)
        ) {
          // Pre-rollout agent: positions are untagged AND this is the
          // only strategy on the account, so attribution is unambiguous.
          stratHasOpenPos = true;
          stratUnrealized = acctPositions.reduce((s, p) => s + (Number(p.unrealized) || 0), 0);
          stratOpenContracts = maxAbsQty(acctPositions);
          stratPosSymbol = firstSymbol(acctPositions);
        } else {
          stratHasOpenPos = false;
          stratUnrealized = 0;
          stratOpenContracts = 0;
        }
        const displayName = window.strategyLabelFor
          ? window.strategyLabelFor(a.id, sId)
          : sId;
        out.push({
          id:          `${a.id}:${sId}`,
          defId:       sId,
          acct:        a.id,
          instance:    a.instance,
          // Row belongs to a RETIRED (active=0 / decommissioned) account
          // that still traded inside the 30d window.  Kept so per-strategy
          // and cross-account P&L stay complete; flagged so the UI can say
          // "retired account" instead of implying it's still deployed.
          retired:     acctRetired,
          // EXTERNALLY-MANAGED row: reported by a read-only mirror instance
          // (ops/tools/topstep_mirror_agent.py feeds the 369979 login as
          // instance "topstep-mirror" with strategy "external").  Those
          // accounts trade, so their P&L belongs on the page — but they are
          // NOT aviaf deployments, so they must not inflate the live/stopped
          // deployment counts.
          external:    window.EXTERNAL_INSTANCES
            ? window.EXTERNAL_INSTANCES.has(a.instance)
            : false,
          // Operator-facing label.  Resolved via STRATEGY_CONFIG_SEED
          // + DEFAULT_VARIANT so bare "mr"/"orb" agent slugs never reach
          // the map (live agent doesn't yet emit _v2/_v3 suffixes).
          name:        displayName,
          // Real per-strategy metadata (symbol, contracts, since,
          // lastSig) isn't available from the agent's current emit
          // surface — leave them null so AccountCard's meta row
          // doesn't render placeholder "—" / "1c" garbage next to a
          // FLAT short status.  Once the agent ships a
          // strategy_def_inventory event with these fields, plug
          // them in here.
          symbol:      null,
          state:       stratState,
          hasOpenPos:  stratHasOpenPos,
          unrealized:  stratUnrealized,
          // Live contract count + symbol from open positions tagged
          // with this strategy.  Symbol is critical because the agent
          // sometimes leaves stale phantom positions in positions_latest
          // after the broker has flattened — operator saw "25c MNQ" on
          // an mr trade while actually holding "5c MGC" elsewhere.
          // Surfacing the symbol next to the count lets the operator
          // immediately catch the mismatch.
          openContracts: stratOpenContracts,
          posSymbol:   stratPosSymbol,
          // Snapshot the same ts the positions came from so the
          // renderer can gate the chip on freshness (hide / mark stale
          // when last update is > 5 min old).
          posLastUs:   Number(a.strategiesLastUs) || 0,
          // Configured contracts-per-trade from the agent's strategy status
          // emit (added 2026-06-01).  Falls back to the live open-position
          // count, else null — so the risk map shows "5c" not "0c".
          contracts:   (a.strategyContracts && typeof a.strategyContracts[stratSlug] === "number")
                         ? a.strategyContracts[stratSlug]
                         : (stratOpenContracts > 0 ? stratOpenContracts : null),
          // riskUnit = real 1R ($); riskUnitReal = true when agent-emitted
          // (not the placeholder). The risk map divides pnl/dd by riskUnit
          // to plot in R. riskPerTrade kept for back-compat (now real).
          riskUnit,
          riskUnitReal,
          riskPerTrade: riskUnit,
          today:       resolvedToday,
          pnl7:        resolvedPnl7,
          pnl30:       resolvedPnl30,
          pnlAll:      resolvedPnlAll,
          wr, pf, eR, trades: tradeCount,
          dd:          stratDdAll,
          dd7:         stratDd7,
          dd30:        stratDd30,
          ddAll:       stratDdAll,
          sharpe:      stratSharpe,
          sortino:     stratSortino,
          lastSig:     stratLastSig,
          // Raw aggregates feed Overview's fleet-PF KPI so we don't
          // weighted-average an Infinity into NaN.
          grossWin:    stratGrossWin,
          grossLoss:   stratGrossLoss,
        });
      }
    }
    window.STRATS = out;

    // ── Cross-account aggregation: one row per strategy (defId) ──────────
    // "Pure strategy performance" — merge a strategy's trades across every
    // account it runs on (combine8 + combine9 + …).  Each trade is converted
    // to R with ITS OWN account's 1R (riskByKey), so R sums/curves are valid
    // across accounts; combined maxDD is on the merged, time-sorted stream.
    // $ fields sum the raw dollars and take the combined $ drawdown.  The
    // risk map swaps to these rows in "By strategy" mode.
    {
      const groups = {};
      for (const row of out) {
        const g = (groups[row.defId] = groups[row.defId] || { rows: [], ex: [] });
        g.rows.push(row);
        const ex = exitsBy[`${row.acct}:${row.defId}`] || [];
        // riskUnit can legitimately be 0 (no agent risk emit, no realized
        // loss yet, initial_capital 0) — those exits used to be DROPPED
        // from the aggregate entirely, zeroing a cross-account strategy's
        // $ P&L.  Always carry the trade; R conversion guards on ru.
        const ru = row.riskUnit || 0;
        for (const t of ex) g.ex.push({ t, ru });
      }
      const _inR = (x) => (x.ru > 0 ? (Number(x.t.pnl) || 0) / x.ru : 0);
      const _sumWin = (items, cut, inR) => items.reduce((s, x) =>
        _exitMs(x.t) >= cut ? s + (inR ? _inR(x) : (Number(x.t.pnl) || 0)) : s, 0);
      const _ddWin = (items, cut, inR) => {
        const inw = items.filter((x) => _exitMs(x.t) >= cut).sort((a, b) => _exitMs(a.t) - _exitMs(b.t));
        let cum = 0, peak = 0, mdd = 0;
        for (const x of inw) {
          cum += inR ? _inR(x) : (Number(x.t.pnl) || 0);
          if (cum > peak) peak = cum;
          if (peak - cum > mdd) mdd = peak - cum;
        }
        return mdd;
      };
      const byDef = [];
      for (const defId of Object.keys(groups)) {
        const g = groups[defId];
        const trades = g.rows.reduce((s, r) => s + (Number(r.trades) || 0), 0);
        const wins = g.ex.filter((x) => (Number(x.t.pnl) || 0) > 0).length;
        const pnlAllR = _sumWin(g.ex, 0, true);
        byDef.push({
          id: defId, defId, aggR: true,
          name: (g.rows[0] && g.rows[0].name) || defId,
          acct: `${g.rows.length} acct${g.rows.length === 1 ? "" : "s"}`,
          accounts: g.rows.map((r) => r.acct),
          // Which of the contributing accounts are retired — the aggregate
          // includes their in-window trades (see RETIRED-ACCOUNT INCLUSION)
          // so the UI can say "2 accts (1 retired)" instead of implying the
          // strategy still runs everywhere it ever lost money.
          retiredAccounts: g.rows.filter((r) => r.retired).map((r) => r.acct),
          state: g.rows.some((r) => (window.LIVE_STATES || new Set()).has(r.state)) ? "running" : (g.rows[0] && g.rows[0].state) || "stopped",
          trades,
          contracts: g.rows.reduce((s, r) => Math.max(s, Number(r.contracts) || 0), 0) || null,
          // Sum of contracts across accounts — the $/ct divisor for the merged
          // strategy (per-contract on an aggregate is approximate since accounts
          // trade different sizes; the UI labels it "approx").
          contractsSum: g.rows.reduce((s, r) => s + (Number(r.contracts) || 0), 0) || null,
          wr: trades ? wins / trades : null,
          // Raw win/loss dollars so consumers can compute a live profit
          // factor without weighted-averaging per-account Infinities.
          grossWin: g.rows.reduce((s, r) => s + (Number(r.grossWin) || 0), 0),
          grossLoss: g.rows.reduce((s, r) => s + (Number(r.grossLoss) || 0), 0),
          // $ windows (summed P&L; combined $ drawdown on the merged stream)
          pnl7: _sumWin(g.ex, _7dCutoff, false), pnl30: _sumWin(g.ex, _30dCutoff, false), pnlAll: _sumWin(g.ex, 0, false),
          dd7: _ddWin(g.ex, _7dCutoff, false), dd30: _ddWin(g.ex, _30dCutoff, false), ddAll: _ddWin(g.ex, 0, false),
          dd: _ddWin(g.ex, 0, false),
          // R windows (summed R; combined maxDD-R on the merged R stream)
          pnl7R: _sumWin(g.ex, _7dCutoff, true), pnl30R: _sumWin(g.ex, _30dCutoff, true), pnlAllR,
          dd7R: _ddWin(g.ex, _7dCutoff, true), dd30R: _ddWin(g.ex, _30dCutoff, true), ddAllR: _ddWin(g.ex, 0, true),
          eR: trades ? pnlAllR / trades : null,
          riskUnit: null,
        });
      }
      window.STRATS_BY_DEF = byDef;
    }
  }

  /* -- Seed STRATEGY_DEFS for known agent strategies --
   *
   * The Divergence row + Research-vs-Live plot need a `bench` object
   * per strategy to compute "vs research" deltas.  Until the agent
   * emits a strategy_def_inventory event with real research bench
   * numbers, we seed each known slug with its target metrics so the
   * sections aren't hidden.  Numbers come from the strategies' last
   * documented research validation (research/ in the aviaf repo).
   *
   * When the agent later sends real defs via WS, normalizeStrategyDef
   * upserts over these placeholders.
   */
  function seedKnownStrategyDefs() {
    // Idempotent — only seed slugs the catalog doesn't already have
    // (so a later WS strategy_def_inventory event with richer fields
    // can upsert without us clobbering it).  Each seed includes the
    // fields StrategyEditor reads: bench, keyParams (so the
    // "Parameters" tab has SOMETHING to edit), author + basis (about
    // tab + provenance card).
    const existing = new Set((window.STRATEGY_DEFS || []).map(d => d.id));
    // Bench numbers are pulled from the strategy's canonical research
    // config in ~/Documents/aviaf/configs/ (the headlines users marked
    // as "validated").  When the agent eventually emits
    // strategy_def_inventory, those upserts win over these seeds.
    //
    // Verified 2026-05-17.  Each bench carries enough to draw a research
    // projection without leaning on placeholder risk: eR (R-units),
    // risk_R ($ per trade — the 1R loss size at the deployed contracts),
    // tpd (trades per day).  ResearchVsLivePlot uses
    // expected_pnl = eR × risk_R × tpd × rangeDays — fixed target slope
    // per strategy, independent of how many trades the user has done.
    //
    //  - orb: nq_5m_orb_20260403.toml (4-session combined, slippage-adj).
    //         WR 65% weighted, avg_loss ≈ $977 (1c NQ), realized TPD 2.3,
    //         PF 1.27, E[R] 0.10.
    //  - mr:  mr_1m_ml_20260510.toml (5y walk-forward, threshold 0.82).
    //         WR 91.1%, $/trade +$1,115 holdout, TPD 5.16.  avg_loss
    //         estimated from worst-fold MDD ÷ ~30 losers in fold ≈ $700.
    //  - pair_divergence: pair_div_sprinter_v2_20260509.toml (FULL).
    //         WR 78.3%, PF 9.44, E[R] 1.835.  DD $2,688 over 5y; TPD
    //         derived from sprinter-v2 TOD gate (4 CT hours) ≈ 1.5.
    //  - ma_cross: trend_strategy_research_20260514.md (GC 1h sma_cross_htf
    //         10/30/200).  515 trades over 5y (252×5 = 1260 trading days)
    //         → TPD ≈ 0.41.  Trend-template typicals: WR 40%, avg_loss
    //         on MGC ≈ $200, eR 0.30.
    //  - orb_test: shadow-mode v6 ML challenger on prac.  No real risk
    //         (strategy_sub_budgets.orb_test = 0), no expected $ flow
    //         — tpd=0 so research line is flat at 0.
    const seeds = [
      { id: "orb",             name: "ORB · NQ 5m",
        family: "opening-range-breakout", symbol: "NQ", venue: "CME",
        author: "agent", basis: "configs/nq_5m_orb_20260403.toml",
        summary: "Opening-range breakout across Tokyo / London / NY sessions.  Broker-side STOP brackets; software SL/TP detection.",
        validated: "2026-04-03",
        keyParams: { opening_range_minutes: 30, breakout_buffer_ticks: 2, sl_atr_mult: 1.0, tp_atr_mult: 3.0 },
        bench: { wr: 0.65, pf: 1.27, eR: 0.10, tpd: 2.3, risk_R: 977, ppass: 0.962, pruin: 0.038 } },
      { id: "mr",              name: "Mean Reversion · NQ 1m + ML",
        family: "mean-reversion", symbol: "NQ", venue: "CME",
        author: "agent", basis: "configs/mr_1m_ml_20260510.toml",
        summary: "Bollinger-band fade on 1-min NQ; 5-seed LightGBM ensemble gates entries at threshold 0.82.",
        validated: "2026-05-10",
        keyParams: { sma_period: 300, band_sigma: 2.0, sl_sigma: 3.0, ml_threshold: 0.82, time_stop_bars: 240 },
        bench: { wr: 0.911, pf: 3.0, eR: 0.25, tpd: 5.16, risk_R: 700, ppass: 0.85, pruin: 0.08 } },
      { id: "orb_test",        name: "ORB Shadow · v6 ML",
        family: "opening-range-breakout", symbol: "NQ", venue: "CME",
        author: "agent", basis: "research/account_audit/orb_v4_v6_live_compare_2026_05_16.md",
        summary: "v6 ORB+ML challenger to the live v4 filter.  VirtualOMSProxy — no real broker orders, no real risk.  Champion/challenger trial running on prac since 2026-05-16.",
        validated: "2026-05-16",
        keyParams: { ml_threshold: 0.68, abstain_threshold: 0.358, opening_range_minutes: 30 },
        bench: { wr: 0.65, pf: 1.45, eR: 0.18, tpd: 0, risk_R: 0, ppass: 0.95, pruin: 0.04 } },
      { id: "pair_divergence", name: "Pair Divergence · Sprinter v2",
        family: "stat-arb", symbol: "NQ/MGC", venue: "CME",
        author: "agent", basis: "configs/pair_div_sprinter_v2_20260509.toml",
        summary: "Long-short pair divergence on rolling z-score; full Sprinter v2 with TOD gate (CT hours 17, 18, 0, 1).",
        validated: "2026-05-09",
        keyParams: { z_entry: 0.008, tod_hours_ct: "[17,18,0,1]", sl_z: 1.5, tp_z: 0.5 },
        bench: { wr: 0.783, pf: 9.44, eR: 1.835, tpd: 1.5, risk_R: 200, ppass: 0.78, pruin: 0.06 } },
      { id: "ma_cross",        name: "MA Cross · GC 1h SMA htf",
        family: "trend-following", symbol: "GC", venue: "COMEX",
        author: "agent", basis: "research/trend_strategy_research_20260514.md",
        summary: "GC 1h SMA-cross with 200-bar HTF trend filter (10/30/200).  Only template that cleared the LFA keeper gate across NQ/ES/GC/CL/RTY/ZN.",
        validated: "2026-05-14",
        keyParams: { ma_fast: 10, ma_slow: 30, htf_sma: 200, atr_stop_mult: 2.0 },
        bench: { wr: 0.40, pf: 1.83, eR: 0.30, tpd: 0.41, risk_R: 200, ppass: 0.55, pruin: 0.12 } },
    ];
    // klass groups recipes into runtime classes for the new
    // strategy-class page.  Live agent strategies are flat (one slug
    // == one class), so klass defaults to id; the seed below keeps
    // pair_divergence + ma_cross + mr + orb + orb_test as distinct
    // class IDs surfaced by STRATEGY_CLASSES.
    for (const s of seeds) if (!s.klass) s.klass = s.id;
    const merged = [...(window.STRATEGY_DEFS || [])];
    for (const s of seeds) if (!existing.has(s.id)) merged.push(s);
    // Ensure pre-existing defs also have klass (in case agent pushed
    // first then we re-seed).
    for (const d of merged) if (!d.klass) d.klass = d.id;
    window.STRATEGY_DEFS = merged;
  }
  // Run at module load so PageOverview / PageAccountDetail can resolve
  // STRATEGY_DEFS entries even before bootstrap completes.
  try { seedKnownStrategyDefs(); } catch (_) {}

  /* -- strategy-class catalog + small shared helpers --
   *
   * The new PageStrategies / PageStrategyClass pages group recipes by
   * `klass` and read STRATEGY_CLASSES for the class hero + risk-map
   * legend.  We define one class per live strategy (orb, mr, ma_cross,
   * pair_divergence, orb_test) so each gets its own class page; if
   * agents later send a strategy_def_inventory with a richer klass
   * field, normalizeStrategyDef wins via id-keyed upsert.
   */
  /* matchPrefixes lets the class swallow variants whose defId isn't in
   * STRATEGY_DEFS yet — e.g. orb_v5 / orb_rule / orb_unfiltered → orb,
   * mr_v2 / mr_v3 → mr, pair_div_sprinter / pair_div_v2 → pair_divergence.
   * Match rules (see inferStrategyClass below):
   *   - exact: id === prefix  (catches `orb`, `mr`, etc.)
   *   - boundary: id starts with prefix + ("_" | "-" | ".")  (catches `orb_v5`)
   * orb_test stays its own class because its id is matched first (longest-
   * id-first ordering) before `orb` gets a chance to swallow it. */
  window.STRATEGY_CLASSES = [
    { id: "orb",             label: "ORB",               desc: "opening-range breakout",            color: "oklch(0.62 0.13 250)",
      matchPrefixes: ["orb"] },
    { id: "mr",              label: "Mean reversion",    desc: "Bollinger fade · ML-gated",         color: "oklch(0.72 0.13 75)",
      matchPrefixes: ["mr", "meanreversion", "bollinger"] },
    { id: "ma_cross",        label: "MA cross · trend",  desc: "trend-following with HTF filter",   color: "oklch(0.58 0.13 155)",
      matchPrefixes: ["ma_cross", "macross", "ma", "trend", "sma_cross"] },
    { id: "pair_divergence", label: "Pair divergence",   desc: "stat-arb · rolling z-score",        color: "oklch(0.65 0.14 305)",
      matchPrefixes: ["pair_divergence", "pair_div", "pair", "stat_arb", "statarb"] },
    { id: "orb_test",        label: "ORB shadow",        desc: "v6 ML challenger · no real risk",   color: "oklch(0.60 0.05 250)",
      matchPrefixes: ["orb_test", "orb_shadow"] },
  ];

  /* Per-(account, defId) strategy-config seed.
   *
   * Background: the trading agent emits generic class slugs ("mr", "orb")
   * in fills/equity events even when the deployed module is a specific
   * variant — strategy/mr_strategy.py on aviaf-paper currently has
   * `_strategy_name = "mr"` hardcoded (no _v2/_v3 suffix logic deployed
   * yet).  The hub can't recover the variant from the WS surface, so
   * without this seed the map would label every MR bubble "mr" and
   * every ORB bubble "orb" — which is true about the *class* but lies
   * about which model variant is actually running.
   *
   * This map is the operator-curated truth, derived from
   * /opt/aviaf/config.paper.toml on aviaf-paper (2026-05-24 snapshot):
   *   combine4 strategies = ["mr", "pair_divergence"]
   *   combine4 mr_ml_model_dir = "models/ml/mr/v2"      → mr_v2
   *   prac strategies = ["orb", "orb_v5", "orb_rule",
   *                      "orb_unfiltered", "mr", "ma_cross",
   *                      "ma_cross_ng", "fomc"]
   *   prac uses global [strategy.mr] mr_ml_model_dir = "models/ml/mr/v3"
   *   prac uses global [strategy.orb] ml_model_dir   = "models/ml/orb/v4"
   *
   * Each entry carries a display `label` (no bare "mr"/"orb" leaks
   * through onto the map) plus the params an operator needs to read
   * without ssh'ing into the agent host — model_dir, threshold,
   * sub_budget, etc.  The strategy detail panel renders these under
   * Config tab.
   *
   * Per-installation additions go in localStorage[\"aviaf.strategy.labels\"]
   * as a JSON `{ \"<account>\": { \"<defId>\": \"<displayLabel>\" } }` so
   * label changes survive releases without code edits (config-level
   * fields can only be added via this seed).
   *
   * Display-only: does NOT change attribution buckets, exits-by keys,
   * or the agent-side slug — only what the operator sees.  The hover
   * title surfaces the raw defId for forensics.
   */
  const STRATEGY_CONFIG_SEED = {
    prac: {
      mr_v2: {
        label: "mr_v2",
        family: "mean-reversion",
        symbol: "NQ",
        ml_model_dir: "models/ml/mr/v2",
        ml_threshold: 0.70,
        notes: "prac mr_v2 era (before v3 deploy 2026-05-20)",
      },
      mr_v3: {
        label: "mr_v3",
        family: "mean-reversion",
        symbol: "NQ",
        ml_model_dir: "models/ml/mr/v3",
        ml_threshold: 0.60,
        ml_display_only: false,
        sub_budget: 3000,
        config_file: "config.paper.toml",
        config_section: "[strategy.mr] (global default; prac inherits)",
        notes: "LdP methodology refactor; 5-day soak before combine promotion",
        basis: "research/ml/mr_v3/train.py",
      },
      mr_v4: {
        label: "mr_v4",
        family: "mean-reversion",
        symbol: "NQ",
        ml_model_dir: "models/ml/mr/v4",
        sub_budget: 750,
        config_file: "config.paper.toml",
        config_section: "[strategy.mr] (prac mr_v4 rollout)",
        notes: "fresh strategy — deployed 2026-05-26",
      },
      orb: {
        // Bubble label reflects the *effective* behavior, not just the
        // attached model.  [strategy.orb] ml_display_only=true means
        // the v4 ML scores every breakout but never gates entries — so
        // every fill is functionally a naked ORB-rule trade.  Calling
        // it bare "orb_v4" implied gating that isn't happening.
        // Confirmed empirically: 9 of 10 prac.orb fills 5/17-5/23
        // logged ml_decision=display_only (one "accept" was just an
        // ML score that happened to clear thr=0.18, still un-gated).
        // Distinct from the prac.orb_unfiltered slug (= virtual fills
        // on VirtualOMSProxy, same rule).
        label: "orb_v4 (no filter)",
        family: "opening-range-breakout",
        symbol: "NQ",
        ml_model_dir: "models/ml/orb/v4",
        ml_threshold: 0.18,
        ml_display_only: true,
        sub_budget: 4500,
        config_file: "config.paper.toml",
        config_section: "[strategy.orb] ml_display_only=true (global)",
        notes: "v4 ML attached but display_only=true — entries are unfiltered (naked ORB rule). To start gating, flip [strategy.orb].ml_display_only to false. orb_unfiltered slug is the virtual-fills shadow of the same naked rule.",
      },
      orb_v4: {
        label: "orb_v4",
        family: "opening-range-breakout",
        symbol: "NQ",
        ml_model_dir: "models/ml/orb/v4",
        ml_threshold: 0.18,
        ml_display_only: true,
        sub_budget: 4500,
        config_file: "config.paper.toml",
        notes: "same as prac.orb but agent now tags fills as orb_v4",
      },
      orb_v5: {
        label: "orb_v5",
        family: "opening-range-breakout",
        symbol: "NQ",
        ml_model_dir: "models/ml/orb/v5_balanced",
        ml_threshold: 0.395,
        sub_budget: 0,
        notes: "shadow · VirtualOMSProxy · best $-wise model in 5/20 audit",
      },
      orb_v5m: {
        label: "orb_v5m",
        family: "opening-range-breakout",
        symbol: "NQ",
        sub_budget: 0,
        notes: "shadow · orb v5 minor variant",
      },
      orb_rule: {
        label: "orb_rule",
        family: "opening-range-breakout",
        symbol: "NQ",
        sub_budget: 0,
        notes: "shadow · depth-3 decision tree on the ORB rule",
      },
      orb_unfiltered: {
        label: "orb_unfiltered",
        family: "opening-range-breakout",
        symbol: "NQ",
        sub_budget: 0,
        notes: "shadow · ORB rule with NO ML filter — counterfactual baseline",
      },
      ma_cross: {
        label: "ma_cross",
        family: "trend",
        symbol: "MGC",
        sub_budget: 4500,
        notes: "GC 1h sma_cross_htf 10/30/200 (legacy slug — agent now uses ma_cross_gc)",
      },
      ma_cross_gc: {
        label: "ma_cross_gc",
        family: "trend",
        symbol: "MGC",
        sub_budget: 4500,
        notes: "GC 1h sma_cross_htf 10/30/200",
      },
      ma_cross_ng: {
        label: "ma_cross_ng",
        family: "trend",
        symbol: "MNG",
        sub_budget: 4500,
        notes: "Tier-7 NG leg, added 2026-05-20",
      },
      fomc: {
        label: "fomc",
        family: "event",
        sub_budget: 3000,
      },
    },
  };

  /* Variant resolution for a *bare* class slug ("mr", "orb").  When a
   * fill arrives tagged "mr" or "orb" with no per-(account,slug) entry
   * in STRATEGY_CONFIG_SEED, we'd otherwise show a bare class slug —
   * explicitly contrary to the operator goal that no class-only slugs
   * reach the map.  Defaults track the agent's GLOBAL config blocks:
   *   [strategy.mr]  mr_ml_model_dir defaults to v3 (combine4 pins v2)
   *   [strategy.orb] ml_display_only=true   → naked rule entries
   * If a never-seen account starts gating ORB with v4, add an explicit
   * STRATEGY_CONFIG_SEED entry — don't rely on the default.
   */
  const DEFAULT_VARIANT = {
    mr: "mr_v?",                // unknown variant — operator should add entry
    orb: "orb_v4 (no filter)",  // global default is display_only=true
  };

  /* Per-account fill-slug remap — when historical fills were tagged with
   * one slug ("orb") that the operator considers the same physical
   * deployment as another rostered strategy ("mr" → displayed as
   * "mr_v2"), fold those fills into the canonical bucket so the bubble
   * carries real P&L instead of stranding the data on a now-defunct
   * strategy row.  Distinct from the class-match pass below (which only
   * fires when the source slug isn't in the roster AND has a unique
   * class sibling) — FILL_REMAP is an unconditional operator override.
   *
   * Display-only consequence: the orb strat row no longer appears for
   * combine4 (its fills now live under the mr bucket); the bubble at
   * (+pnl, -dd) is labelled "mr_v2" via STRATEGY_CONFIG_SEED.  Extend
   * here for other renames.
   */
  const FILL_REMAP = {
    prac: { mr: "mr_v3" },
  };

  /* Per-account multi-era model rollout map.  Solves the problem
   * FILL_REMAP can't: when the agent ships the same bare slug ("mr")
   * across multiple model versions (v1 → v2 → v3 → v4), FILL_REMAP
   * folds every era into the latest canonical bucket — inflating its
   * P&L by the entire model lineage and starving any other era of
   * fills.  Operator confirmed 2026-05-26: prac:mr_v3 was showing
   * mr_v2 + mr_v3 + pre-v2 perf combined; prac:mr_v4 was empty even
   * though today's fills were live (and losing money).
   *
   * Format per (acct, slug):
   *   [{ since: ISO_date, into: bucket }, …, { into: catch_all }]
   * Processed newest-first.  A fill lands in the first era whose
   * `since` it satisfies; the last entry needs no `since`.
   *
   * Override per (acct, slug) via localStorage:
   *   localStorage.setItem("aviaf.strategy.eras", JSON.stringify({
   *     "prac": { "mr_v3": [...] }
   *   }))
   *
   * Dates below are the operator-known rollout dates:
   *   prac:  mr_v3 went live 2026-05-18 (see prac.mr_pre_v3 SEED note);
   *          mr_v4 went live 2026-05-26 (see prac.mr_v4 SEED note).
   *          The agent ships every mr fill bare → FILL_REMAP folds to
   *          mr_v3 → eras then re-partition by actual model era.
   *   combine4: mr_v4 went live 2026-05-26 (see combine4.mr_v4 SEED
   *             note); everything before is mr_v2 era.
   *
   * The era-split also runs on the raw bare slug ("mr") in case any
   * fills bypass FILL_REMAP — same eras apply.
   */
  const PRAC_MR_ERAS = [
    { since: "2026-05-26T00:00:00Z", into: "mr_v4" },
    { since: "2026-05-18T00:00:00Z", into: "mr_v3" },
    { into: "mr_pre_v3" },
  ];
  const STRATEGY_ERAS = {
    prac: {
      mr:    PRAC_MR_ERAS,
      mr_v3: PRAC_MR_ERAS,
      mr_v4: PRAC_MR_ERAS,
    },
  };
  let _STRATEGY_ERAS_USER = {};
  try {
    const raw = localStorage.getItem("aviaf.strategy.eras");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") _STRATEGY_ERAS_USER = parsed;
    }
  } catch (_) {}
  window.strategyErasFor = function strategyErasFor(acct, sId) {
    if (!acct || !sId) return null;
    return (_STRATEGY_ERAS_USER[acct] || {})[sId]
        || (STRATEGY_ERAS[acct] || {})[sId]
        || null;
  };

  /* Operator-declared primary strategy per account.  When the agent emits
   * a fill with strategy=null on a multi-strategy account, the prior
   * fallback used a.strategies[0] — i.e. whichever variant the agent
   * happened to list first in HELLO.  That's an accident of agent config
   * order, not operator intent, and it silently misattributes the
   * majority of fills when the agent isn't tagging them.
   *
   * combine4 example (2026-05-25): agent reports
   * strategies=["pair_divergence","mr"] so every untagged fill was being
   * attributed to pair_divergence — operator's broker P&L showed ~$8k on
   * mr_v2 for the week while the dashboard showed $2k (the rest went to
   * the pair_divergence bubble).  Declaring mr as combine4's primary
   * routes those untagged fills to the correct bucket.
   *
   * Long-term fix is the agent tagging every fill with strategy=…; this
   * map is the operator's authoritative override until that lands. */
  const ACCOUNT_PRIMARY_STRATEGY = {
  };
  let _PRIMARY_STRATEGY_USER = {};
  try {
    const raw = localStorage.getItem("aviaf.account.primary");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") _PRIMARY_STRATEGY_USER = parsed;
    }
  } catch (_) {}
  window.accountPrimaryStrategy = function accountPrimaryStrategy(acctId) {
    if (!acctId) return null;
    return _PRIMARY_STRATEGY_USER[acctId]
        || ACCOUNT_PRIMARY_STRATEGY[acctId]
        || null;
  };
  let _FILL_REMAP_USER = {};
  try {
    const raw = localStorage.getItem("aviaf.fill.remap");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") _FILL_REMAP_USER = parsed;
    }
  } catch (_) {}
  window.fillSlugRemap = function fillSlugRemap(accountId, slug) {
    if (!accountId || !slug) return slug;
    const u = _FILL_REMAP_USER[accountId] || {};
    const s = FILL_REMAP[accountId] || {};
    return u[slug] || s[slug] || slug;
  };
  let _STRATEGY_LABEL_USER = {};
  try {
    const raw = localStorage.getItem("aviaf.strategy.labels");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") _STRATEGY_LABEL_USER = parsed;
    }
  } catch (_) { /* ignore quota/parse */ }
  /* Resolve the display label for a (account, defId) bubble.
   * Priority: user localStorage override → per-account seed →
   * DEFAULT_VARIANT (when defId is a bare class slug like "mr"/"orb") →
   * raw defId.  The fallback chain guarantees no bare "mr"/"orb"
   * reaches the map: an unmapped bare slug becomes "mr_v?" / "orb_v4".
   */
  window.strategyLabelFor = function strategyLabelFor(accountId, defId) {
    if (!defId) return defId || "";
    if (defId === "_unattributed") return "untagged fills";
    // The agent stamps a broker half-turn "manual" when NO attribution source
    // resolved it (OMS order map / per-strategy CSV / unique-symbol owner).
    // On 2026-07-26 that bucket held 105 trades / −$3.3k of 30d P&L on
    // dev-prac1+2 that were really agent trades (cl6e_spread's two legs and
    // bar-cadence orb_rule/ma_cross exits) — calling it "manual" implied
    // hand-placed trades and hid the attribution gap.  Label it for what it
    // is; the agent-side fix shrinks it to genuinely un-attributable fills.
    if (defId === "manual") return "unattributed / manual";
    if (defId.endsWith("_legacy")) {
      const base = defId.slice(0, -"_legacy".length);
      const baseLabel = window.strategyLabelFor(accountId, base);
      // Strip the variant suffix from baseLabel ("mr_v3" → "mr") so
      // the legacy chip reads "mr · pre-v3" rather than "mr_v3 (pre-rollout)"
      // which contradicts itself.
      const stem = baseLabel.replace(/[ _]?v?\d.*$/i, "").replace(/\s*\(.*\)\s*/g, "").trim() || base;
      return `${stem} · pre-rollout`;
    }
    const userMap = _STRATEGY_LABEL_USER[accountId] || {};
    if (userMap[defId]) return userMap[defId];
    const seedEntry = (STRATEGY_CONFIG_SEED[accountId] || {})[defId];
    if (seedEntry && seedEntry.label) return seedEntry.label;
    if (DEFAULT_VARIANT[defId]) return DEFAULT_VARIANT[defId];
    return defId;
  };

  /* Resolve the full per-(account, defId) config object: ml_model_dir,
   * threshold, sub_budget, notes, etc.  Used by the strategy detail
   * panel's Config tab so the operator can read the live params
   * without ssh'ing into aviaf-paper.  Returns null when no seed
   * entry exists (panel falls back to the legacy "no config snapshot"
   * empty state).
   */
  window.strategyConfigFor = function strategyConfigFor(accountId, defId) {
    if (!accountId || !defId) return null;
    return (STRATEGY_CONFIG_SEED[accountId] || {})[defId] || null;
  };

  /* Stable hue from a string — used as a fallback class color when a
   * deployed strategy slug doesn't match any registered class.  Same hash
   * → same color across reloads so operators learn the visual identity. */
  window.derivedClassColor = function derivedClassColor(seed) {
    const s = String(seed || "");
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    const hue = ((h % 360) + 360) % 360;
    return `oklch(0.62 0.10 ${hue})`;
  };

  /* The single source of truth for "what class does this defId belong to?".
   * Resolution order:
   *   1. STRATEGY_DEFS entry's explicit `klass` (agent-pushed inventory wins)
   *   2. Exact STRATEGY_CLASSES.id match
   *   3. matchPrefixes scan, most-specific (longest id) first
   *   4. "other"  (caught by classMeta and rendered with a derived color) */
  window.inferStrategyClass = function inferStrategyClass(defId) {
    if (!defId) return "other";
    const def = (window.STRATEGY_DEFS || []).find((d) => d.id === defId);
    if (def && def.klass) return def.klass;
    const classes = [...(window.STRATEGY_CLASSES || [])]
      .sort((a, b) => (b.id?.length || 0) - (a.id?.length || 0));
    for (const c of classes) {
      if (c.id === defId) return c.id;
      const prefixes = [c.id, ...(c.matchPrefixes || [])];
      for (const p of prefixes) {
        if (defId === p) return c.id;
        if (defId.startsWith(p + "_")
            || defId.startsWith(p + "-")
            || defId.startsWith(p + ".")) return c.id;
      }
    }
    return "other";
  };

  /* account-type metadata + survival thresholds (for at-tab breakdown) */
  window.ACCT_TYPES = [
    { id: "combine",  label: "Combine",  desc: "prop-firm evaluation", survival: 4500, color: "var(--accent)" },
    { id: "xfa",      label: "XFA",      desc: "express funded acct",  survival: 2500, color: "var(--warn)"   },
    { id: "practice", label: "Practice", desc: "sim / paper trading",  survival: null, color: "var(--muted)"  },
    { id: "paper",    label: "Paper",    desc: "sim / paper trading",  survival: null, color: "var(--muted)"  },
    { id: "funded",   label: "Funded",   desc: "real capital",         survival: null, color: "var(--pos)"    },
    { id: "live",     label: "Live",     desc: "real capital",         survival: null, color: "var(--pos)"    },
  ];

  window.LIVE_STATES = new Set(["running", "armed", "paused"]);

  window.classOfStrat = function (s) {
    return window.inferStrategyClass(s && s.defId);
  };
  window.classOfDef = function (id) {
    return window.inferStrategyClass(id);
  };
  window.classMeta = function (k) {
    const hit = (window.STRATEGY_CLASSES || []).find((c) => c.id === k);
    if (hit) return hit;
    if (k === "other") {
      return { id: "other", label: "Other", desc: "uncategorized recipes",
               color: "var(--muted-2)", matchPrefixes: [] };
    }
    return { id: k, label: k, desc: "",
             color: window.derivedClassColor(k), matchPrefixes: [] };
  };
  window.getLiveStrats = function () {
    return (window.STRATS || []).filter((s) => window.LIVE_STATES.has(s.state));
  };
  /* Risk × return map needs every deployed strategy, not just live ones —
   * variants the agent has attached but not activated yet should still
   * appear on the canvas (faded, "stopped" state). */
  window.getAllDeployedStrats = function () {
    return (window.STRATS || []).slice();
  };
  window.acctTypeMeta = function (k) {
    return (window.ACCT_TYPES || []).find((t) => t.id === k) ||
      { id: k, label: k, desc: "", survival: null, color: "var(--muted)" };
  };
  window.acctTypeOfStrat = function (s) {
    const a = (window.ACCOUNTS || []).find((x) => x.id === s.acct);
    return a ? (a.kind || "other") : "other";
  };

  /* nice-number tick generator (used by plots) */
  window.niceTicks = function (min, max, target = 5) {
    if (!isFinite(min) || !isFinite(max) || min === max) return [min];
    const range = max - min;
    const rough = range / Math.max(1, target);
    const mag = Math.pow(10, Math.floor(Math.log10(rough)));
    const norm = rough / mag;
    const step = (norm < 1.5 ? 1 : norm < 3.5 ? 2 : norm < 7.5 ? 5 : 10) * mag;
    const start = Math.ceil(min / step) * step;
    const out = [];
    for (let v = start; v <= max + 1e-9; v += step) out.push(+v.toFixed(10));
    return out;
  };

  /* deterministic placeholder equity walker (used by the new strategy
   * card sparklines + research-vs-live preview when no real curve is
   * available yet).  Same shape as the mock data.jsx genEquity so the
   * new components from the design drop render. */
  window.genEquity = function genEquity(seed = 1, len = 90) {
    const out = [];
    let v = 0;
    let r = seed;
    for (let i = 0; i < len; i++) {
      r = (r * 9301 + 49297) % 233280;
      const n = (r / 233280 - 0.5) * 2;
      v += 180 + n * 520 + (i > 60 ? 60 : 0);
      out.push(v);
    }
    return out;
  };

  /* deterministic-seeded equity walker (used for class-curve previews) */
  function genCurveDet(seed, n) {
    let r = (seed * 9301 + 49297) % 233280;
    const out = [];
    let v = 0;
    for (let i = 0; i < n; i++) {
      r = (r * 9301 + 49297) % 233280;
      v += (r / 233280 - 0.5) * 2;
      out.push(v);
    }
    return out;
  }

  /* research-vs-live curve pair for a deployment.  Used only by the new
   * class-strip preview; gracefully degrades when fields are missing.
   */
  window.genCurvePair = function (strat, len = 60) {
    const def = (window.STRATEGY_DEFS || []).find((d) => d.id === strat.defId);
    const seed = (strat.name || "").length * 7 + (strat.acct || "").charCodeAt(0) * 3 + 13;
    const liveRaw = genCurveDet(seed, len);
    const last = liveRaw.at(-1) || 1;
    const target = strat.pnl30 || 0;
    const live = liveRaw.map((v) => last !== 0 ? v / last * target : 0);
    const approxTrades = Math.max(strat.trades || 0, 20);
    const eR = def?.bench?.eR ?? 0;
    const risk = strat.riskPerTrade || def?.bench?.risk_R || 200;
    const expectedEnd = eR * risk * approxTrades * 1.0;
    const research = [];
    const phase = (strat.name || "x").length * 0.7;
    for (let i = 0; i < len; i++) {
      const t = i / Math.max(1, len - 1);
      const easing = 0.92 + 0.08 * t;
      const wave = Math.sin(i * 0.35 + phase) * Math.abs(expectedEnd) * 0.025;
      research.push(expectedEnd * t * easing + wave);
    }
    return { research, live };
  };

  window.aggregateCurves = function (strats, len = 60) {
    const out = { research: new Array(len).fill(0), live: new Array(len).fill(0) };
    strats.forEach((s) => {
      const p = window.genCurvePair(s, len);
      for (let i = 0; i < len; i++) {
        out.research[i] += p.research[i] || 0;
        out.live[i] += p.live[i] || 0;
      }
    });
    return out;
  };

  /* -- storage rollup: sum across collectors --
   *
   * The Overview Health panel + PageCollectors header both read
   * window.STORAGE_ROLLUP for a fleet-wide "tick recorder X GB / Y GB"
   * line.  Aggregate the per-collector storage{used,capacity} blobs.
   */
  function rebuildStorageRollup() {
    const cols = window.COLLECTORS || [];
    let used = 0, cap = 0, ticksToday = 0, tps = 0;
    for (const c of cols) {
      used += Number(c.storage?.used ?? 0);
      cap  += Number(c.storage?.capacity ?? 0);
      ticksToday += Number(c.ticksToday ?? 0);
      tps += Number(c.ticksPerSec ?? 0);
    }
    window.STORAGE_ROLLUP = { used, cap, ticksToday, tps };
  }

  /* -- Health-panel item builder --
   *
   * Returns the same array shape both desktop HealthPanel (pages.jsx)
   * and mobile MHealth (mobile.jsx) render.  Derives every row from
   * live window state — INSTANCES, ACCOUNTS, COLLECTORS, STORAGE_ROLLUP,
   * ALERTS — and the current wall clock.  Caller is responsible for
   * triggering a re-render on a 1s tick so time-since values stay
   * current between WS frames.
   *
   * Single source of truth: any new health signal added here lights up
   * both surfaces at once, with no duplicated derivation logic to drift
   * apart.
   */
  function computeHealthItems() {
    const insts = window.INSTANCES || [];
    const accts = window.ACCOUNTS || [];
    const cols  = window.COLLECTORS || [];
    const storage = window.STORAGE_ROLLUP || { used: 0, cap: 0 };
    const alerts = window.ALERTS || [];
    const nowMs = Date.now();
    const nowUs = nowMs * 1000;

    const liveInsts = insts.filter(i => i.state === "running");
    const worstOffset = insts.reduce((m, i) => Math.max(m, i.lagMs || 0), 0);
    // Severity: no agents at all OR all-registered-agents offline → "neg"
    // (red, pulsing) because the broker leg is wholly dark — trades that
    // closed via the agent won't reach the hub, the day-P&L tile is stale,
    // the risk guard can't enforce mid-trade flatten.  Partial outage
    // (some agents up, some down) stays "warn".  Anything else "ok".
    let broker;
    if (insts.length === 0) {
      broker = { v: "neg", x: "no agents connected" };
    } else if (liveInsts.length === 0) {
      broker = { v: "neg",
                 x: `0/${insts.length} agent${insts.length === 1 ? "" : "s"} · OFFLINE` };
    } else {
      broker = { v: liveInsts.length === insts.length ? "ok" : "warn",
                 x: `${liveInsts.length}/${insts.length} agent${insts.length === 1 ? "" : "s"}` +
                    (worstOffset > 0 ? ` · ${worstOffset}ms skew` : "") };
    }

    const isStale = (c) => !c._ts_us || (nowUs - c._ts_us) > 30_000_000;
    const liveCols = cols.filter(c => !isStale(c) && (c.state === "recording" || c.state === "running")).length;
    const staleCols = cols.filter(isStale).length;
    const gapsToday = cols.reduce((s, c) => s + (c.gapsToday || c.quality?.gapsToday || 0), 0);
    const market = cols.length === 0
      ? { v: "warn", x: "no collectors reporting" }
      : { v: (staleCols > 0 || gapsToday > 0) ? "warn" : "ok",
          x: `${liveCols}/${cols.length} stream${cols.length === 1 ? "" : "s"} · ${gapsToday} gap${gapsToday === 1 ? "" : "s"}` +
             (staleCols > 0 ? ` · ${staleCols} stale` : "") };

    const acctsWithFloor = accts.filter(a => (a.mllFloor || 0) > 0).length;
    const acctsAtRisk = accts.filter(a => {
      const fl = a.mllFloor || 0;
      return fl > 0 && (a.equity || 0) - fl < 500;
    }).length;
    const risk = acctsWithFloor === 0
      ? { v: "warn", x: "no MLL floors reported" }
      : { v: acctsAtRisk > 0 ? "warn" : "ok",
          x: `${acctsWithFloor}/${accts.length} floor${accts.length === 1 ? "" : "s"}` +
             (acctsAtRisk > 0 ? ` · ${acctsAtRisk} near floor` : "") };

    const usedGB = (storage.used || 0).toFixed(1);
    const capGB = storage.cap || 0;
    const tickRec = capGB === 0
      ? { v: "warn", x: "no storage reported" }
      : { v: (storage.used / capGB) > 0.9 ? "warn" : "ok",
          x: `${usedGB} GB / ${capGB} GB` };

    const latestSeen = insts.reduce((m, i) => Math.max(m, i.lastSeenMs || 0), 0);
    const reconcileAgeS = latestSeen ? Math.round((nowMs - latestSeen) / 1000) : null;
    const reconcile = reconcileAgeS == null
      ? { v: "warn", x: "no heartbeat yet" }
      : { v: reconcileAgeS > 300 ? "neg" : (reconcileAgeS > 60 ? "warn" : "ok"),
          x: `agent heartbeat ${reconcileAgeS}s ago` };

    const critOpen = alerts.filter(a =>
      String(a.severity || a.sev || "").toLowerCase() === "crit"
      && !(a.acknowledged || a.ack)
    );
    const critStale = critOpen.filter(a => {
      const ageS = a.ts_us ? (nowMs - a.ts_us / 1000) / 1000 : 0;
      return ageS > 60;
    }).length;
    const notifPerm = (typeof Notification !== "undefined")
      ? Notification.permission
      : "default";
    let notifier;
    if (critStale > 0) {
      notifier = { v: "neg",
                   x: `${critStale} crit unacked >60s · perm:${notifPerm}` };
    } else if (critOpen.length > 0) {
      notifier = { v: "warn",
                   x: `${critOpen.length} crit open · perm:${notifPerm}` };
    } else if (notifPerm === "denied") {
      notifier = { v: "warn", x: "browser notify denied" };
    } else if (notifPerm !== "granted") {
      notifier = { v: "warn", x: "browser notify not enabled" };
    } else {
      notifier = { v: "ok", x: "browser notify · 0 open crit" };
    }

    return [
      { k: "Broker · TopstepX", ...broker },
      { k: "Market data",       ...market },
      { k: "Risk guard",        ...risk },
      { k: "Tick recorder",     ...tickRec },
      { k: "Reconcile loop",    ...reconcile },
      { k: "Crit notifier",     ...notifier },
    ];
  }
  window.computeHealthItems = computeHealthItems;

  /* -- bootstrap (one REST sweep on connect/reconnect) -- */

  // Per-bootstrap response cache.  When bootstrap() opens its first
  // request, it calls _prefetchBootstrap() below; that single round-trip
  // to /api/bootstrap returns everything the dashboard's first paint
  // needs (accounts, instances, positions, alerts, fills, events, +
  // per-account equity latest/hourly/30d-ago).  Subsequent getJson()
  // calls for those URLs are served from this Map instead of firing
  // their own HTTP requests — collapsing ~3N+5 round-trips to 1 on
  // cold load.  Each entry's TTL is short (the response itself is
  // server-cached for 30s); after the bootstrap window we clear the
  // map so live updates flow through the normal WS bridge.
  const _bootCache = new Map();
  function _bootCacheSet(key, value) {
    _bootCache.set(key, value);
  }
  function _bootCacheGet(key) {
    return _bootCache.get(key);
  }
  function _bootCacheClear() { _bootCache.clear(); }
  window.__bootCacheClear = _bootCacheClear;

  async function getJson(path) {
    // Fast path: bootstrap pre-fetched this URL.
    const cached = _bootCacheGet(path);
    if (cached !== undefined) return cached;
    const r = await fetch(path, { credentials: "same-origin" });
    if (r.status === 401) { window.__LIVE.authed = false; throw new Error("401"); }
    if (!r.ok) throw new Error(`${path} -> ${r.status}`);
    return r.json();
  }

  // Fetch /api/bootstrap and pre-populate _bootCache with every
  // per-endpoint URL the bootstrap() function will subsequently ask for.
  // If the endpoint is unavailable (older hub, transient 5xx), this
  // returns false and bootstrap() falls back to the per-endpoint path —
  // no behavior change vs. before this commit.
  async function _prefetchBootstrap() {
    let boot;
    try {
      const r = await fetch("/api/bootstrap", { credentials: "same-origin" });
      if (!r.ok) return false;
      boot = await r.json();
    } catch (_) {
      return false;
    }
    // Top-level endpoints map 1:1.
    _bootCacheSet("/api/instances", boot.instances || []);
    _bootCacheSet("/api/accounts", boot.accounts || []);
    _bootCacheSet("/api/positions", boot.positions || []);
    _bootCacheSet("/api/alerts?limit=200", boot.alerts || []);
    _bootCacheSet("/api/fills?limit=5000", boot.fills || []);
    _bootCacheSet("/api/events?kind=health&limit=300", boot.events_health || []);
    _bootCacheSet("/api/events?kind=goal_state&limit=200", boot.events_goal_state || []);
    _bootCacheSet("/api/events?kind=pass_estimate&limit=100", boot.events_pass_estimate || []);
    // Per-account equity URLs — same query-string shape bootstrap()
    // builds at the call site.  These must match exactly (encodeURIComponent
    // on instance + label).
    //
    // CRITICAL: round the 30d-ago timestamp to a 60-second boundary.
    // bootstrap() step 5 computes its OWN TS_30D_AGO_US ms after this
    // call; without rounding, the two integers differ and the
    // /api/equity?until_us=... cache key here misses step 5's lookup.
    // window.__BOOT_TS_30D_AGO_US is what step 5 reads.
    const TS_30D_AGO_US =
      Math.floor((Date.now() - 30 * 24 * 3600 * 1000) / 60_000) * 60_000_000;
    window.__BOOT_TS_30D_AGO_US = TS_30D_AGO_US;
    for (const [acctKey, ebs] of Object.entries(boot.equity_by_account || {})) {
      const [inst, lbl] = acctKey.split("/", 2);
      const ei = encodeURIComponent(inst);
      const el = encodeURIComponent(lbl);
      _bootCacheSet(
        `/api/equity?instance_id=${ei}&account_label=${el}&limit=2`,
        ebs.latest || [],
      );
      _bootCacheSet(
        `/api/equity?instance_id=${ei}&account_label=${el}` +
          `&bucket_seconds=3600&limit=9000`,
        ebs.series_hourly || [],
      );
      _bootCacheSet(
        `/api/equity?instance_id=${ei}&account_label=${el}` +
          `&until_us=${TS_30D_AGO_US}&limit=1`,
        ebs.eq30 || [],
      );
    }
    return true;
  }

  // Throttle/coalesce bootstrap() so a WS-reconnect storm can't pile up
  // identical bootstrap runs and hammer the hub.  Two guards:
  //   1. _bootstrapInflight: if a bootstrap is already running, return
  //      the same Promise — concurrent callers all await one run.
  //   2. _bootstrapMinIntervalMs: if a bootstrap completed within the
  //      last 5 s, return the prior result instead of re-running.  WS
  //      reconnects within 5 s are noise, not new state.
  // Without this, every flaky network hop triggered ~30 cascaded
  // /api/* round-trips per reconnect; the console showed 200+ repeated
  // "bootstrap step 2 done" lines from a single user session.
  let _bootstrapInflight = null;
  let _bootstrapLastOkAt = 0;
  const _bootstrapMinIntervalMs = 5000;

  async function bootstrap(opts) {
    const force = !!(opts && opts.force);
    if (_bootstrapInflight) return _bootstrapInflight;
    const sinceLast = Date.now() - _bootstrapLastOkAt;
    if (!force && _bootstrapLastOkAt > 0 && sinceLast < _bootstrapMinIntervalMs) {
      // Recent successful bootstrap — skip this run.  The WS bridge
      // continues to deliver live updates; nothing is missed.  A manual
      // refresh (pull-to-refresh / FAB / desktop refresh button) passes
      // {force:true} to bypass this throttle.
      return;
    }
    _bootstrapInflight = _runBootstrap().finally(() => {
      _bootstrapInflight = null;
    });
    return _bootstrapInflight;
  }

  // Manual full re-fetch, exposed for the pull-to-refresh handler, the
  // mobile FAB, and the desktop refresh control.  Forces past the 5 s
  // coalesce window but still rides the in-flight guard, so a double-tap
  // awaits one run rather than firing two.  Returns the bootstrap promise.
  window.refreshBootstrap = function () { return bootstrap({ force: true }); };

  async function _runBootstrap() {
    window.__LIVE.bootstrapping = true;
    bump();
    try {
      // Auth gate.  When /api/auth/me 401s on a re-bootstrap (cookie
      // expired mid-session, e.g. after a WS-triggered refresh), we
      // used to wipe ACCOUNTS/INSTANCES — UI flips to "0 accounts"
      // until the next bootstrap.  Instead: keep the stale state
      // visible, mark __LIVE.authed=false, and dispatch a custom
      // event the App component listens for so it can set
      // session=false and render <LoginScreen />.  Initial-load auth
      // is gated by App's own /api/auth/me check, so bootstrap only
      // sees 401 on session refresh — never on cold start.
      // Fire auth check, health, and bootstrap in parallel so
      // the ~200ms auth round-trip overlaps with the heavier
      // bootstrap fan-out instead of serializing ahead of it.
      const [me, health, _bootOK] = await Promise.all([
        fetch("/api/auth/me", { credentials: "same-origin" }),
        fetch("/api/health").then(r => r.json()).catch(() => null),
        _prefetchBootstrap(),
      ]);
      if (me.status === 401 || me.status === 403) {
        window.__LIVE.authed = false;
        console.warn("[aviaf] /api/auth/me", me.status, "— auth lost, surfacing to App");
        try { window.dispatchEvent(new CustomEvent("aviaf:auth-lost")); } catch (_) {}
        return;
      }
      if (!me.ok) {
        console.warn("[aviaf] /api/auth/me", me.status, "— transient, keeping state");
        return;
      }
      window.__LIVE.authed = true;
      if (health) window.__LIVE.instancesLive = health.instances_connected;
      if (_bootOK) {
        console.log("[aviaf] /api/bootstrap pre-fetched — fast path active");
      }

      // 1. Instances + last 60 health events for cpu/mem/heartbeat
      const [instances, healthEvents, accounts] = await Promise.all([
        getJson("/api/instances"),
        getJson("/api/events?kind=health&limit=300"),
        getJson("/api/accounts"),
      ]);

      const latestHealthByInst = {};
      const hbByInst = {};
      const logsByInst = {};
      for (const e of healthEvents) {
        if (!latestHealthByInst[e.instance_id]) latestHealthByInst[e.instance_id] = e;
        (hbByInst[e.instance_id] = hbByInst[e.instance_id] || [])
          .push(e.payload?.host?.cpu_pct ?? 0);
        // Synthesise log lines from health events.  Real agent log
        // shipping doesn't exist yet (would need protocol change), but
        // health events have a timestamp, instance id, and structured
        // host metrics — enough to render a terminal-style ticker
        // so the "Recent log · last 12h" panel isn't permanently
        // empty.  Add alerts + fills below for higher signal.
        const tIso = new Date((e.ts_us || 0) / 1000).toISOString();
        const p = e.payload || {};
        const cpu = p.host?.cpu_pct;
        const mem = p.host?.mem_used_bytes && p.host?.mem_total_bytes
          ? Math.round(p.host.mem_used_bytes / p.host.mem_total_bytes * 100)
          : null;
        const load = p.host?.load_1m;
        const txt = "health · cpu " +
          (cpu != null ? cpu.toFixed(1) : "?") + "% · mem " +
          (mem != null ? mem + "%" : "?") + " · load " +
          (load != null ? load.toFixed(2) : "?");
        const arr = (logsByInst[e.instance_id] = logsByInst[e.instance_id] || []);
        arr.push({
          t:   tIso.slice(11, 19),  // HH:MM:SS
          ts:  e.ts_us,
          lvl: cpu != null && cpu > 90 ? "WARN" : "INFO",
          src: "health",
          txt,
        });
      }
      // Fold alerts + fills in as log lines too.  Sorted newest-first,
      // capped at 500 per instance to bound DOM size.
      for (const a of (window.ALERTS || [])) {
        if (!a.instance_id) continue;
        const arr = (logsByInst[a.instance_id] = logsByInst[a.instance_id] || []);
        const tIso = new Date((a.ts_us || 0) / 1000).toISOString();
        arr.push({
          t:   tIso.slice(11, 19),
          ts:  a.ts_us,
          lvl: (a.severity || "warn").toUpperCase(),
          src: "alert",
          txt: (a.code ? "[" + a.code + "] " : "") + (a.message || a.code || "(unknown)"),
        });
      }
      for (const inst of Object.keys(logsByInst)) {
        logsByInst[inst].sort((x, y) => (y.ts || 0) - (x.ts || 0));
        if (logsByInst[inst].length > 500) logsByInst[inst].length = 500;
      }
      window.INSTANCE_LOGS = logsByInst;
      const buildHb = id => {
        const xs = (hbByInst[id] || []).slice(0, 60).reverse().map(v => Math.max(0.05, v/100));
        while (xs.length < 60) xs.unshift(0.05);
        return xs;
      };
      window.INSTANCES = (instances || []).map(row =>
        instanceFromRow(row, latestHealthByInst[row.instance_id], buildHb(row.instance_id))
      );
      // HELLO replaces the agent's roster: accounts the agent no longer
      // reports (e.g. combine1/xfa removed from config.paper.toml) come
      // back with active=0. Hide them so the dashboard mirrors the toml
      // without forensic rows from prior sessions.
      //
      // Preserve numeric fields (equity / dayPnl / totalPnl / mllFloor)
      // from the previous ACCOUNTS array (cache hydration or prior
      // bootstrap) so step 1 doesn't briefly replace real values with
      // accountFromRow's anchor default before step 2 fills them in.
      // /api/equity is the authoritative source for those numbers;
      // step 2 will overwrite them.  But if step 2 returns no rows
      // (transient empty), we keep the cached value rather than drop
      // to $150K anchor.
      const prevAcctMap = new Map((window.ACCOUNTS || []).map(a => [a.uid, a]));
      // Cemetery view on the Accounts page reads from ACCOUNTS_INACTIVE.
      // Same shape as ACCOUNTS (accountFromRow), so the table renders
      // identically with only the active flag flipped.  Keyed by
      // (instance, label) tuples so we don't collide with the live
      // window.ACCOUNTS — labels can repeat across instances.
      const _isInactive = r => r.active === 0 || DECOMMISSIONED_LABELS.has(r.label);
      window.ACCOUNTS_INACTIVE = (accounts || []).filter(_isInactive).map(accountFromRow);
      window.ACCOUNTS = (accounts || []).filter(r => !_isInactive(r)).map(row => {
        const fresh = accountFromRow(row);
        const prev = prevAcctMap.get(fresh.uid);
        if (prev) {
          if (typeof prev.equity === "number" && prev.equity > 0) fresh.equity = prev.equity;
          if (prev.equityVerified) fresh.equityVerified = true;
          if (typeof prev.dayPnl === "number") fresh.dayPnl = prev.dayPnl;
          if (typeof prev.totalPnl === "number") fresh.totalPnl = prev.totalPnl;
          if (typeof prev.mllFloor === "number" && prev.mllFloor > 0) fresh.mllFloor = prev.mllFloor;
          if (prev.strategyStatus) fresh.strategyStatus = prev.strategyStatus;
          if (prev.strategyLines)  fresh.strategyLines  = prev.strategyLines;
          if (prev.strategiesLastUs) fresh.strategiesLastUs = prev.strategiesLastUs;
          if (prev.positions)      fresh.positions      = prev.positions;
          if (typeof prev.openPositions === "number") fresh.openPositions = prev.openPositions;
        }
        return fresh;
      });
      markLoaded("accounts");  // /api/accounts + /api/instances resolved

      // Cross-link: instance.strategies + primary account
      const stratsByInst = {};
      const primaryByInst = {};
      for (const a of accounts || []) {
        (stratsByInst[a.instance_id] = stratsByInst[a.instance_id] || new Set()).add(a.strategy);
        if (!primaryByInst[a.instance_id]) primaryByInst[a.instance_id] = a.label;
      }
      for (const inst of window.INSTANCES) {
        inst.strategies = Array.from(stratsByInst[inst.id] || []);
        inst.account = primaryByInst[inst.id] || null;
      }

      // 2. Equity snapshot per account -> fill ACCOUNTS[i].equity / dayPnl
      //    / mllFloor.  /api/equity returns DESC (newest first), so the
      //    latest row is eq[0] — not eq[eq.length-1], which was the prior
      //    bug that left a.equity reading the *oldest* of the limit=2
      //    pull and rendering as $0 for fresh accounts whose first row
      //    pre-dated any agent emit.
      await Promise.all((window.ACCOUNTS || []).map(async a => {
        try {
          const eq = await getJson(
            `/api/equity?instance_id=${encodeURIComponent(a.instance)}` +
            `&account_label=${encodeURIComponent(a.id)}&limit=2`
          );
          if (eq.length) {
            const latest = eq[0];
            const synth = window.practiceEquity(a, latest);
            // Only override existing equity if we got something meaningful
            // (synthesised practice value OR a non-zero balance row).
            const realBalance = latest.balance;
            if (synth != null) {
              a.equity = synth;
            } else if (typeof realBalance === "number" &&
                       (realBalance > 0 || a.kind === "xfa" || a.kind === "funded")) {
              a.equity = realBalance;
            }
            // Always update intra-day pnl + mll from the latest snapshot.
            // XFA/EXPRESS + funded balances are drawdown-relative (start ~0,
            // go negative on losses), but the agent emits day_pnl = balance −
            // day_start where day_start is seeded from the tier capital
            // (~$150K).  That yields absurd values like −$153,818.50 for a
            // −$3,818 EXPRESS balance.  Detect the tell — |day_pnl| blown up
            // by roughly the tier capital — and derive the real day P&L from
            // today's exit fills instead (0 when flat today), mirroring the
            // practiceDayPnl correction.  Plausible snapshots pass through.
            if (typeof latest.day_pnl === "number") {
              const dp = latest.day_pnl;
              const tier = Number(a.initial_capital) || 0;
              const bogusTierSubtraction =
                (a.kind === "xfa" || a.kind === "funded") &&
                tier > 0 && Math.abs(dp) > tier * 0.5;
              a.dayPnl = bogusTierSubtraction ? window.practiceDayPnl(a) : dp;
            }
            if (typeof latest.mll_floor === "number" && latest.mll_floor > 0)
              a.mllFloor = latest.mll_floor;
            a.totalPnl = (a.equity || 0) - window.accountAnchor(a);
            a.equityVerified = true;
          } else if (!(a.equity > 0)) {
            // No equity history *and* no cached value — fall back to the
            // account's initial_capital so KPIs show the allocated anchor
            // instead of $0 until the first tick arrives.  When cache
            // hydration already populated a.equity, the fallback is a
            // downgrade and we skip it.
            a.equity = Number(a.initial_capital) || 0;
            a.dayPnl = 0;
            a.totalPnl = 0;
          }
        } catch (_) {
          // Fetch failed — leave whatever we had (cache value) rather
          // than overwrite with the anchor.
        }
      }));

      // Flip hasData=true here, AFTER per-account equity is filled.
      // Previously this lived right after /api/accounts returned, before
      // step 2 had populated a.equity — so the shimmer dissolved into
      // KPI tiles showing $0 even though /api/equity had data.  The
      // remaining bootstrap steps (fills, alerts, inventory) still flip
      // it at the end as a safety net.
      window.__LIVE.hasData = true;
      markLoaded("equitySnapshot");
      bump();

      // Diagnostic: log post-step-2 state so the operator can paste
      // this into a bug report when KPIs look empty.  One line per
      // account with the values that drive the tiles.  Gated on a
      // window debug flag because every WS reconnect re-bootstraps
      // and this line was producing 200+ noisy lines per session —
      // operators can flip window.__AVIAF_DEBUG_BOOTSTRAP=true in
      // devtools to surface it when actually triaging.
      if (window.__AVIAF_DEBUG_BOOTSTRAP) {
        try {
          const summary = (window.ACCOUNTS || []).map(a => ({
            id: a.id, kind: a.kind, equity: a.equity, dayPnl: a.dayPnl,
            totalPnl: a.totalPnl, mllFloor: a.mllFloor, initial_capital: a.initial_capital,
          }));
          console.log("[aviaf] bootstrap step 2 done — accounts:", summary,
                      "instances:", (window.INSTANCES || []).length);
        } catch (_) {}
      }

      // 3. Fills -> TRADES (only what's in the DB; no fillers)
      // Pull the API max (5000) so per-account WR/PF/best-day/calendar
      // have enough exit fills to compute against — limit=200 was too
      // small for active accounts where most recent fills are entries.
      // pairFillsToTrades collapses each (open, close) fill pair into a
      // single round-trip row; unmatched entries remain as open stubs.
      const fills = await getJson("/api/fills?limit=5000");
      window.TRADES = pairFillsToTrades(fills || []);
      markLoaded("fills");

      // 3b. Recompute practice equity + dayPnl now that TRADES is
      // populated — but only when step 2 fell back to initial_capital
      // (i.e. the latest equity snapshot had balance=0, the legacy
      // path).  Newer agents report a real balance for practice
      // accounts, in which case step 2 already set a.equity correctly
      // and re-synthesising from fills would *downgrade* the value:
      //   step 2:  a.equity = 160_722.80    (real broker balance)
      //   step 3b: a.equity = 150_000 + Σ(attributed exits) = 150_000
      //                       (when fills lack strategy attribution
      //                        the loop sums 0 and we lose 10K)
      // The day-pnl override stays — practice day_pnl from snapshots
      // is unreliable regardless of the balance source.
      for (const a of (window.ACCOUNTS || [])) {
        // XFA/funded with a tier-capital-poisoned day_pnl (see step 2): now
        // that TRADES exists, redo the fills-based day P&L correctly.
        if (a.kind === "xfa" || a.kind === "funded") {
          const tier = Number(a.initial_capital) || 0;
          if (tier > 0 && Math.abs(Number(a.dayPnl) || 0) > tier * 0.5) {
            a.dayPnl = window.practiceDayPnl(a);
          }
          continue;
        }
        if (a.kind !== "practice") continue;
        const anchor = window.accountAnchor(a);
        const stepTwoUsedFallback = (a.equity === anchor) || !(a.equity > 0);
        if (stepTwoUsedFallback) {
          const synth = window.practiceEquity(a, { balance: 0 });
          if (synth != null) {
            a.equity = synth;
            a.totalPnl = synth - anchor;
          }
        }
        const dp = window.practiceDayPnl(a);
        if (dp != null) a.dayPnl = dp;
      }

      // 3c. Early STRATS build so mobile shows real values (not "0/0")
      // as soon as ACCOUNTS + TRADES are available.  The final
      // rebuildStratsFromAccounts() at the end refines with strategy
      // states from step 4b and equity curves from step 5.
      rebuildStratsFromAccounts();
      window.__LIVE.strats_ready = true;
      bump();

      // 4. Open positions + per-strategy status per (instance, account).
      // Hub-side /api/positions now reads from the positions_latest table
      // (one row per account) which the ingest handler upserts on every
      // equity event.  Each row carries:
      //   .positions   — live positions[] array
      //   .strategies  — agent's strategies_status array
      //                  ({id, short, lines[]} per strategy)
      // Without seeding strategyStatus here, hard-refreshed dashboards
      // showed every strategy as "—" until the next 60-s heartbeat
      // equity WS frame arrived.  Now the panel is populated on first
      // paint.
      window.OPEN_POSITIONS = {};
      try {
        const posRows = await getJson("/api/positions");
        for (const r of (posRows || [])) {
          const arr = Array.isArray(r.positions) ? r.positions : [];
          if (arr.length) {
            const tagged = arr.map(p => ({
              ...window.normalizePosition(p), account: r.account_label,
            }));
            const inst = r.instance_id;
            window.OPEN_POSITIONS[inst] = (window.OPEN_POSITIONS[inst] || []).concat(tagged);
          }
          // Seed per-strategy status on the matching account.  Same
          // shape as the WS equity handler builds at line ~1654 so the
          // panel renders identically pre- and post- first-WS-frame.
          const strats = Array.isArray(r.strategies) ? r.strategies : [];
          if (strats.length) {
            const a = (window.ACCOUNTS || []).find(
              x => x.instance === r.instance_id && x.id === r.account_label,
            );
            if (a) {
              const m = {}, lm = {}, cm = {}, pm = {}, rm = {};
              const ids = [];
              const seenIds = new Set();
              for (const s of strats) {
                if (!s || !s.id) continue;
                m[s.id] = s.short || "";
                lm[s.id] = Array.isArray(s.lines) ? s.lines : [];
                if (typeof s.contracts === "number") cm[s.id] = s.contracts;
                if (s.paused) pm[s.id] = true;
                if (Number(s.risk_per_trade) > 0) rm[s.id] = Number(s.risk_per_trade);
                if (!seenIds.has(s.id)) { seenIds.add(s.id); ids.push(s.id); }
              }
              a.strategyStatus = m;
              a.strategyLines = lm;
              a.strategyContracts = cm;
              a.strategyPaused = pm;
              a.strategyRisk = rm;
              a.strategiesLastUs = r.ts_us || (Date.now() * 1000);
              // Merge any new variants into a.strategies — same rationale
              // as the equity-event backfill below: variants attached
              // post-HELLO won't be in /api/accounts.strategy but DO show
              // up in every status emit, so the risk-return map only
              // misses them when we replace-only-when-empty.
              if (ids.length) {
                const have = new Set(a.strategies || []);
                const merged = [...(a.strategies || [])];
                for (const sid of ids) {
                  if (!have.has(sid)) { merged.push(sid); have.add(sid); }
                }
                if (merged.length !== (a.strategies || []).length) {
                  a.strategies = merged;
                  if (!a.strategy) a.strategy = merged[0];
                }
              }
            }
          }
        }
        markLoaded("positions");
        // Strategy status is fully populated from positions_latest
        // (which carries strategies_json on every equity ingest).
        // The old step 4b fetched /api/events?kind=equity&limit=500
        // but since schema v8, equity events store slim payloads
        // (payload='{}'), so that fetch returned empty strategies
        // and was a pure no-op + wasted round-trip.  Mark strategies
        // loaded here — the first WS equity frame will refresh.
        markLoaded("strategies");
      } catch (_) { markLoaded("positions"); markLoaded("strategies"); }

      // 4c. Goal-tracker snapshot — latest goal_state event per account.
      // Carries profit_target + consistency-adjusted effective target so
      // combine accounts render the right MLL bar geometry on first paint.
      // Without this, a hard refresh shows "no target — survival" until the
      // next post-fill goal_state push (which may be hours away on a quiet
      // session).  Same shape as 4b above, just a different kind.
      try {
        const goalEvents = await getJson("/api/events?kind=goal_state&limit=200");
        const latestGoalByAcct = {};
        for (const e of (goalEvents || [])) {
          const k = `${e.instance_id}/${e.account_label}`;
          if (!latestGoalByAcct[k] || (e.ts_us || 0) > (latestGoalByAcct[k].ts_us || 0)) {
            latestGoalByAcct[k] = e;
          }
        }
        for (const a of (window.ACCOUNTS || [])) {
          const e = latestGoalByAcct[`${a.instance}/${a.id}`];
          const p = e?.payload;
          if (!p) continue;
          if (typeof p.profit_target === "number" && p.profit_target > 0) a.target = p.profit_target;
          if (typeof p.effective_profit_target === "number" && p.effective_profit_target > 0) {
            a.effectiveTarget = p.effective_profit_target;
          }
          if (typeof p.consistency_pct === "number") a.consistencyPct = p.consistency_pct;
          if (typeof p.max_daily_profit === "number") a.maxDailyProfit = p.max_daily_profit;
          if (typeof p.best_day_pnl === "number") a.bestDayPnl = p.best_day_pnl;
          if (typeof p.cumulative_pnl === "number") a.cumulativePnl = p.cumulative_pnl;
          if (typeof p.today_pnl === "number") a.todayGoalPnl = p.today_pnl;
          if (typeof p.days_traded === "number") a.daysTraded = p.days_traded;
          if (typeof p.passed === "boolean") a.passed = p.passed;
          a.goalLastUs = e.ts_us || null;
        }
      } catch (_) { /* tolerated; WS goal_state push will fill in */ }

      // 4d. Combine P(pass)/T(pass) snapshot — latest pass_estimate event per
      // account (agent computes hourly from the account's CURRENT state via
      // strategy/pass_prob.py; see ops/agent/pass_estimate.py).  AccountCard
      // renders the P(pass 30d) / P(eventual) / T50 / T90 row for combines.
      try {
        const passEvents = await getJson("/api/events?kind=pass_estimate&limit=100");
        const latestPassByAcct = {};
        for (const e of (passEvents || [])) {
          const k = `${e.instance_id}/${e.account_label}`;
          if (!latestPassByAcct[k] || (e.ts_us || 0) > (latestPassByAcct[k].ts_us || 0)) {
            latestPassByAcct[k] = e;
          }
        }
        for (const a of (window.ACCOUNTS || [])) {
          const e = latestPassByAcct[`${a.instance}/${a.id}`];
          if (!e?.payload) continue;
          a.passEst = e.payload;
          a.passEstLastUs = e.ts_us || null;
        }
      } catch (_) { /* tolerated; WS pass_estimate push will fill in */ }

      // 5. Per-account equity series + a real aggregate.
      // Pull each account's recent equity in parallel, store per-account
      // for detail pages, AND build EQUITY_TOTAL as the SUM-across-accounts
      // at each shared timestamp. Without this, the plot bounces between
      // accounts (artifact) instead of showing real fleet equity.
      window.EQUITY_BY_ACCT = {};
      // 30D-trailing reference timestamp.  Per-account, fetch the most
      // recent equity row at or before this — the sum is AUM_30D_AGO,
      // which the dashboard subtracts from current AUM to compute the
      // "30D P&L" KPI (= trailing 30-day overall P&L).
      //
      // Reuse the exact integer _prefetchBootstrap chose (rounded to
      // a 60-second boundary).  If we recompute Date.now() here, the
      // until_us query-string bytes drift from the prefetch's cache
      // key by milliseconds and every per-account /api/equity?until_us
      // call goes to the wire — exactly the round-trips bootstrap was
      // supposed to eliminate.
      const TS_30D_AGO_US =
        window.__BOOT_TS_30D_AGO_US != null
          ? window.__BOOT_TS_30D_AGO_US
          : Math.floor((Date.now() - 30 * 24 * 3600 * 1000) / 60_000) * 60_000_000;
      const seriesByAcct = await Promise.all((window.ACCOUNTS || []).map(async a => {
        try {
          // Agents emit equity at ~5s cadence; a YTD window is millions
          // of rows.  Ask the hub to bucket hourly — at 24 buckets/day
          // that's ~720 points over 30D, ~2160 over 90D, ~8760 over a
          // full year, the right density for a plot without flattening
          // into contiguous-second rows that render as a straight line
          // because nothing moves within 16 minutes of trading.
          // limit=9000 ≈ 375 days, so 5D / 30D / 90D / YTD all have real
          // history at any time of year.  Previously this also passed
          // since_us=TS_30D_AGO_US, which hard-capped the plot to 30
          // days regardless of which timeframe button was active —
          // 90D and YTD collapsed onto the 30D view.
          const [eq, eq30] = await Promise.all([
            getJson(
              `/api/equity?instance_id=${encodeURIComponent(a.instance)}` +
              `&account_label=${encodeURIComponent(a.id)}` +
              `&bucket_seconds=3600&limit=9000`
            ),
            getJson(
              `/api/equity?instance_id=${encodeURIComponent(a.instance)}` +
              `&account_label=${encodeURIComponent(a.id)}` +
              `&until_us=${TS_30D_AGO_US}&limit=1`
            ).catch(() => []),
          ]);
          // /api/equity returns DESC; reverse for chronological plotting.
          const series = eq.map(r => (r.mtm_equity ?? r.balance)).reverse();
          window.EQUITY_BY_ACCT[`${a.instance}/${a.id}`] = series;
          // eq30[0] is the most recent equity row at-or-before 30d ago.
          // Compute eq30Val with the allocated anchor (initial_capital
          // for combine/practice; 0 for XFA/funded) as a FLOOR — not just
          // a fallback for missing rows.  Practice accounts emit
          // balance=0 historically (no real broker balance behind them),
          // so a raw eq30row.balance reads 0 and the dashboard's "30d
          // P&L = AUM - 0 = AUM" looks like a 100% gain in 30 days when
          // really it's the practice anchor masking the comparison.
          // For XFA/funded the anchor is 0, so this floor is a no-op
          // and the broker-reported equity flows through normally.
          const anchor = window.accountAnchor
            ? window.accountAnchor(a)
            : (a.initial_capital || 0);
          const eq30row = (eq30 && eq30[0]) || null;
          const eq30FromApi = eq30row
            ? (Number(eq30row.mtm_equity) || Number(eq30row.balance) || anchor)
            : anchor;
          const eq30Val = Math.max(eq30FromApi, anchor);
          // eq30HasHist should only flip true when the API row carries
          // *usable* equity (mtm_equity or balance > 0); a zero-balance
          // practice row doesn't qualify, so the KPI subtitle correctly
          // reads "since inception" rather than "trailing 30 days".
          const eq30RowUsable = !!(eq30row
            && (Number(eq30row.mtm_equity) > 0 || Number(eq30row.balance) > 0));
          // Stash on the account object so _refreshAccounts (which doesn't
          // re-run the 30d sweep) can keep AUM_30D_AGO in sync after an
          // add/delete by simply re-summing surviving accounts.  Without
          // this, a freshly-added-then-deleted account left AUM_30D_AGO
          // padded by its initial_capital anchor while current AUM dropped
          // by the same amount — the diff showed up as a phantom 30d loss.
          a.eq30 = eq30Val;
          a.eq30HasHist = eq30RowUsable;
          return { acct: a, series, raw: eq, eq30: eq30Val, eq30HasHist: eq30RowUsable };
        } catch (_) {
          // /api/equity timeout / 5xx should NOT zero the account's
          // contribution to AUM_30D_AGO — fall back to the anchor so the
          // 30D P&L KPI stays honest even on a partial bootstrap failure.
          const anchor = window.accountAnchor
            ? window.accountAnchor(a)
            : (a.initial_capital || 0);
          a.eq30 = anchor;
          a.eq30HasHist = false;
          return { acct: a, series: [], raw: [], eq30: anchor, eq30HasHist: false };
        }
      }));
      // AUM_30D_AGO = sum across accounts of equity at-or-before 30d-ago
      // (or the account's allocated anchor when the account is younger
      // than 30d).  Track which accounts actually have ≥30d of history so
      // the KPI subtitle can be honest about partial coverage.
      window.AUM_30D_AGO = seriesByAcct.reduce(
        (s, x) => s + (Number.isFinite(x.eq30)
          ? x.eq30
          : (window.accountAnchor
              ? window.accountAnchor(x.acct)
              : (x.acct.initial_capital || 0))),
        0,
      );
      window.AUM_30D_HAS_HIST = seriesByAcct.some(x => x.eq30HasHist);
      // Aggregate: union the timestamps across all accounts and, at
      // each tick, sum each account's equity *at-or-before* that
      // timestamp (binary search), falling back to the account's anchor
      // (initial capital) when an account didn't exist yet at that
      // point.  This preserves the longest account's full history even
      // when other accounts are newer or have shorter coverage — a
      // prior minLen-based walk clipped the aggregate to the SHORTEST
      // account's length, so adding a 1-day-old account would silently
      // drop the 7-day practice account's earlier history.
      const stratOf = (a) => a.strategy || (a.strategies && a.strategies[0]) || "unassigned";
      // Build per-account chronological ts arrays from raw rows (DESC →
      // asc).  series is already chronological — line them up.
      const acctTs = seriesByAcct.map(s => ({
        ...s,
        tsChrono: (s.raw || []).map(r => r.ts_us).reverse(),
      }));
      const tsDonor = acctTs.reduce(
        (best, x) => (x.tsChrono.length > (best ? best.tsChrono.length : 0) ? x : best),
        null,
      );
      if (tsDonor && tsDonor.tsChrono.length > 0) {
        const tsAxis = tsDonor.tsChrono.slice();
        const allStrats = Array.from(new Set(seriesByAcct.map(s => stratOf(s.acct))));
        const byStrat = Object.fromEntries(allStrats.map(k => [k, []]));
        const pnlByStrat = Object.fromEntries(allStrats.map(k => [k, []]));
        const allocAnchor = (window.ACCOUNTS || []).reduce(
          (s, a) => s + (window.accountAnchor ? window.accountAnchor(a) : (a.initial_capital || 0)), 0,
        );
        const stratAlloc = Object.fromEntries(allStrats.map(k => [k, 0]));
        for (const s of seriesByAcct) {
          const k = stratOf(s.acct);
          stratAlloc[k] += window.accountAnchor ? window.accountAnchor(s.acct) : (s.acct.initial_capital || 0);
        }
        // Binary-search the largest index where ts[i] <= target.  -1 if
        // no row at-or-before target.
        const idxAtOrBefore = (arr, target) => {
          let lo = 0, hi = arr.length;
          while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (arr[mid] <= target) lo = mid + 1;
            else hi = mid;
          }
          return lo - 1;
        };
        const aggregate = [];
        const pnl = [];
        for (let i = 0; i < tsAxis.length; i++) {
          const ti = tsAxis[i];
          let sum = 0;
          const tmp = Object.fromEntries(allStrats.map(k => [k, 0]));
          for (const s of acctTs) {
            const idx = idxAtOrBefore(s.tsChrono, ti);
            const v = idx >= 0
              ? s.series[idx]
              : (window.accountAnchor ? window.accountAnchor(s.acct) : (s.acct.initial_capital || 0));
            sum += v;
            tmp[stratOf(s.acct)] += v;
          }
          aggregate.push(sum);
          pnl.push(sum - allocAnchor);
          for (const k of allStrats) {
            byStrat[k].push(tmp[k]);
            pnlByStrat[k].push(tmp[k] - stratAlloc[k]);
          }
        }
        window.EQUITY_TOTAL = aggregate;
        window.EQUITY_BY_STRATEGY = byStrat;
        window.PNL_TOTAL = pnl;
        window.PNL_BY_STRATEGY = pnlByStrat;
        window.EQUITY_TS = tsAxis;
      } else {
        window.EQUITY_TOTAL = [];
        window.EQUITY_BY_STRATEGY = {};
        window.PNL_TOTAL = [];
        window.PNL_BY_STRATEGY = {};
        window.EQUITY_TS = [];
      }
      markLoaded("equitySeries");

      // 6. Recent events -> feed.  Restrict to human-meaningful kinds:
      // an unfiltered /api/events returns the newest rows, which are almost
      // all high-volume market-data anchors (quote/bar) — useless in the feed
      // and, before idx_events_ts existed, a full-table scan that timed out.
      // fill/alert/status are each covered by idx_events_kind_ts.
      const events = await getJson("/api/events?limit=30&kinds=fill,alert,status");
      window.FEED = (events || []).map(eventToFeed);
      markLoaded("feed");

      // 7. Inventory snapshots — collectors / configs / research.  Each
      // endpoint returns the latest payload across all instances so the
      // dashboard pages render immediately on first paint; live updates
      // arrive via WS frames handled in applyFrame().
      try {
        const [collectors, configs, research, runs, artifacts, candidates, spc, alerts, spcBaselines, incidents] = await Promise.all([
          getJson("/api/collectors").catch(() => []),
          getJson("/api/configs").catch(() => []),
          getJson("/api/research").catch(() => []),
          getJson("/api/research/runs?limit=200").catch(() => []),
          getJson("/api/research/artifacts?limit=500").catch(() => []),
          getJson("/api/research/candidates?limit=500").catch(() => []),
          getJson("/api/spc?limit=200").catch(() => []),
          getJson("/api/alerts?limit=200").catch(() => []),
          getJson("/api/spc/baselines").catch(() => []),
          getJson("/api/techops/incidents?limit=50").catch(() => []),
        ]);
        // TechOps incident timeline (alert → diagnosis → action → outcome).
        // Newest-first; live-appended from the "techops" WS channel below.
        window.TECHOPS_INCIDENTS = incidents || [];
        window.COLLECTORS = (collectors || []).map(normalizeCollector);
        window.CONFIGS    = (configs || []).map(normalizeConfig);
        window.RESEARCH   = research || [];
        window.RESEARCH_RUNS = runs || [];
        window.RESEARCH_ARTIFACTS = artifacts || [];
        window.RESEARCH_CANDIDATES = candidates || [];
        window.SPC_ROWS = spc || [];
        // Keyed by `${account_label}|${strategy}` so the SPC panel can
        // look up baselines for any (account, strategy) pair the dashboard
        // is rendering.  Empty {} when the agent hasn't shipped any yet —
        // panel falls back to a "no baseline" empty state.
        const sb = {};
        for (const b of (spcBaselines || [])) {
          if (!b || !b.strategy) continue;
          sb[`${b.account_label || ""}|${b.strategy}`] = b;
        }
        window.SPC_BASELINES = sb;
        window.ALERTS   = alerts || [];   // raw DAL rows; PageAlerts normalises
        // Derive market regime symbol cells from collector telemetry
        // (live price + tps as a coarse activity proxy).  Real regime
        // metrics (ATR, ADX, z-score) ride on a future agent emit.
        rebuildRegimeFromCollectors();
        rebuildStorageRollup();
        markLoaded("spc");
        markLoaded("alerts");
        markLoaded("inventory");
      } catch (_) { markLoaded("spc"); markLoaded("alerts"); markLoaded("inventory"); /* render empty state */ }

      // 8. Market data (Phase 4) — catalog of recorded symbols + latest
      // quote per symbol.  Per-symbol bars / book / prints load lazily
      // when the detail page mounts (so the dashboard doesn't fetch N
      // big arrays upfront).  Live updates arrive via the WS handlers
      // below.
      try {
        const symbols = await getJson("/api/market/symbols").catch(() => []);
        window.MARKET_CATALOG = symbols || [];
        // Seed the quote store from the catalog so the list page renders
        // last px without an extra fetch per card.
        const quoteMap = {};
        for (const s of (symbols || [])) {
          if (s.symbol && s.quote) quoteMap[s.symbol] = s.quote;
        }
        window.MARKET_QUOTES = quoteMap;
        // Lazily-populated stores — pages call window.fetchMarket*() to
        // populate on demand.  Each holds the per-symbol most-recent
        // payload merged with WS deltas.
        window.MARKET_BARS   = window.MARKET_BARS   || {};   // sym → {tf → [bars]}
        window.MARKET_BOOK   = window.MARKET_BOOK   || {};   // sym → {bids,asks,ts_us}
        window.MARKET_PRINTS = window.MARKET_PRINTS || {};   // sym → [{ts_us,px,sz,side}, ...]
      } catch (_) { /* render empty state */ }

      // STRATS roster depends on ACCOUNTS being populated AND PnL
      // totals being computed, so build it last in the bootstrap chain.
      rebuildStratsFromAccounts();
      // Stamp strats_ready even if rebuildStratsFromAccounts produced
      // zero rows (truly no strategies on any account) — without this
      // the map would shimmer indefinitely on a freshly-registered hub
      // that has no accounts attached yet.  The map will flip from
      // shimmer to the "deploy on an account" prompt at this point.
      window.__LIVE.strats_ready = true;

      window.__LIVE.lastBootstrap = Date.now();
      window.__LIVE.hasData = true;
      window.__LIVE.err = null;
      // Mark this run as the last successful bootstrap.  The throttle
      // guard in bootstrap() reads this so a WS reconnect within 5 s
      // skips re-running the whole pipeline.
      _bootstrapLastOkAt = Date.now();
      persistCache();
      bump();
    } catch (err) {
      window.__LIVE.err = String(err.message || err);
    } finally {
      // Drop the bootstrap pre-fetch cache so post-bootstrap fetches
      // (debounced /api/alerts refresh, periodic _refreshAccounts) hit
      // the wire again and pick up state mutated since boot.  The hub's
      // per-endpoint TTL cache still absorbs duplicate requests across
      // tabs / quick reloads.
      _bootCacheClear();
      window.__LIVE.bootstrapping = false;
      bump();
    }
  }

  /* -- WS push: real-time; primary update path -- */

  let bridge = null;
  let pingTimer = null;
  let pingId = 0;
  let lastWsStatus = "idle";
  // Sticky flag: set when we see "reconnecting", consumed on the next
  // "live".  The natural reconnect transition is reconnecting → connecting
  // → live, and lastWsStatus checks alone can't distinguish that from the
  // initial idle → connecting → live (both have lastWsStatus === "connecting"
  // when "live" arrives).  Without this, bootstrap silently failed to
  // re-fire after a drop, so any events that occurred during the gap stayed
  // missing until the next organic event triggered a render.
  let sawReconnect = false;

  /* Merge a "techops" push into the incident timeline + activity feed.
   * The router emits two shapes on this channel:
   *   open   — carries `kind` (+ actor/tier/instance): a new action row
   *   update — no `kind`, carries `status`/`outcome`: patch an existing row
   * Both are keyed by (incident_id, action_pk).  Incidents newest-first. */
  function applyTechops(msg) {
    if (!msg || !msg.incident_id) return;
    const list = window.TECHOPS_INCIDENTS || (window.TECHOPS_INCIDENTS = []);
    let inc = list.find((x) => x.incident_id === msg.incident_id);
    if (!inc) {
      inc = { incident_id: msg.incident_id, started_us: msg.ts_us || (Date.now() * 1000),
              last_us: msg.ts_us || (Date.now() * 1000), n_actions: 0,
              trigger_alert_pk: msg.trigger_alert_pk ?? null, open: true, actions: [] };
      list.unshift(inc);
      window.TECHOPS_INCIDENTS = list.slice(0, 100);
    }
    if (msg.kind) {
      // new action row
      inc.actions.push({
        action_pk: msg.action_pk, kind: msg.kind, actor: msg.actor,
        tier: msg.tier, status: msg.status || "executing",
        instance_id: msg.instance_id, account_label: msg.account_label,
        ts_us: msg.ts_us || (Date.now() * 1000),
      });
      if (msg.trigger_alert_pk != null && inc.trigger_alert_pk == null)
        inc.trigger_alert_pk = msg.trigger_alert_pk;
      // surface the action in the live activity feed too
      const who = (msg.actor || "").replace(/^mcp:/, "");
      const tgt = msg.account_label ? ` · ${msg.account_label}` : "";
      window.FEED = [{
        t: new Date().toLocaleTimeString(),
        tag: msg.tier === "destructive" ? "risk" : "info",
        txt: `techops · ${who} · ${msg.kind}${tgt}`,
      }, ...(window.FEED || [])].slice(0, 80);
    } else if (msg.action_pk != null) {
      // patch an existing action's outcome
      const a = inc.actions.find((x) => x.action_pk === msg.action_pk);
      if (a) { if (msg.status) a.status = msg.status; if (msg.outcome != null) a.outcome = msg.outcome; }
    }
    inc.n_actions = inc.actions.length;
    inc.last_us = Date.now() * 1000;
    inc.open = inc.actions.some((a) => ["proposed", "approved", "executing"].includes(a.status));
    // bubble the touched incident to the front
    const idx = list.indexOf(inc);
    if (idx > 0) { list.splice(idx, 1); list.unshift(inc); }
  }

  function applyFrame(msg) {
    if (!msg || !window.__LIVE.authed) return;

    // Latency netgraph (system channel).  cpong → browser computes its own
    // RTT; sping → reply so the hub can measure its RTT; slat → the hub's
    // measured RTT for us to display.  hello/ping carry no app data.
    if (msg.channel === "system") {
      const k = msg.kind;
      if (k === "cpong") {
        if (typeof msg.t === "number") {
          const rtt = Math.max(0, Math.round(performance.now() - msg.t));
          window.__LIVE.pingClient = rtt;
          _recordPing("Client", rtt);
          bump();
        }
      } else if (k === "sping") {
        if (bridge) bridge.send({ kind: "spong", id: msg.id });
      } else if (k === "slat") {
        if (typeof msg.ms === "number") {
          const rtt = Math.round(msg.ms);
          window.__LIVE.pingServer = rtt;
          _recordPing("Server", rtt);
          bump();
        }
      }
      return;
    }

    if (msg.channel === "instance") {
      // connect/disconnect -- re-pull authoritative list
      bootstrap();
      return;
    }
    if (msg.channel === "techops") {
      applyTechops(msg);
      bump();
      return;
    }
    if (msg.channel !== "event") return;

    // Heartbeat: every event from this instance is a liveness signal.
    // Bump lastSeenMs so the Overview Health panel's "agent heartbeat
    // Ns ago" row keeps decreasing between bootstraps.  Before this,
    // lastSeenMs was frozen at the value /api/instances returned on the
    // last bootstrap — so the row grew unboundedly even while the agent
    // was actively heartbeating, and only reset on a fresh bootstrap.
    if (msg.instance_id) {
      const inst = (window.INSTANCES || []).find(i => i.id === msg.instance_id);
      if (inst) {
        inst.lastSeenMs = Date.now();
        // If the WS frame carries a clock_offset_us on the envelope,
        // refresh the skew display too.  Hub-side ingest stamps every
        // event with the agent's reported clock offset (touch_instance
        // writes it to instances.last_clock_offset_us).  Frames don't
        // carry it directly today, so we leave the bootstrap-derived
        // value in place and only update lastSeenMs.
      }
    }

    // Always prepend to feed
    window.FEED = [eventToFeed({
      ts_us: msg.ts_us, kind: msg.kind, instance_id: msg.instance_id,
      account_label: msg.account_label, payload: msg.payload,
    }), ...(window.FEED || [])].slice(0, 80);

    if (msg.kind === "health") {
      const inst = (window.INSTANCES || []).find(i => i.id === msg.instance_id);
      if (inst) {
        const host = msg.payload?.host || {};
        const proc = msg.payload?.process || {};
        inst.cpu = Math.round((host.cpu_pct ?? 0) * 10) / 10;
        inst.mem = Math.round((proc.rss_bytes || 0) / 1_000_000);
        if (host.mem_total_bytes) inst.memPct = (host.mem_used_bytes || 0) / host.mem_total_bytes;
        inst.load1m = host.load_1m ?? 0;
        if (Array.isArray(inst.heartbeat) && inst.heartbeat.length) {
          inst.heartbeat = [...inst.heartbeat.slice(1), Math.max(0.05, (host.cpu_pct ?? 0)/100)];
        }
      }
      // Live-append to INSTANCE_LOGS so the terminal-style panel
      // updates without waiting for the next bootstrap.
      pushInstanceLog(msg);
    } else if (msg.kind === "alert") {
      pushInstanceLog(msg);
    } else if (msg.kind === "fill") {
      applyLiveFillToTrades({
        fill_pk: Date.now(),
        instance_id: msg.instance_id,
        account_label: msg.account_label,
        ts_us: msg.ts_us,
        ...(msg.payload || {}),
      });
      // Practice equity rides on the fills sum, not on agent-reported
      // balance — recompute immediately so the card moves on every fill
      // instead of waiting for the next equity tick.
      const acct = (window.ACCOUNTS || []).find(x =>
        x.instance === msg.instance_id && x.id === msg.account_label
      );
      if (acct && acct.kind === "practice") {
        const synth = window.practiceEquity(acct, { balance: 0 });
        if (synth != null) {
          acct.equity = synth;
          acct.totalPnl = synth - window.accountAnchor(acct);
        }
        const dp = window.practiceDayPnl(acct);
        if (dp != null) acct.dayPnl = dp;
      }
      // Cheap SPC refresh — only on EXIT fills (entry doesn't move
      // realized P&L).  Debounced so a flurry of fills doesn't hammer
      // /api/spc; one refresh per ~3s is plenty for a 20-session band.
      const isExit = !!(msg.payload && msg.payload.is_exit);
      if (isExit) {
        // Recompute per-strategy today/pnl30/wr/pf NOW so the account
        // detail page's Strategies rows + AccountSpcPanel reflect this
        // fill immediately, instead of waiting for the next equity tick
        // to fire rebuildStratsFromAccounts.  Cheap — O(TRADES × strats).
        rebuildStratsFromAccounts();
        scheduleSpcRefresh();
      }
    } else if (msg.kind === "equity") {
      const a = (window.ACCOUNTS || []).find(x =>
        x.instance === msg.instance_id && x.id === msg.account_label
      );
      const p = msg.payload || {};
      if (a && (typeof p.balance === "number" || a.kind === "practice")) {
        // Mark-to-market preferred over raw balance (it includes unrealized
        // P&L on open positions). Fall back to balance when positions=0.
        // Practice has no real broker balance — synthesize from tier + P&L.
        const synth = window.practiceEquity(a, p);
        a.equity = (synth != null)
          ? synth
          : ((typeof p.mtm_equity === "number") ? p.mtm_equity : p.balance);
        a.balance = p.balance;
        a.unrealized = p.unrealized_pnl ?? 0;
        // Hands-off: operator has manual control (reconciler + protective lock
        // suppressed on the agent). Drives the HANDS-OFF badge.
        a.handsOff = !!p.hands_off;
        // DLL day-lock: the broker's DAILY_LOSS session lock on DLL-variant
        // tickets, mirrored day-scoped by the agent. NOT a permanent lock —
        // entries resume next session. Drives the amber DLL banner.
        a.dllDayLock = !!p.dll_day_lock;
        // First agent equity tick after a fresh registration — drop the
        // shimmer; the value on screen is now real, not an anchor fallback.
        a.equityVerified = true;
        // Override unreliable agent-reported day_pnl for practice with a
        // fills-derived value (UTC midnight cutoff). Trust the snapshot
        // for combine/xfa/funded where the broker actually reports it.
        const dp = window.practiceDayPnl(a);
        a.dayPnl = (dp != null) ? dp : (p.day_pnl ?? a.dayPnl);
        a.realizedPnl = p.realized_pnl ?? 0;
        a.totalPnl = (a.equity || 0) - window.accountAnchor(a);
        a.mllFloor = p.mll_floor ?? a.mllFloor;
        a.mllHeadroom = p.mll_headroom ?? (a.equity - (a.mllFloor||0));
        // profit_target from goal_tracker: ship it into a.target so the
        // MLL bar shows the operator's combine goal instead of "no
        // target — survival" for an account that does have a target set
        // in config.toml.  Falls back to whatever we had (cache hydration
        // may have populated this on a prior session) when the agent
        // omitted the field (older builds, or accounts with no goal).
        if (typeof p.profit_target === "number" && p.profit_target > 0) {
          a.target = p.profit_target;
        }
        a.openPositions = p.open_positions ?? 0;
        a.positions = Array.isArray(p.positions)
          ? p.positions.map(window.normalizePosition)
          : [];
        // Per-strategy live state — the aviaf instance ships a
        // `strategies` array on every emit_equity payload (one entry per
        // running strategy with {id, short, lines}).  We index by id:
        //   strategyStatus[id]  -> short one-line summary (Overview rows)
        //   strategyLines[id]   -> array of indented detail lines
        //                         (Strategy + Instance detail pages
        //                          render these as the engine STATUS view)
        // Pre-payload deploys leave both empty; the UI falls back.
        if (Array.isArray(p.strategies)) {
          const m = {};
          const lm = {};
          const pm = {};
          const rm = {};
          const ids = [];
          const seenIds = new Set();
          for (const s of p.strategies) {
            if (!s || !s.id) continue;
            m[s.id] = s.short || "";
            lm[s.id] = Array.isArray(s.lines) ? s.lines : [];
            if (s.paused) pm[s.id] = true;
            if (Number(s.risk_per_trade) > 0) rm[s.id] = Number(s.risk_per_trade);
            if (!seenIds.has(s.id)) { seenIds.add(s.id); ids.push(s.id); }
          }
          a.strategyStatus = m;
          a.strategyLines  = lm;
          a.strategyPaused = pm;
          a.strategyRisk = rm;
          a.strategiesLastUs = msg.ts_us || Date.now() * 1000;
          // Merge new variants into a.strategies so the risk-return map
          // picks up post-HELLO additions (orb_rule/orb_v5/orb_unfiltered
          // hot-attached mid-session).  See bootstrap-side equivalent at
          // line ~1844.
          if (ids.length) {
            const have = new Set(a.strategies || []);
            const merged = [...(a.strategies || [])];
            for (const sid of ids) {
              if (!have.has(sid)) { merged.push(sid); have.add(sid); }
            }
            if (merged.length !== (a.strategies || []).length) {
              a.strategies = merged;
              if (!a.strategy) a.strategy = merged[0];
            }
          }
        }
      }
      // Per-account series so reloads / detail pages can plot one account
      // without the multi-account interleaving artifact.
      window.EQUITY_BY_ACCT = window.EQUITY_BY_ACCT || {};
      const key = `${msg.instance_id}/${msg.account_label}`;
      if (typeof p.balance === "number") {
        const series = window.EQUITY_BY_ACCT[key] || [];
        series.push(a?.equity ?? p.balance);
        window.EQUITY_BY_ACCT[key] = series.slice(-400);
      }
      // Live appends used to push every ~5 s WS tick directly onto
      // EQUITY_TOTAL with a slice(-400) cap, so the array could span at
      // most ~33 minutes — every 5D / 30D / 90D / YTD timeframe button
      // collapsed into the same recent half-hour and the bootstrap's
      // hourly buckets got wiped.  Bucket live samples by HOUR to match
      // the bootstrap (which fetches /api/equity?bucket_seconds=3600):
      // within the current hour, replace the trailing point in place;
      // once the hour rolls over, append a new bucket.  720 points per
      // 30 days; slice(-9000) keeps ~375 days so the YTD button has
      // headroom even late in the year.
      const tickTsUs = msg.ts_us || Math.round(Date.now() * 1000);
      const HOUR_US = 3600 * 1_000_000;
      const tickBucket = Math.floor(tickTsUs / HOUR_US);
      const tsArr = window.EQUITY_TS || [];
      const lastTs = tsArr.length ? tsArr[tsArr.length - 1] : null;
      const sameBucket = lastTs != null
        && Math.floor(lastTs / HOUR_US) === tickBucket;

      const total = (window.ACCOUNTS || []).reduce((s,x)=>s+(x.equity||0), 0);
      const allocAnchor = (window.ACCOUNTS || []).reduce(
        (s, a) => s + (window.accountAnchor ? window.accountAnchor(a) : (a.initial_capital || 0)), 0,
      );

      const upsertHourly = (arr, value, capHours = 9000) => {
        const next = arr ? arr.slice() : [];
        if (sameBucket && next.length) {
          next[next.length - 1] = value;
        } else {
          next.push(value);
          if (next.length > capHours) next.splice(0, next.length - capHours);
        }
        return next;
      };

      window.EQUITY_TOTAL = upsertHourly(window.EQUITY_TOTAL, total);
      window.PNL_TOTAL = upsertHourly(window.PNL_TOTAL, total - allocAnchor);
      window.EQUITY_TS = upsertHourly(window.EQUITY_TS, tickTsUs);

      // Per-strategy series stays in lockstep — same bucketing rules so a
      // plot showing AUM and per-strategy P&L on the same x-axis can't
      // drift between series.
      const stratOf = (x) => x.strategy || (x.strategies && x.strategies[0]) || "unassigned";
      const stratSums = {};
      const stratAllocSums = {};
      for (const x of (window.ACCOUNTS || [])) {
        const k = stratOf(x);
        stratSums[k] = (stratSums[k] || 0) + (x.equity || 0);
        stratAllocSums[k] = (stratAllocSums[k] || 0) +
          (window.accountAnchor ? window.accountAnchor(x) : (x.initial_capital || 0));
      }
      const ebs = window.EQUITY_BY_STRATEGY || {};
      const pbs = window.PNL_BY_STRATEGY || {};
      for (const k of Object.keys(stratSums)) {
        ebs[k] = upsertHourly(ebs[k], stratSums[k]);
        pbs[k] = upsertHourly(pbs[k], stratSums[k] - (stratAllocSums[k] || 0));
      }
      window.EQUITY_BY_STRATEGY = ebs;
      window.PNL_BY_STRATEGY = pbs;
      // STRATS rides on per-account totals; rebuild after each equity
      // update so the per-account "research vs live" section gets fresh
      // pnl30 numbers without waiting for the next bootstrap.
      rebuildStratsFromAccounts();
    } else if (msg.kind === "pass_estimate") {
      // Combine P(pass)/T(pass) snapshot (hourly agent emit) — replace the
      // account's latest wholesale; AccountCard reads a.passEst.
      const a = (window.ACCOUNTS || []).find(
        x => x.instance === msg.instance_id && x.id === msg.account_label,
      );
      if (a && msg.payload) {
        a.passEst = msg.payload;
        a.passEstLastUs = msg.ts_us || (Date.now() * 1000);
      }
    } else if (msg.kind === "goal_state") {
      // Goal-tracker snapshot — carries profit_target, best_day_pnl,
      // cumulative_pnl, effective_profit_target (consistency-adjusted),
      // consistency_pct, max_daily_profit, today_pnl, days_traded.
      // AccountCard consumes these to show the real "$X / $Y target" KPI
      // + consistency-adjusted bar.  Without this branch the payload was
      // dropped on the floor and combine accounts rendered as "no
      // target — survival" forever.
      const p = msg.payload || {};
      const a = (window.ACCOUNTS || []).find(
        x => x.instance === msg.instance_id && x.id === msg.account_label,
      );
      if (a) {
        if (typeof p.profit_target === "number" && p.profit_target > 0) {
          a.target = p.profit_target;
        }
        if (typeof p.effective_profit_target === "number" && p.effective_profit_target > 0) {
          a.effectiveTarget = p.effective_profit_target;
        }
        if (typeof p.consistency_pct === "number") a.consistencyPct = p.consistency_pct;
        if (typeof p.max_daily_profit === "number") a.maxDailyProfit = p.max_daily_profit;
        if (typeof p.best_day_pnl === "number") a.bestDayPnl = p.best_day_pnl;
        if (typeof p.cumulative_pnl === "number") a.cumulativePnl = p.cumulative_pnl;
        if (typeof p.today_pnl === "number") a.todayGoalPnl = p.today_pnl;
        if (typeof p.days_traded === "number") a.daysTraded = p.days_traded;
        if (typeof p.passed === "boolean") a.passed = p.passed;
        a.goalLastUs = msg.ts_us || Date.now() * 1000;
      }
      bump();
    } else if (msg.kind === "alert") {
      // Synthesise a DAL-row-shaped object and prepend so PageAlerts
      // shows the new row instantly.  alert_pk is unknown until the
      // debounced refresh lands (the hub assigns it at INSERT, after
      // forwarding the event); we use a negative placeholder so React
      // keys don't collide with real rows, and disable the ack button
      // for the ~1.5s gap by virtue of the synthetic pk failing the
      // POST — the refresh replaces the row with the real one before
      // the operator has time to click.
      const p = msg.payload || {};
      const synthetic = {
        alert_pk: -(msg.ts_us || Date.now() * 1000),   // negative sentinel; never collides with DB pks
        instance_id: msg.instance_id,
        account_label: msg.account_label || null,
        ts_us: msg.ts_us || Date.now() * 1000,
        severity: p.severity || "info",
        source: p.source || "agent",
        code: p.code || null,
        message: p.message || null,
        acknowledged: 0, ack_user: null, ack_ts_us: null,
      };
      window.ALERTS = [synthetic, ...(window.ALERTS || [])].slice(0, 500);
      // Browser notification for crits — silently no-ops if permission
      // hasn't been granted.
      maybeNotifyCrit(msg);
      // SPC events still need to refresh the parsed-SPC view.
      const src = p.source || "";
      if (src === "execution.spc_monitor") scheduleSpcRefresh();
      // Pull the real DB row (with alert_pk + canonical normalisation)
      // shortly after the synthetic prepend.
      scheduleAlertsRefresh();
    } else if (msg.kind === "collector_stats") {
      // Upsert one collector by (instance, collector_id).  Each emit is
      // a full replacement payload — no merging needed.
      const p = msg.payload || {};
      const cid = p.collector_id || p.host || msg.instance_id;
      const list = (window.COLLECTORS || []).filter(
        c => !(c._instance_id === msg.instance_id && (c.collector_id || c.host) === cid),
      );
      list.push(normalizeCollector({ ...p, _instance_id: msg.instance_id, _ts_us: msg.ts_us }));
      window.COLLECTORS = list;
      rebuildRegimeFromCollectors();
      rebuildStorageRollup();
    } else if (msg.kind === "config_inventory") {
      // Whole-roster replacement for this instance.  Drop the old
      // entries from this instance and append the new ones.
      const fresh = ((msg.payload || {}).configs || []).map(
        c => normalizeConfig({ ...c, _instance_id: msg.instance_id }),
      );
      const others = (window.CONFIGS || []).filter(c => c._instance_id !== msg.instance_id);
      window.CONFIGS = [...others, ...fresh];
    } else if (msg.kind === "research_catalog") {
      const fresh = ((msg.payload || {}).items || []).map(
        r => ({ ...r, _instance_id: msg.instance_id }),
      );
      const others = (window.RESEARCH || []).filter(r => r._instance_id !== msg.instance_id);
      window.RESEARCH = [...others, ...fresh];
    } else if (msg.kind === "bar") {
      // Live 1m candle.  Update the symbol's 1m series in place: replace
      // the bar with matching ts_open_us, or append if it's a new bucket.
      // Higher TFs (5m/15m/1h/1d) re-derive on next fetch — too costly to
      // roll up client-side on every tick, and the dashboard's chart
      // shows one TF at a time anyway.
      const p = msg.payload || {};
      const sym = p.symbol;
      if (sym) {
        const store = window.MARKET_BARS || (window.MARKET_BARS = {});
        const bySym = store[sym] || (store[sym] = {});
        const tfArr = bySym[p.tf || "1m"] || (bySym[p.tf || "1m"] = []);
        const tsOpen = p.ts_open_us;
        const last = tfArr[tfArr.length - 1];
        const bar = {
          ts_open_us: tsOpen, o: p.o, h: p.h, l: p.l, c: p.c,
          v: p.v, n_trades: p.n_trades, closed: !!p.closed,
          tf: p.tf || "1m", symbol: sym,
        };
        if (last && last.ts_open_us === tsOpen) {
          tfArr[tfArr.length - 1] = bar;
        } else {
          tfArr.push(bar);
          if (tfArr.length > 1200) tfArr.shift();
        }
      }
    } else if (msg.kind === "quote") {
      const p = msg.payload || {};
      if (p.symbol) {
        const store = window.MARKET_QUOTES || (window.MARKET_QUOTES = {});
        store[p.symbol] = p;
      }
    } else if (msg.kind === "print") {
      const p = msg.payload || {};
      const sym = p.symbol;
      const arr = (p.prints || []);
      if (sym && arr.length) {
        const store = window.MARKET_PRINTS || (window.MARKET_PRINTS = {});
        const existing = store[sym] || [];
        // Newest-first ordering matches what /api/market/prints returns.
        const next = [...arr.slice().reverse(), ...existing].slice(0, 400);
        store[sym] = next;
      }
    } else if (msg.kind === "book_top") {
      const p = msg.payload || {};
      if (p.symbol) {
        const store = window.MARKET_BOOK || (window.MARKET_BOOK = {});
        store[p.symbol] = {
          ts_us: p.ts_us, bids: p.bids || [], asks: p.asks || [],
        };
      }
    } else if (msg.kind === "symbol_catalog") {
      // Replace the slice of catalog from this instance.
      const fresh = ((msg.payload || {}).symbols || []).map(
        s => ({ ...s, _instance_id: msg.instance_id }),
      );
      const others = (window.MARKET_CATALOG || []).filter(
        s => s._instance_id !== msg.instance_id,
      );
      window.MARKET_CATALOG = [...others, ...fresh];
    }
    bump();
    // Re-persist after each significant WS frame so a reload mid-session
    // hydrates with within-seconds-fresh data, not just whatever was
    // cached at the last bootstrap.
    persistCache();
  }

  function openBridge() {
    if (bridge || !window.createHubBridge) return;
    bridge = window.createHubBridge({
      onStatus: (s) => {
        window.__LIVE.wsStatus = s;
        // Track when the live feed dropped so ConnectionBanner can warn
        // after a grace period.  Cleared the moment we're live again; set
        // on the first non-live transition after having been connected.
        if (s === "live") {
          window.__LIVE.wsDownSince = null;
        } else if (!window.__LIVE.wsDownSince && lastWsStatus === "live") {
          window.__LIVE.wsDownSince = Date.now();
        }
        if (s === "reconnecting") sawReconnect = true;
        // On a successful (re)connect after a drop, refresh state to
        // pick up anything that fired during the gap.  Gated on
        // sawReconnect so the initial bootstrap (already called from
        // useLiveData) isn't duplicated.
        if (s === "live" && sawReconnect) {
          sawReconnect = false;
          bootstrap();
        }
        lastWsStatus = s;
        bump();
      },
      onMessage: applyFrame,
    });
    startPingLoop();
  }
  // Client→hub latency probe: send a timestamped ping every 2s.  bridge.send
  // no-ops while the socket is mid-reconnect, so this is safe to keep running;
  // the hub echoes it back as cpong (see applyFrame) for the browser RTT.
  function startPingLoop() {
    stopPingLoop();
    pingTimer = setInterval(() => {
      pingId += 1;
      if (bridge) bridge.send({ kind: "cping", id: pingId, t: Math.round(performance.now()) });
    }, 2000);
  }
  function stopPingLoop() {
    if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
  }
  function closeBridge() {
    stopPingLoop();
    window.__LIVE.pingClient = null;
    window.__LIVE.pingServer = null;
    window.__LIVE.pingClientSrtt = window.__LIVE.pingClientJit = window.__LIVE.pingClientP95 = null;
    window.__LIVE.pingServerSrtt = window.__LIVE.pingServerJit = window.__LIVE.pingServerP95 = null;
    _pingRing.client.length = 0; _pingRing.server.length = 0;
    if (bridge) { bridge.close(); bridge = null; }
  }

  /* -- mutating account helpers --
   * Thin wrappers over /api/accounts + /api/brokers; the Accounts page
   * calls these from the "+ Add account" picker and the delete button.
   * Each helper triggers a fresh /api/accounts pull on success so the
   * UI mirrors hub state without waiting for the next bootstrap.
   */
  async function _refreshAccounts() {
    try {
      const accounts = await getJson("/api/accounts");
      const prevAcctMap = new Map((window.ACCOUNTS || []).map(a => [a.uid, a]));
      const _isInactive = r => r.active === 0 || DECOMMISSIONED_LABELS.has(r.label);
      window.ACCOUNTS_INACTIVE = (accounts || []).filter(_isInactive).map(accountFromRow);
      window.ACCOUNTS = (accounts || []).filter(r => !_isInactive(r)).map(row => {
        const fresh = accountFromRow(row);
        const prev = prevAcctMap.get(fresh.uid);
        if (prev) {
          if (typeof prev.equity === "number" && prev.equity > 0) fresh.equity = prev.equity;
          if (prev.equityVerified) fresh.equityVerified = true;
          if (typeof prev.dayPnl === "number") fresh.dayPnl = prev.dayPnl;
          if (typeof prev.totalPnl === "number") fresh.totalPnl = prev.totalPnl;
          if (typeof prev.mllFloor === "number" && prev.mllFloor > 0) fresh.mllFloor = prev.mllFloor;
          // Preserve per-account 30d-anchor so AUM_30D_AGO stays consistent
          // with the surviving accounts after an add/delete — re-derived
          // below.  A freshly added account that the bootstrap hasn't yet
          // 30d-fetched gets eq30=undefined here and is excluded from the
          // sum until the next sweep, which mirrors how it would have
          // looked before its row existed.
          if (typeof prev.eq30 === "number") fresh.eq30 = prev.eq30;
          if (typeof prev.eq30HasHist === "boolean") fresh.eq30HasHist = prev.eq30HasHist;
        }
        return fresh;
      });
      // Re-derive AUM_30D_AGO from the surviving accounts so the Overview
      // KPI doesn't ghost an added-then-deleted account as a phantom
      // ±$balance swing.  Two symmetric failure modes the bootstrap-time
      // scalar would hit:
      //   - Account deleted: ACCOUNTS shrinks, current AUM drops by the
      //     account's equity, but AUM_30D_AGO still holds the deleted
      //     account's anchor → diff renders as a phantom 30d loss.
      //   - Account just added: ACCOUNTS grows, current AUM jumps by the
      //     account's balance, AUM_30D_AGO doesn't yet know about it →
      //     diff renders as a phantom 30d gain.
      // Restrict the sum to equity-verified accounts. The headline AUM
      // calc on PageOverview applies the same filter, so both sides of
      // pnl30 = aum - aum30 either both include the new account (once
      // its first equity event lands) or both exclude it.
      const anchorFor = (a) => {
        if (typeof a.eq30 === "number" && Number.isFinite(a.eq30)) return a.eq30;
        return window.accountAnchor ? window.accountAnchor(a) : (a.initial_capital || 0);
      };
      window.AUM_30D_AGO = window.ACCOUNTS.reduce(
        (s, a) => a.equityVerified ? s + (anchorFor(a) || 0) : s, 0,
      );
      window.AUM_30D_HAS_HIST = window.ACCOUNTS.some(a => a.eq30HasHist);
      bump();
    } catch (e) { console.error("[accounts] refresh failed", e); }
  }
  window.fetchBrokers = async function() {
    return await getJson("/api/brokers");
  };
  // Market data — lazy fetchers used by the Market detail page on mount.
  // Each writes into the global store so subsequent WS frames merge in
  // place.  Caller passes an optional opts; we always return the array
  // for convenience.
  window.fetchMarketBars = async function(symbol, tf = "1m", { limit = 500 } = {}) {
    if (!symbol) return [];
    const url = `/api/market/bars?symbol=${encodeURIComponent(symbol)}`
              + `&tf=${encodeURIComponent(tf)}&limit=${limit}`;
    try {
      const r = await getJson(url);
      const bars = (r && r.bars) || [];
      const store = window.MARKET_BARS || (window.MARKET_BARS = {});
      const bySym = store[symbol] || (store[symbol] = {});
      bySym[tf] = bars;
      bump();
      return bars;
    } catch (_) { return []; }
  };
  window.fetchMarketBook = async function(symbol) {
    if (!symbol) return null;
    try {
      const b = await getJson(`/api/market/book?symbol=${encodeURIComponent(symbol)}`);
      const store = window.MARKET_BOOK || (window.MARKET_BOOK = {});
      store[symbol] = { ts_us: b.ts_us, bids: b.bids || [], asks: b.asks || [] };
      bump();
      return store[symbol];
    } catch (_) { return null; }
  };
  window.fetchMarketPrints = async function(symbol, { limit = 200 } = {}) {
    if (!symbol) return [];
    try {
      const r = await getJson(`/api/market/prints?symbol=${encodeURIComponent(symbol)}&limit=${limit}`);
      const arr = (r && r.prints) || [];
      const store = window.MARKET_PRINTS || (window.MARKET_PRINTS = {});
      store[symbol] = arr;
      bump();
      return arr;
    } catch (_) { return []; }
  };
  window.fetchBrokerAccounts = async function(broker, instance_id) {
    // Round-trips through the named agent to query the broker live.
    // 503 = agent offline, 501 = agent doesn't speak broker_account_list,
    // 504 = agent didn't ack within the router's timeout.  Caller surfaces
    // the error message verbatim so the operator knows what to fix.
    const url = `/api/brokers/${encodeURIComponent(broker)}/accounts`
              + `?instance_id=${encodeURIComponent(instance_id)}`;
    const r = await fetch(url, { credentials: "same-origin" });
    if (!r.ok) {
      let detail = `${r.status}`;
      try { detail = (await r.json()).detail || detail; } catch (_) {}
      throw new Error(detail);
    }
    return await r.json();
  };
  window.registerAccount = async function(req) {
    const r = await fetch("/api/accounts", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    });
    if (!r.ok) {
      let detail = `${r.status}`;
      try { detail = (await r.json()).detail || detail; } catch (_) {}
      throw new Error(detail);
    }
    await _refreshAccounts();
    return await r.json();
  };
  /* Flatten every position on an account. Hub forwards to the agent's
   * existing FLATTEN command, which the broker reads via market-close
   * orders.  Returns the ack body so the caller can render any error
   * the agent surfaced (e.g. "no broker session"). */
  window.flattenAccount = async function(instance_id, label) {
    const r = await fetch(
      `/api/commands/${encodeURIComponent(instance_id)}/flatten`
      + `?account_label=${encodeURIComponent(label)}`,
      { method: "POST", credentials: "same-origin" },
    );
    if (!r.ok) {
      let detail = `${r.status}`;
      try { detail = (await r.json()).detail || detail; } catch (_) {}
      throw new Error(detail);
    }
    return await r.json();
  };

  /* Pause / resume trading on an account.  Both forward to the agent's
   * existing PAUSE/RESUME command via /api/commands/{id}/{pause,resume}.
   * Scope is the ACCOUNT (account_label), so when an account runs several
   * strategies all of them pause together — callers must say so in their
   * confirm copy.  Returns the ack body (CommandResponse). */
  async function _accountCommand(instance_id, label, verb) {
    const r = await fetch(
      `/api/commands/${encodeURIComponent(instance_id)}/${verb}`
      + `?account_label=${encodeURIComponent(label)}`,
      { method: "POST", credentials: "same-origin" },
    );
    const body = await r.json().catch(() => ({}));
    if (!r.ok || body.ok === false) {
      throw new Error(body.error || body.detail || `HTTP ${r.status}`);
    }
    return body;
  }
  window.pauseInstance  = (instance_id, label) => _accountCommand(instance_id, label, "pause");
  window.resumeInstance = (instance_id, label) => _accountCommand(instance_id, label, "resume");
  // Hands-off: on=true engages manual control (suppresses reconciler orphan-
  // flatten + protective drawdown lock, pauses strategies); on=false releases.
  window.handsOffInstance = async (instance_id, label, on) => {
    const r = await fetch(
      `/api/commands/${encodeURIComponent(instance_id)}/hands_off`
      + `?account_label=${encodeURIComponent(label)}&on=${on ? "true" : "false"}`,
      { method: "POST", credentials: "same-origin" },
    );
    const body = await r.json().catch(() => ({}));
    if (!r.ok || body.ok === false) throw new Error(body.error || body.detail || `HTTP ${r.status}`);
    return body;
  };

  /* Emergency stop-all for an entire agent.  The hub requires the operator
   * to echo the instance_id (confirm_label) — we pass it through; UI MUST
   * still gate on its own confirm() since killswitch is irreversible. */
  window.killswitchInstance = async function(instance_id) {
    const r = await fetch(
      `/api/commands/${encodeURIComponent(instance_id)}/killswitch`
      + `?confirm_label=${encodeURIComponent(instance_id)}`,
      { method: "POST", credentials: "same-origin" },
    );
    const body = await r.json().catch(() => ({}));
    if (!r.ok || body.ok === false) {
      throw new Error(body.error || body.detail || `HTTP ${r.status}`);
    }
    return body;
  };

  /* Fleet profit factor = Σ gross win / Σ gross loss across strategies.
   * Profit factor is a ratio of sums, so it CANNOT be a (trade-weighted)
   * average of per-strategy PFs — that understates the fleet (it drags
   * all-winner strategies whose pf is Infinity, and no-trade strategies
   * whose pf is null, toward 0).  Desktop and mobile both call this so the
   * Overview KPI agrees across surfaces.  Returns a finite number,
   * Infinity (wins, no losses), or null (no attributed P&L yet). */
  window.fleetProfitFactor = function(strats) {
    const list = Array.isArray(strats) ? strats : [];
    let gw = 0, gl = 0;
    for (const s of list) { gw += (s && s.grossWin) || 0; gl += (s && s.grossLoss) || 0; }
    if (gw > 0 || gl > 0) return gl > 0 ? gw / gl : Infinity;
    return null;
  };

  /* Bulk-ack every open alert in one request (vs one POST per row).  Used by
   * the desktop + mobile "Ack all" controls so a backlog of hundreds clears
   * instantly.  excludeCrit=true leaves crits visible.  Returns {ok, acked}. */
  window.ackAllAlerts = async function(excludeCrit) {
    const q = excludeCrit ? "?exclude_crit=true" : "";
    const r = await fetch(`/api/alerts/ack-all${q}`, { method: "POST", credentials: "same-origin" });
    const body = await r.json().catch(() => ({}));
    if (!r.ok || body.ok === false) throw new Error(body.detail || body.error || `HTTP ${r.status}`);
    // Optimistically mark local rows acked so the nav badge + lists update
    // before the next bootstrap refresh lands.
    const arr = window.ALERTS || [];
    for (const a of arr) {
      if (!a.acknowledged && !(excludeCrit && window.normalizeAlertSeverity
            && window.normalizeAlertSeverity(a.severity) === "crit")) {
        a.acknowledged = 1; a.ack_user = a.ack_user || "you"; a.ack_ts_us = Date.now() * 1000;
      }
    }
    if (window.__LIVE) window.__LIVE.rev = (window.__LIVE.rev || 0) + 1;
    return body;
  };

  /* Wire-up for strategy add/remove. Both POST through the existing
   * /api/commands/{id}/strategy_{add,remove} hub endpoints, which
   * forward to the agent. Agent may respond ok=false with a clear
   * error (e.g. "single-strategy account — config edit + restart");
   * the caller surfaces that verbatim. On ok=true we refresh the
   * accounts roster so dashboard state reflects the agent.
   */
  window.strategyAdd = async function(instance_id, label, strategy) {
    const r = await fetch(
      `/api/commands/${encodeURIComponent(instance_id)}/strategy_add`,
      { method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_label: label, strategy }) },
    );
    const body = await r.json().catch(() => ({}));
    if (!r.ok || !body.ok) {
      throw new Error(body.detail || body.error || `${r.status}`);
    }
    await _refreshAccounts();
    return body;
  };
  window.strategyRemove = async function(instance_id, label, strategy) {
    const r = await fetch(
      `/api/commands/${encodeURIComponent(instance_id)}/strategy_remove`,
      { method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_label: label, strategy }) },
    );
    const body = await r.json().catch(() => ({}));
    if (!r.ok || !body.ok) {
      throw new Error(body.detail || body.error || `${r.status}`);
    }
    await _refreshAccounts();
    return body;
  };

  /* Hot config reload: agent re-reads config.toml and applies only the
   * strategy delta live.  dry_run previews the diff without applying.
   * Returns {path,dry_run,strategies_added,strategies_removed,skipped,
   * errors,accounts_new_in_config,accounts_missing_from_config}. */
  window.configReload = async function(instance_id, dry_run = false) {
    const q = dry_run ? "?dry_run=true" : "";
    const r = await fetch(
      `/api/instances/${encodeURIComponent(instance_id)}/config_reload${q}`,
      { method: "POST", credentials: "same-origin" },
    );
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.detail || `${r.status}`);
    if (!dry_run) await _refreshAccounts();
    return body;
  };

  window.deleteAccount = async function(instance_id, label, { hard = false } = {}) {
    const q = hard ? "?hard=true" : "";
    const r = await fetch(
      `/api/accounts/${encodeURIComponent(instance_id)}/${encodeURIComponent(label)}${q}`,
      { method: "DELETE", credentials: "same-origin" },
    );
    if (!r.ok) {
      let detail = `${r.status}`;
      try { detail = (await r.json()).detail || detail; } catch (_) {}
      throw new Error(detail);
    }
    await _refreshAccounts();
    return await r.json();
  };

  /* Wipe seeded mock fixtures at MODULE-LOAD (before React mounts), then
   * try to hydrate from localStorage. With cache hit, the very first
   * render shows last-known real values instead of $0. Without it,
   * arrays stay empty and pages.jsx renders 'Loading...' placeholders
   * (gated on window.__LIVE.hasData). */
  resetState();   // wipes mock state + re-seeds STRATEGY_DEFS to agent's valid_kinds
  if (hydrateCache()) {
    window.__LIVE.hasData = true;
    bump();
  }

  /* -- connection / availability banner --
   * A self-timed strip the app renders at the bottom of the viewport.
   * It reads window.__LIVE directly (the same live state every page
   * polls) and re-evaluates once a second so it surfaces and clears
   * without depending on a parent re-render.
   *
   * Three states, in priority order:
   *   1. No data + bootstrap error      -> "instance not available" (bad)
   *   2. No data + slow (> SLOW_MS)      -> "still loading…"          (warn)
   *   3. Have data + WS down > WS_GRACE  -> "live feed disconnected"  (warn)
   * Returns null (renders nothing) in the healthy/normal-loading case so
   * a fast cold start never flashes a banner. */
  window.ConnectionBanner = function ConnectionBanner() {
    const [, force] = React.useState(0);
    const mountedAtRef = React.useRef(Date.now());
    React.useEffect(() => {
      const id = setInterval(() => force(n => (n + 1) % 1e9), 1000);
      return () => clearInterval(id);
    }, []);

    const L = window.__LIVE || {};
    const now = Date.now();
    const elapsed = now - mountedAtRef.current;
    const SLOW_MS = 12_000;       // initial load considered "stuck" past this
    const WS_GRACE_MS = 15_000;   // tolerate brief WS blips before warning

    let level = null, text = null;
    if (!L.hasData) {
      if (L.err) {
        level = "bad";
        text = `Instance not available — couldn't reach the hub (${L.err}). Retrying…`;
      } else if (elapsed > SLOW_MS) {
        level = "warn";
        text = `Still loading after ${Math.round(elapsed / 1000)}s — the agent or hub may be unavailable.`;
      }
    } else if (L.wsDownSince && (now - L.wsDownSince) > WS_GRACE_MS) {
      level = "warn";
      text = `Live feed disconnected ${Math.round((now - L.wsDownSince) / 1000)}s ago — values may be stale. Reconnecting…`;
    } else if (L.err && L.wsStatus !== "live") {
      level = "warn";
      text = `Last refresh failed (${L.err}) — showing cached values.`;
    }
    if (!text) return null;

    return (
      <div className={"conn-banner conn-" + level} role="status" aria-live="polite">
        <span className="conn-banner-dot" aria-hidden />
        <span className="conn-banner-msg">{text}</span>
      </div>
    );
  };

  /* -- public hook --
   * Pass in App's session state so we re-bootstrap whenever the user
   * logs in / logs out. session shape mirrors /api/auth/me:
   *   null              -> boot in progress; skip
   *   false             -> logged out; tear down
   *   {auth_enabled:..} -> ready; bootstrap + open bridge
   */
  window.useLiveData = function useLiveData(session) {
    React.useEffect(() => {
      if (session === null) return;            // pre-auth-check
      closeBridge();
      // Only wipe state when going *out* (logout/auth-lost).  On
      // login (null → {...}) we keep whatever the localStorage cache
      // hydrated so the operator sees real values immediately instead
      // of a "$0 / no accounts" flash while bootstrap re-fetches.
      // bootstrap() overwrites stale values with fresh ones in place.
      if (session === false) {
        resetState();
        return;
      }
      bootstrap();
      openBridge();

      // Safety-net poll.  The live path is the WS, but a silently-stalled
      // socket — a half-open TCP still fed by the server keepalive ping so the
      // 45s watchdog never fires, a backgrounded/throttled tab, or a
      // subscriber whose frames quietly stopped — freezes the dashboard at its
      // last snapshot: the "stale Nm" badge only advances when a fresh equity
      // frame lands, so it can climb to HOURS while the hub is perfectly
      // healthy (operator saw "AGENT OFFLINE · values as of 2h ago" while
      // aviaf-paper was connected and emitting).  Re-bootstrap on an interval
      // so the dashboard self-heals regardless of WS state: /api/bootstrap has
      // a 30s server cache and bootstrap() coalesces, so it's cheap, and it
      // refreshes strategiesLastUs / positions / equity in place.
      const pollId = setInterval(() => { bootstrap(); }, 40_000);

      // Revalidate on tab returning to foreground / network coming
      // back online.  Without these, an idle tab whose WS appeared
      // healthy (server didn't drop, OS didn't notice a half-open
      // TCP yet) would keep rendering whatever state was current
      // when it lost focus.  The ws.jsx watchdog catches half-open
      // sockets within ~45s; these handlers cut that to <100ms when
      // there's a real user-observable signal that the gap is over.
      //
      // STALE_THRESHOLD: don't churn the WS for a tab that was only
      // hidden for a few seconds (tab-switch, alt-tab).  10s matches
      // the watchdog's lower bound for "noticeably behind on frames"
      // — anything shorter is just thrash.
      const STALE_THRESHOLD_MS = 10_000;
      const revalidate = (reason) => {
        if (!bridge) return;
        const age = Date.now() - (bridge.lastFrameMs ? bridge.lastFrameMs() : 0);
        if (age < STALE_THRESHOLD_MS) return;
        try { bridge.forceReconnect(); } catch (_) {}
      };
      const onVis = () => { if (!document.hidden) revalidate("visible"); };
      const onOnline = () => revalidate("online");
      document.addEventListener("visibilitychange", onVis);
      window.addEventListener("online", onOnline);

      return () => {
        clearInterval(pollId);
        document.removeEventListener("visibilitychange", onVis);
        window.removeEventListener("online", onOnline);
        closeBridge();
      };
    }, [session]);
  };
})();
